import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, TransitionChild } from '@headlessui/react';
import { ClockIcon, PaperAirplaneIcon, PowerIcon, StarIcon } from '@heroicons/react/24/solid';
import { BuildingOffice2Icon, Cog6ToothIcon, CurrencyPoundIcon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { FunnelIcon, MapPinIcon, TagIcon, EnvelopeOpenIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  Bars3Icon,
  TableCellsIcon,
  AdjustmentsHorizontalIcon,
  MegaphoneIcon,
  PhotoIcon,
  CreditCardIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ServerStackIcon,
  ChartBarIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import CreditBuyModal from './components/CreditBuyModal';
import {format, isToday, isTomorrow, startOfWeek, addDays, isSameDay} from 'date-fns';
import CompanySettingsModal from './components/CompanySettingsModal';
import { AuthContext } from "../AuthContext";
import AvenorNew from './avenornew';
import { EnvelopeIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { VictoryLine, VictoryBar, VictoryChart, VictoryAxis, VictoryTooltip, VictoryVoronoiContainer } from 'victory';
import AIAgent from './agent';
import ICP from './components/ICP';
import sicCodeGroups from './siccode.json';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}



function parseExtractedRevenueValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

function formatExtractedRevenueValue(value) {
  const numeric = parseExtractedRevenueValue(value);
  if (numeric === null) {
    return 'No Information';
  }

  return new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 0,
  }).format(Math.max(0, numeric));
}

// Socials is stored as JSON: [{ Facebook: url|null, Posts: [...] }, { Instagram: ... }, { X: ... }, { LinkedIn: ... }]
function parseSocials(lead) {
  const socialFieldName = Object.keys(lead || {}).find((key) => key.toLowerCase() === 'socials');
  const storedSocials = socialFieldName ? lead[socialFieldName] : null;
  if (!storedSocials) return [];

  try {
    let parsed = typeof Buffer !== 'undefined' && Buffer.isBuffer(storedSocials)
      ? storedSocials.toString('utf8')
      : storedSocials;
    while (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }

    const entries = Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === 'object'
      ? Object.entries(parsed)
          .filter(([platform]) => platform !== 'Posts')
          .map(([platform, value]) => ({
            [platform]: typeof value === 'string' ? value : value?.url || null,
            Posts: Array.isArray(value?.Posts) ? value.Posts : parsed.Posts || [],
          }))
      : [];

    return entries
      .map((entry) => {
        const platform = Object.keys(entry || {}).find((key) => key !== 'Posts');
        if (!platform) return null;
        const posts = Array.isArray(entry.Posts)
          ? entry.Posts
              .map((post) => typeof post === 'string' ? post : post?.text || post?.content || post?.caption || '')
              .filter(Boolean)
          : [];
        return { platform, url: entry[platform] || null, posts };
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

const SOCIAL_ICON_META = {
  Facebook: {
    color: 'bg-[#1877F2]',
    path: 'M22 12a10 10 0 10-11.5 9.9v-7H8v-2.9h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6v1.9H16l-.4 2.9h-2.1v7A10 10 0 0022 12z',
  },
  Instagram: {
    color: 'bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600',
    path: 'M12 7.2a4.8 4.8 0 100 9.6 4.8 4.8 0 000-9.6zm0 7.9a3.1 3.1 0 110-6.2 3.1 3.1 0 010 6.2zM16.9 6a1.1 1.1 0 100 2.2 1.1 1.1 0 000-2.2zM21.9 7.6a5.5 5.5 0 00-1.5-3.9 5.5 5.5 0 00-3.9-1.5C15.2 2 14.8 2 12 2s-3.2 0-4.5.2A5.5 5.5 0 003.6 3.7 5.5 5.5 0 002.1 7.6C2 8.9 2 9.3 2 12s0 3.1.2 4.4a5.5 5.5 0 001.5 3.9 5.5 5.5 0 003.9 1.5c1.3.2 1.7.2 4.5.2s3.2 0 4.5-.2a5.5 5.5 0 003.9-1.5 5.5 5.5 0 001.5-3.9c.1-1.3.1-1.7.1-4.4s0-3.1-.2-4.4zM20 16.3a3.6 3.6 0 01-1 2.5 3.6 3.6 0 01-2.5 1c-1.2.1-1.6.1-4.4.1s-3.2 0-4.4-.1a3.6 3.6 0 01-2.5-1 3.6 3.6 0 01-1-2.5C4 15.1 4 14.7 4 12s0-3.1.1-4.3a3.6 3.6 0 011-2.5 3.6 3.6 0 012.5-1C8.9 4.1 9.3 4.1 12 4.1s3.2 0 4.4.1a3.6 3.6 0 012.5 1 3.6 3.6 0 011 2.5c.1 1.2.1 1.6.1 4.3s0 3.1-.1 4.3z',
  },
  X: {
    color: 'bg-black',
    path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  },
  LinkedIn: {
    color: 'bg-[#0A66C2]',
    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  },
};

// Compact row of social icon links; greyed out + disabled when no URL is known for that platform.
function SocialBadges({ lead, className = '' }) {
  const socials = parseSocials(lead);
  if (!socials.length) return null;

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {socials.map(({ platform, url, posts }) => {
        const meta = SOCIAL_ICON_META[platform];
        if (!meta) return null;
        const hasUrl = Boolean(url);
        const title = hasUrl
          ? `${platform}${posts.length ? ` — ${posts.length} recent post${posts.length === 1 ? '' : 's'}` : ''}`
          : `${platform} — not found`;
        const content = (
          <span
            className={`flex h-5 w-5 items-center justify-center rounded-full text-white shadow-sm ${hasUrl ? meta.color : 'bg-slate-300'}`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
              <path d={meta.path} />
            </svg>
          </span>
        );
        return hasUrl ? (
          <a key={platform} href={url} target="_blank" rel="noopener noreferrer" title={title} className="relative">
            {content}
            {posts.length > 0 && (
              <span className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-emerald-500 text-[7px] font-bold text-white">
                {posts.length}
              </span>
            )}
          </a>
        ) : (
          <span key={platform} title={title} className="opacity-40">{content}</span>
        );
      })}
    </div>
  );
}

const FOLLOW_UP_STAGE_LABELS = {
  1: 'Stage 1 · Initial email sent',
  2: 'Stage 2 · Follow-up 1 sent',
  3: 'Stage 3 · Follow-up 2 sent',
  4: 'Stage 4 · Follow-up 3 sent',
};

// Ordered bottom-to-top; used by FunnelIndicator and the funnel stage filter
const FUNNEL_LEVELS = ['Unaware', 'Problem Aware', 'Solution Aware', 'Product Aware', 'Most Aware'];

// Highest follow-up stage reached for a lead, based on which FollowUpEmailNSent timestamps are set.
function getLeadFollowUpStage(lead) {
  if (lead?.FollowUpEmail3Sent) return { stage: 4, label: FOLLOW_UP_STAGE_LABELS[4], sentAt: lead.FollowUpEmail3Sent };
  if (lead?.FollowUpEmail2Sent) return { stage: 3, label: FOLLOW_UP_STAGE_LABELS[3], sentAt: lead.FollowUpEmail2Sent };
  if (lead?.FollowUpEmail1Sent) return { stage: 2, label: FOLLOW_UP_STAGE_LABELS[2], sentAt: lead.FollowUpEmail1Sent };
  return { stage: 1, label: FOLLOW_UP_STAGE_LABELS[1], sentAt: lead?.Contacted ?? lead?.LastContacted ?? null };
}

// FollowUpSchedule is stored as an array wrapping a single object: [{ followup1, followup2, followup3 }]
function parseFollowUpSchedule(lead) {
  const raw = lead?.FollowUpSchedule;
  if (!raw) return null;
  try {
    let parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) parsed = parsed[0] ?? null;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      followup1: parsed.followup1 ?? parsed.FollowUp1 ?? null,
      followup2: parsed.followup2 ?? parsed.FollowUp2 ?? null,
      followup3: parsed.followup3 ?? parsed.FollowUp3 ?? null,
    };
  } catch {
    return null;
  }
}

function normalizeSectorKey(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function getWarmupStatsFromInboxes(inboxes, todaySent) {
  // Compute total daily cap from each inbox's stage
  const totalDailyCap = inboxes.reduce((sum, inbox) => {
    const stage = Number(inbox.stage) || 1;
    let cap = 20;
    if (stage === 2) cap = 30;
    else if (stage === 3) cap = 40;
    else if (stage === 4) cap = 50;
    // you can extend for more stages
    return sum + cap;
  }, 0);

  const remainingToday = Math.max(0, totalDailyCap - (todaySent || 0));

  // Determine the highest stage among all inboxes
  const maxStage = inboxes.reduce(
    (max, inbox) => Math.max(max, Number(inbox.stage) || 1),
    1
  );

  return {
    emailStage: maxStage,
    emailDailyCap: totalDailyCap,
    emailRemainingToday: remainingToday,
  };
}

const smtpProviderPresets = {
  custom: { label: 'Custom', host: '', port: '587', secure: false },
  gmail: { label: 'Gmail', host: 'smtp.gmail.com', port: '587', secure: false },
  outlook: { label: 'Outlook / Microsoft 365', host: 'smtp.office365.com', port: '587', secure: false },
  icloud: { label: 'iCloud', host: 'smtp.mail.me.com', port: '587', secure: false },
  yahoo: { label: 'Yahoo', host: 'smtp.mail.yahoo.com', port: '465', secure: true },
  ionos: { label: 'IONOS', host: 'smtp.ionos.co.uk', port: '465', secure: true },
};

export default function Leads() {
  const INITIAL_LEADS_RENDER_COUNT = 60;
  const LEADS_RENDER_BATCH_COUNT = 40;
  const ENRICH_QUEUE_LEAD_CAP = 50;
  const navigate = useNavigate();
  const routeLocation = useLocation();

  const parseJsonOrThrow = async (response, endpointLabel) => {
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    const raw = await response.text();

    if (!raw) {
      return null;
    }

    if (!contentType.includes('application/json')) {
      const preview = raw.slice(0, 140).replace(/\s+/g, ' ').trim();
      throw new Error(`${endpointLabel} returned non-JSON (${response.status}). ${preview}`);
    }

    try {
      return JSON.parse(raw);
    } catch (jsonError) {
      throw new Error(`${endpointLabel} returned invalid JSON (${response.status}).`);
    }
  };

  const parseJsonLenient = async (response, endpointLabel) => {
    try {
      return await parseJsonOrThrow(response, endpointLabel);
    } catch (_error) {
      return null;
    }
  };

  const [leads, setLeads] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [location, setLocation] = useState('');
  const [sector, setSector] = useState('');
  const [leadPullLoading, setLeadPullLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [sectorSummary, setSectorSummary] = useState(null);
  const [showPursuedOnly, setShowPursuedOnly] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [singleLeadEnrichingId, setSingleLeadEnrichingId] = useState(null);
  const [callScriptGeneratingId, setCallScriptGeneratingId] = useState(null);
  const [modalState, setModalState] = useState({ open: false, title: '', body: '' });
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [initialEmailEditingId, setInitialEmailEditingId] = useState(null);
  const [initialEmailDrafts, setInitialEmailDrafts] = useState({});
  const [initialEmailSubmittingId, setInitialEmailSubmittingId] = useState(null);
  const [initialEmailMessages, setInitialEmailMessages] = useState({});
  const [aiLocationPrompt, setAiLocationPrompt] = useState('');
  const [aiSectorPrompt, setAiSectorPrompt] = useState('');
  const [aiLocations, setAiLocations] = useState([]);
  const [aiSectors, setAiSectors] = useState([]);
  const [selectedExistingAiSectors, setSelectedExistingAiSectors] = useState([]);
  const [aiLocationsLoading, setAiLocationsLoading] = useState(false);
  const [selectedSicCategory, setSelectedSicCategory] = useState('Human Health and Social Work Activities');
  const [selectedSicCodes, setSelectedSicCodes] = useState([]);
  const [manualTargetLocation, setManualTargetLocation] = useState('');
  const [targetLeadQuantity, setTargetLeadQuantity] = useState(10);
  const [aiSectorsLoading, setAiSectorsLoading] = useState(false);
  const [aiLeadPullRunning, setAiLeadPullRunning] = useState(false);
  const [aiLeadPullProgress, setAiLeadPullProgress] = useState('');
  const [bulkApplyFilteredOnly, setBulkApplyFilteredOnly] = useState(true);
  const [bulkInitialSendRunning, setBulkInitialSendRunning] = useState(false);
  const [bulkInitialSendFeedback, setBulkInitialSendFeedback] = useState('');
  const [bulkInitialSendError, setBulkInitialSendError] = useState('');
  const [bulkDeleteRunning, setBulkDeleteRunning] = useState(false);
  const [bulkDeleteFeedback, setBulkDeleteFeedback] = useState('');
  const [bulkDeleteError, setBulkDeleteError] = useState('');
  const [granularSendModal, setGranularSendModal] = useState({ open: false });
  const [granularSendQuantity, setGranularSendQuantity] = useState(0);
  const [granularSendRunning, setGranularSendRunning] = useState(false);
  const [granularSendFeedback, setGranularSendFeedback] = useState('');
  const [granularSendError, setGranularSendError] = useState('');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [scheduledLeadIds, setScheduledLeadIds] = useState(new Set());
  const [scheduleSendRunning, setScheduleSendRunning] = useState(false);
  const [scheduleSendFeedback, setScheduleSendFeedback] = useState('');
  const [scheduleSendError, setScheduleSendError] = useState('');
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [followUpLeadIds, setFollowUpLeadIds] = useState(new Set());
  const [followUpSendRunning, setFollowUpSendRunning] = useState(false);
  const [followUpSendFeedback, setFollowUpSendFeedback] = useState('');
  const [followUpSendError, setFollowUpSendError] = useState('');
  const [showLeadActions, setShowLeadActions] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showFilteredOnly, setShowFilteredOnly] = useState(false);
  const [showCompaniesHouseHitOnly, setShowCompaniesHouseHitOnly] = useState(false);
  const [showContactedOnly, setShowContactedOnly] = useState(false);
  const [contactedFromDate, setContactedFromDate] = useState('');
  const [contactedToDate, setContactedToDate] = useState('');
  const [updatedAtFilterDate, setUpdatedAtFilterDate] = useState('');
  const [globalLeadSearch, setGlobalLeadSearch] = useState('');
  const [progressSavingLeadId, setProgressSavingLeadId] = useState(null);
  const [crmAddingLeadId, setCrmAddingLeadId] = useState(null);
  const [locationSortOrder, setLocationSortOrder] = useState('none'); // 'none', 'asc', 'desc'
  const [enrichCreditsToUse, setEnrichCreditsToUse] = useState(30);
  const [extractedRevenueMin, setExtractedRevenueMin] = useState('');
  const [extractedRevenueMax, setExtractedRevenueMax] = useState('');
  const [salesCredits, setSalesCredits] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [emailStage, setEmailStage] = useState(1);
  const [emailSent, setEmailSent] = useState(0);
  const [emailSentToday, setEmailSentToday] = useState(0);
  const [emailDailyCap, setEmailDailyCap] = useState(50);
  const [emailRemainingToday, setEmailRemainingToday] = useState(50);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  // Animation removed: credit updates will use company endpoint directly
  const [smtpModalOpen, setSmtpModalOpen] = useState(false);
  const [icpDrawerOpen, setIcpDrawerOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    Email: '',
    Telephone: '',
    CompanyName: '',
    Industry: '',
    Website: '',
    ContactName: '',
    Address: '',
    Postcode: '',
    Description: '',
    ValueProposition: '',
    CallToAction: '',
    EmailFooter: '',
  });
  const [savedEmailFooterHtml, setSavedEmailFooterHtml] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsFeedback, setSettingsFeedback] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [passwordOne, setPasswordOne] = useState('');
  const [passwordTwo, setPasswordTwo] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [smtpConfigs, setSmtpConfigs] = useState([]);
const [smtpFeedback, setSmtpFeedback] = useState('');
const [smtpError, setSmtpError] = useState('');
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpChecking, setSmtpChecking] = useState(false);
  const [smtpConnected, setSmtpConnected] = useState(null);
  const [smtpConnectionMessage, setSmtpConnectionMessage] = useState('');
  const [smtpLastCheckedAt, setSmtpLastCheckedAt] = useState('');
  const [visibleLeadCount, setVisibleLeadCount] = useState(INITIAL_LEADS_RENDER_COUNT);
  const [filterIsSticky, setFilterIsSticky] = useState(false);
  const [hasDismissedNotification, setHasDismissedNotification] = useState(false);
  const [showUnsentEnrichedOnly, setShowUnsentEnrichedOnly] = useState(false);
  const [showScheduledOnly, setShowScheduledOnly] = useState(false);
  const [cancellingScheduleId, setCancellingScheduleId] = useState(null);
  const [filterByCredits , setFilterByCredits] = useState(null);
  const [companyNotification, setCompanyNotification] = useState(null);
  const [showLinkedInOnly, setShowLinkedInOnly] = useState(false);
  const [showEmailCampaignOnly, setShowEmailCampaignOnly] = useState(false);
  const [showUnfilteredOnly, setShowUnfilteredOnly] = useState(false);
  const [showLinkedInMessageSentOnly, setShowLinkedInMessageSentOnly] = useState(false);
  const [linkedInStatusFilter, setLinkedInStatusFilter] = useState('');
  const [followUpStageFilter, setFollowUpStageFilter] = useState('');
  const [funnelStageFilter, setFunnelStageFilter] = useState('');

  const leadActionsRef = useRef(null);
  const filterSentinelRef = useRef(null);
  const bulkOpsRef = useRef(null);
  const leadsTableRef = useRef(null);
  const leadsLoadMoreRef = useRef(null);
  const emailEditorRef = useRef(null);



  const { user } = useContext(AuthContext);

  const permissionLevel = Number(user?.permission ?? user?.Permission ?? 0);
  const accessLevel = String(user?.access ?? user?.Access ?? '').trim().toLowerCase();
  const isFullAccessUser = accessLevel === 'admin' && permissionLevel === 1;
  const isPermissionRestricted = permissionLevel === 2 && !isFullAccessUser;
  const canUseAiAndEnrich = !isPermissionRestricted;
  const companyNameForMenu = String(user?.companyName ?? user?.CompanyName ?? '').trim();
  const showCompanyNameInMenu = Boolean(user?.insightAccount) && companyNameForMenu.length > 0;
  const hasInsightAccount = user?.insightAccount !== null && user?.insightAccount !== undefined && String(user?.insightAccount).trim() !== '';

  console.log(user)

 const createEmptySmtpConfig = () => ({
  id: `smtp-${Date.now()}`,
  host: '',
  port: '587',
  username: '',
  password: '',
  fromEmail: '',
  secure: false,
  provider: 'custom',
  stage: 1, // 🆕 default stage
  sent: 0,
});

const addSmtpConfig = () => {
  setSmtpConfigs(prev => [...prev, createEmptySmtpConfig()]);
};

const removeSmtpConfig = (id) => {
  if (smtpConfigs.length <= 1) {
    setSmtpError('At least one SMTP configuration is required.');
    return;
  }
  setSmtpConfigs(prev => prev.filter(cfg => cfg.id !== id));
};

const updateSmtpConfig = (id, field, value) => {
  setSmtpConfigs(prev =>
    prev.map(cfg =>
      cfg.id === id ? { ...cfg, [field]: value } : cfg
    )
  );
};

const handleSmtpProviderChange = (id, providerKey) => {
  const preset = smtpProviderPresets[providerKey] || smtpProviderPresets.custom;
  setSmtpConfigs(prev =>
    prev.map(cfg =>
      cfg.id === id
        ? {
            ...cfg,
            provider: providerKey,
            host: preset.host,
            port: preset.port,
            secure: preset.secure,
          }
        : cfg
    )
  );
};

  const normalizeListFromPayload = (payload) => {
    if (!payload) return [];
    const candidate = payload?.data ?? payload?.items ?? payload;
    const toArray = Array.isArray(candidate)
      ? candidate
      : typeof candidate === 'string'
        ? candidate.split(/[;,\n]+/)
        : [];

    return toArray
      .map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '')))
      .filter(Boolean);
  };

  function buildPursuedSummary(rows) {
    return rows
      .filter((lead) => Number(lead.Pursue ?? 0) === 1)
      .reduce((acc, lead) => {
        const sectorKey = lead.Sector && lead.Sector.trim() ? lead.Sector : 'Unknown Sector';
        acc[sectorKey] = (acc[sectorKey] || 0) + 1;
        return acc;
      }, {});
  }

  const startAvenorQueueJob = async ({ endpoint, method = 'GET', body, title, returnState = {} }) => {
    const response = await fetch(endpoint, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });

    const payload = await parseJsonLenient(response, endpoint);
    if (!response.ok) {
      throw new Error(payload?.message || 'Failed to start queued job');
    }

    if (payload?.queued && payload?.jobId) {
      navigate(`/avenorloading/leads/${payload.jobId}`, {
        state: {
          title,
          backPath: '/leads',
          returnLabel: 'Return to Leads',
          ...returnState,
        },
      });
    }

    return payload;
  };

  const waitForLeadsJobCompletion = async (statusUrl, timeoutMs = 180000) => {
    const resolvedStatusUrl = statusUrl.startsWith('http')
      ? statusUrl
      : `https://api.insightos.co.uk${statusUrl.startsWith('/') ? statusUrl : `/${statusUrl}`}`;
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      const response = await fetch(resolvedStatusUrl, {
        credentials: 'include',
      });
      const payload = await parseJsonLenient(response, resolvedStatusUrl);

      if (response.ok && payload?.status) {
        const jobStatus = String(payload.status).toLowerCase();
        if (jobStatus === 'completed') {
          return payload;
        }
        if (jobStatus === 'failed') {
          throw new Error(payload?.failedReason || payload?.message || 'Queued email send failed.');
        }
      }

      await new Promise((resolve) => window.setTimeout(resolve, 1000));
    }

    throw new Error('Timed out while waiting for the queued email send to finish.');
  };

  const queueInitialEmailSend = async ({ lead, initialEmail }) => {
    const response = await fetch(`https://api.insightos.co.uk/sales/leads/${lead.id}/initial-email?queue=1`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        initialEmail,
        queue: 1,
        sector: lead?.Sector ?? '',
        location: lead?.Location ?? '',
      }),
    });

    const payload = await parseJsonLenient(response, `https://api.insightos.co.uk/sales/leads/${lead.id}/initial-email?queue=1`);
    if (!response.ok) {
      throw new Error(payload?.message || 'Failed to queue initial email send.');
    }

    if (payload?.queued && payload?.statusUrl) {
      await waitForLeadsJobCompletion(payload.statusUrl);
    }

    return payload;
  };

  // Centralised fetch so it can be reused after creating a pull
  const fetchLeads = async () => {
    setTableLoading(true);
    setError('');
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/leadtable', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }

      const data = await parseJsonOrThrow(response, '/leads/leadtable');
      const leadRows = Array.isArray(data) ? data : data?.leads || [];
      setLeads(leadRows);
      setExpandedRows(new Set());
      if (showPursuedOnly) {
        setSectorSummary(buildPursuedSummary(leadRows));
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error loading leads');
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    const initialise = async () => {
      await Promise.all([fetchLeads(), fetchCompanyCredits(), fetchSmtpSettings()]);
    };

    initialise();
  }, []);

  useEffect(() => {
  const inboxes = smtpConfigs.map((cfg) => ({
    fromEmail: cfg.fromEmail || cfg.username || 'Unnamed',
    stage: cfg.stage || 1,
    sent: cfg.sent || 0,
  }));
  const warmup = getWarmupStatsFromInboxes(inboxes, emailSentToday);
  setEmailStage(warmup.emailStage);
  setEmailDailyCap(warmup.emailDailyCap);
  setEmailRemainingToday(warmup.emailRemainingToday);
}, [smtpConfigs, emailSentToday]);

  // Poll company data (including Notification) every 15 min at aligned boundaries (:00, :15, :30, :45)
  useEffect(() => {
    let intervalId = null;
    const INTERVAL_MS = 15 * 60 * 1000;
    const now = new Date();
    const msIntoInterval = ((now.getMinutes() % 15) * 60 + now.getSeconds()) * 1000 + now.getMilliseconds();
    const msUntilNext = INTERVAL_MS - msIntoInterval;

    const timeoutId = window.setTimeout(() => {
      fetchCompanyCredits();
      intervalId = window.setInterval(() => {
        fetchCompanyCredits();
      }, INTERVAL_MS);
    }, msUntilNext);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const incomingLocation = String(routeLocation.state?.locationFilter || '').trim();
    const incomingSector = String(routeLocation.state?.sectorFilter || '').trim();
    const incomingCompanySearch = String(routeLocation.state?.companySearch || '').trim();

    if (incomingLocation) {
      setLocation(incomingLocation);
    }

    if (incomingSector) {
      setSector(incomingSector);
    }

    if (incomingCompanySearch) {
      setGlobalLeadSearch(incomingCompanySearch);
    }
  }, [routeLocation.state]);

  const extractCompanyRow = (payload) => {
    const rowsCandidate = payload?.data;
    if (Array.isArray(rowsCandidate)) {
      if (rowsCandidate.length > 0 && Array.isArray(rowsCandidate[0])) {
        return rowsCandidate[0][0] ?? null;
      }
      return rowsCandidate[0] ?? null;
    }
    return rowsCandidate && typeof rowsCandidate === 'object' ? rowsCandidate : null;
  };

 const fetchCompanyCredits = async () => {
  setCreditsLoading(true);
  try {
    const response = await fetch('https://api.insightos.co.uk/leads/company', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch company credits');
    }

    const payload = await parseJsonOrThrow(response, '/leads/company');
    const row = extractCompanyRow(payload);

    const nextSalesCredits = Number(row?.SalesCredits ?? 0);
    const nextCreditsUsed = Number(row?.CreditsUsed ?? 0);
    const nextEmailSent = Number(row?.EmailSent ?? 0);
    const nextEmailSentToday = Number(row?.EmailSentToday ?? 0);

    setSalesCredits(Number.isFinite(nextSalesCredits) ? nextSalesCredits : 0);
    setCreditsUsed(Number.isFinite(nextCreditsUsed) ? nextCreditsUsed : 0);
    setEmailSent(nextEmailSent);
    setEmailSentToday(nextEmailSentToday);

    setCompanyForm((prev) => ({
      ...prev,
      Email: String(row?.Email ?? ''),
      Telephone: String(row?.Telephone ?? ''),
      CompanyName: String(row?.CompanyName ?? ''),
      Industry: String(row?.Industry ?? ''),
      Website: String(row?.Website ?? ''),
      ContactName: String(row?.ContactName ?? ''),
      Address: String(row?.Address ?? ''),
      Postcode: String(row?.Postcode ?? ''),
      Description: String(row?.Description ?? ''),
      ValueProposition: String(row?.ValueProposition ?? ''),
      CallToAction: String(row?.CallToAction ?? ''),
      EmailFooter: String(row?.EmailFooter ?? ''),
    }));
    setSavedEmailFooterHtml(String(row?.EmailFooter ?? ''));
    setCompanyNotification(row?.Notification ?? null);
  } catch (err) {
    console.error(err);
  } finally {
    setCreditsLoading(false);
  }
};

const fetchSmtpSettings = async () => {
  try {
    const response = await fetch('https://api.insightos.co.uk/leads/smptp', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const payload = await parseJsonOrThrow(response, '/leads/smptp');
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.message || 'Failed to load SMTP settings');
    }

    let data = payload?.data;
    if (data && !Array.isArray(data)) {
      data = [data];
    }
    if (!data || data.length === 0) {
      data = [createEmptySmtpConfig()];
    }
    const configs = data.map((cfg, idx) => ({
      ...cfg,
      id: cfg.id || `smtp-${idx}-${Date.now()}`,
      provider: cfg.provider || 'custom',
    }));
    setSmtpConfigs(configs);
  } catch (err) {
    console.error('Failed to load SMTP settings:', err);
    setSmtpError('Unable to load SMTP settings from server.');
    // 👇 Set a default config so the user can add one
    setSmtpConfigs([createEmptySmtpConfig()]);
  }
};

const checkSmtpConnection = async (config) => {
  if (!config.host.trim() || !config.username.trim() || !config.password.trim()) {
    setSmtpConnected(null);
    setSmtpConnectionMessage('Enter host, username, and password to check connection.');
    return;
  }

  setSmtpChecking(true);
  try {
    const response = await fetch('https://api.insightos.co.uk/leads/smptp/check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(config),
    });
    const payload = await parseJsonOrThrow(response, '/leads/smptp/check');
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.message || 'Connection check failed');
    }
    setSmtpConnected(payload?.connected === true);
    setSmtpConnectionMessage(payload?.message || (payload?.connected ? 'Connected' : 'Not connected'));
    setSmtpLastCheckedAt(new Date().toLocaleTimeString());
  } catch (err) {
    console.error(err);
    setSmtpConnected(false);
    setSmtpConnectionMessage(err.message || 'Connection check failed');
    setSmtpLastCheckedAt(new Date().toLocaleTimeString());
  } finally {
    setSmtpChecking(false);
  }
};


  const handleLeadPull = async () => {
    setError('Lead pulls now use the targeted SIC code workflow.');
  };



  const requestAiList = async (endpoint, promptValue, setter, loadingSetter, label) => {
    if (!promptValue.trim()) {
      setError(`Please provide a prompt for ${label}.`);
      return;
    }

    loadingSetter(true);
    setError('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt: promptValue.trim() }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${label}`);
      }

      const payload = await parseJsonOrThrow(response, endpoint);
      setter(normalizeListFromPayload(payload));
    } catch (err) {
      console.error(err);
      setError(err.message || `Unable to load ${label}.`);
      setter([]);
    } finally {
      loadingSetter(false);
    }
  };

  const handleAiLocationsRequest = () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow AI features.');
      return;
    }
    requestAiList('https://api.insightos.co.uk/leads/ai/locations', aiLocationPrompt, setAiLocations, setAiLocationsLoading, 'towns/areas');
  };

  const handleAiSectorsRequest = () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow AI features.');
      return;
    }
    requestAiList(
      'https://api.insightos.co.uk/leads/ai/sectors',
      aiSectorPrompt,
      (nextSectors) => {
        const existingSectorLookup = new Set(
          existingLeadSectors.map((item) => normalizeSectorKey(item)).filter(Boolean)
        );

        setAiSectors(
          nextSectors.filter((item) => !existingSectorLookup.has(normalizeSectorKey(item)))
        );
      },
      setAiSectorsLoading,
      'sectors'
    );
  };

// Add to your state declarations
const [linkedInUpdating, setLinkedInUpdating] = useState(null);

// Handler functions


  const removeAiLocation = (value) => {
    setAiLocations((prev) => prev.filter((item) => item !== value));
  };

  const toggleSicCode = (code) => {
    setSelectedSicCodes((previous) =>
      previous.includes(code)
        ? previous.filter((item) => item !== code)
        : [...previous, code]
    );
  };

  const addManualTargetLocation = () => {
    const nextLocation = manualTargetLocation.trim();
    if (!nextLocation) return;
    setAiLocations((previous) => [...new Set([...previous, nextLocation])]);
    setManualTargetLocation('');
  };

  const removeAiSector = (value) => {
    setAiSectors((prev) => prev.filter((item) => item !== value));
  };

  const toggleExistingAiSector = (value) => {
    const normalizedValue = String(value ?? '').trim();
    if (!normalizedValue) {
      return;
    }

    setSelectedExistingAiSectors((prev) =>
      prev.includes(normalizedValue)
        ? prev.filter((item) => item !== normalizedValue)
        : [...prev, normalizedValue]
    );
  };

  const handleAiLeadPullLoop = async () => {
    setError('AI lead pulls now use the targeted SIC code workflow.');
  };

  const handleGenerate = async (limitOverride = null) => {
    setFilterLoading(true);
    setError('');

    try {
      const parsedLimit = Number(limitOverride);
      const fallbackLimit = Math.max(0, Number(enrichCreditsToUse) || 0);
      const creditBudget = Number.isFinite(parsedLimit) && parsedLimit > 0 ? Math.floor(parsedLimit) : fallbackLimit;

      if (creditBudget < 2) {
        throw new Error('Please choose at least 2 credits to run enrich.');
      }

      await startAvenorQueueJob({
        endpoint: 'https://api.insightos.co.uk/leads/filter',
        method: 'POST',
        body: {
          limit: creditBudget,
          targetLeadIds: enrichTargetLeadIds,
        },
        title: 'Enriching leads',
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error filtering leads');
    } finally {
      setFilterLoading(false);
    }
  };

  

  // Animation removed: no effect required for credit animation

  // Pursued summary modal/section removed; pursue filter now toggles directly via button

  const handleDeleteLead = async (rowId) => {
    if (!rowId) {
      setError('Cannot delete lead without an identifier.');
      return;
    }

    setDeletingLeadId(rowId);
    setError('');

    try {
      const response = await fetch(`https://api.insightos.co.uk/leads/leads/${rowId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete lead');
      }
      // credits are now being returned i want it in a prompt box 
      const data = await parseJsonLenient(response, `https://api.insightos.co.uk/leads/leads/${rowId}`);
      if (data?.credits) {
        alert(`Credits returned: ${data.credits}`);
      }
      
      setGlobalLeadSearch('');

      await fetchLeads();
    } catch (err) {
      console.error(err);
      setError('Error deleting lead');
    } finally {
      setDeletingLeadId(null);
    }
  };

  const handleSingleLeadEnrich = async (lead) => {
    const leadId = Number(lead?.id);
    const filterValue = Number(lead?.Filter ?? 0);

    if (!Number.isInteger(leadId) || leadId <= 0) {
      setError('Cannot enrich lead without a valid identifier.');
      return;
    }

    if (filterValue === 1) {
      setError('This lead is already enriched (Filter = 1).');
      return;
    }

    setSingleLeadEnrichingId(leadId);
    setError('');

    try {
      await startAvenorQueueJob({
        endpoint: `https://api.insightos.co.uk/leads/indfilter/${leadId}`,
        method: 'GET',
        title: 'Enriching selected lead',
        returnState: {
          companySearch: String(lead?.Company ?? '').trim(),
        },
      });
    } catch (err) {
      console.error(err);
      setError('Error enriching selected lead.');
    } finally {
      setSingleLeadEnrichingId(null);
    }
  };

  const handleGenerateCallScript = async (lead) => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow AI features.');
      return;
    }

    const leadId = Number(lead?.id);
    if (!Number.isInteger(leadId) || leadId <= 0) {
      setError('Cannot generate call script without a valid lead id.');
      return;
    }

    setCallScriptGeneratingId(leadId);
    setError('');

    try {
      const response = await fetch(`https://api.insightos.co.uk/leads/${leadId}/call-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const payload = await parseJsonOrThrow(response, `/leads/${leadId}/call-script`);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to generate call script');
      }

      const script = String(payload?.script || '').trim();
      if (!script) {
        throw new Error('No call script was returned.');
      }

      setLeads((prevLeads) =>
        prevLeads.map((existingLead) =>
          existingLead.id === leadId
            ? {
                ...existingLead,
                CallScript: script,
              }
            : existingLead
        )
      );

      openModal(`Call Script - ${lead?.Company || lead?.LeadName || 'Lead'}`, script);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to generate call script.');
    } finally {
      setCallScriptGeneratingId(null);
    }
  };

  const safeFormatDate = (value, fmt = 'dd/MM/yyyy') => {
    if (!value) return 'No Information';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return 'No Information';
      return format(d, fmt);
    } catch {
      return 'No Information';
    }
  };

  const parseLeadContactDate = (lead) => {
    const value = lead?.LastContacted || lead?.Contacted;
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isLeadWithinContactDateRange = (lead) => {
    const hasFrom = Boolean(contactedFromDate);
    const hasTo = Boolean(contactedToDate);

    if (!hasFrom && !hasTo) {
      return true;
    }

    const contactDate = parseLeadContactDate(lead);
    if (!contactDate) {
      return false;
    }

    if (hasFrom) {
      const start = new Date(`${contactedFromDate}T00:00:00`);
      if (contactDate < start) {
        return false;
      }
    }

    if (hasTo) {
      const end = new Date(`${contactedToDate}T23:59:59.999`);
      if (contactDate > end) {
        return false;
      }
    }

    return true;
  };

  const handleProgressChange = async (leadId, progress) => {
    if (!leadId) {
      return;
    }

    const allowedProgress = new Set(['No Reply', 'Interested', 'Sale']);
    if (!allowedProgress.has(progress)) {
      setError('Invalid progress value selected.');
      return;
    }

    setProgressSavingLeadId(leadId);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/leads/progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ leadId, progress }),
      });

      const payload = await parseJsonLenient(response, '/leads/progress');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to update lead progress.');
      }

      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId
            ? {
                ...lead,
                Progress: progress,
              }
            : lead
        )
      );
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Unable to update progress.');
    } finally {
      setProgressSavingLeadId(null);
    }
  };

  const handleAddToCrm = async (lead) => {
    const leadId = Number(lead?.id);
    if (!Number.isInteger(leadId) || leadId <= 0) {
      setError('Cannot add to CRM without a valid lead id.');
      return;
    }

    setCrmAddingLeadId(leadId);
    window.location.assign('https://app.insightos.co.uk/avenor');
  };

 const filteredLeads = useMemo(() => {
    const locationQuery = location.trim().toLowerCase();
    const sectorQuery = sector.trim().toLowerCase();
    const globalSearchQuery = globalLeadSearch.trim().toLowerCase();
    const minRevenue = parseExtractedRevenueValue(extractedRevenueMin);
    const maxRevenue = parseExtractedRevenueValue(extractedRevenueMax);
    const normalizedMin = minRevenue !== null && maxRevenue !== null && minRevenue > maxRevenue ? maxRevenue : minRevenue;
    const normalizedMax = minRevenue !== null && maxRevenue !== null && minRevenue > maxRevenue ? minRevenue : maxRevenue;

    const filtered = leads.filter((lead) => {
      const matchesLocation = !locationQuery || String(lead.Location ?? '').toLowerCase().includes(locationQuery);
      const matchesSector = !sectorQuery || String(lead.Sector ?? '').toLowerCase().includes(sectorQuery);

      const filterValue = Number(lead.Filter ?? 0);
      const pursueValue = Number(lead.Pursue ?? 0);
      const companiesHouseHitValue = Number(lead.CompaniesHouseHit ?? 0);
      const matchesPursue = !showPursuedOnly || pursueValue === 1;
      const matchesCompaniesHouseHit = !showCompaniesHouseHitOnly || companiesHouseHitValue === 1;
      const hasContactDate = Boolean(parseLeadContactDate(lead));
      const matchesContacted = !showContactedOnly || hasContactDate;
      const matchesContactDateRange = isLeadWithinContactDateRange(lead);
      const extractedRevenue = parseExtractedRevenueValue(lead?.ExtractedRevenue);
      const matchesRevenueRange =
        (normalizedMin === null || (extractedRevenue !== null && extractedRevenue >= normalizedMin)) &&
        (normalizedMax === null || (extractedRevenue !== null && extractedRevenue <= normalizedMax));

      const matchesFiltered = !showFilteredOnly || filterValue === 1 || companiesHouseHitValue === 1;
      
      // ← add this
      const matchesUnsentEnriched = !showUnsentEnrichedOnly || (
        (filterValue === 1 || companiesHouseHitValue === 1) && !lead.EmailSent
      );
      const matchesScheduled = !showScheduledOnly || Boolean(lead.InitalSchedule);
      const matchesLinkedInOnly = !showLinkedInOnly || (Number(lead.EmailConfidence ?? 0) === 0 && filterValue === 1);
      const matchesLinkedInStatusFilter = !showLinkedInOnly || !linkedInStatusFilter || String(lead.LinkedInStatus || '').trim() === linkedInStatusFilter;
      const matchesEmailCampaignOnly = !showEmailCampaignOnly || (Number(lead.EmailConfidence ?? 0) > 0 && filterValue === 1);
      const matchesUnfilteredOnly = !showUnfilteredOnly || filterValue === 0;
      const matchesLinkedInMessageSentOnly = !showLinkedInMessageSentOnly || !!lead.LinkedInSent;
      const matchesFollowUpStage = !followUpStageFilter || getLeadFollowUpStage(lead).stage === Number(followUpStageFilter);
      // Exact match only; leads with no FunnelPosition are excluded when a stage is selected
      const matchesFunnelStage = !funnelStageFilter || lead?.FunnelPosition === funnelStageFilter;

      const matchesGlobalSearch =
  
        !globalSearchQuery ||
        Object.values(lead || {}).some((value) => String(value ?? '').toLowerCase().includes(globalSearchQuery));
      const matchesCredits = filterByCredits === null || Number(lead.Credits ?? lead.credits ?? 0) === filterByCredits;
      const matchesUpdatedAtDate = (() => {
        if (!updatedAtFilterDate) return true;
        const raw = lead.UpdatedAt ?? lead.updatedAt ?? lead.updated_at;
        if (!raw) return false;
        const parsedDate = new Date(raw);
        return !Number.isNaN(parsedDate.getTime()) && parsedDate.toISOString().slice(0, 10) === updatedAtFilterDate;
      })();

      return (
        matchesLocation &&
        matchesSector &&
        matchesPursue &&
        matchesCompaniesHouseHit &&
        matchesFiltered &&
        matchesUnsentEnriched &&  // ← add this
        matchesScheduled &&
        matchesLinkedInOnly &&
        matchesLinkedInStatusFilter &&
        matchesEmailCampaignOnly &&
        matchesUnfilteredOnly &&
        matchesLinkedInMessageSentOnly &&
        matchesFollowUpStage &&
        matchesFunnelStage &&
        matchesContacted &&
        matchesContactDateRange &&
        matchesRevenueRange &&
        matchesGlobalSearch &&
        matchesCredits &&
        matchesUpdatedAtDate
      );
    });

    let sorted = filtered;
    if (locationSortOrder === 'asc') {
      sorted = [...filtered].sort((a, b) => {
        const locA = String(a.Location ?? '').toLowerCase();
        const locB = String(b.Location ?? '').toLowerCase();
        return locA.localeCompare(locB);
      });
    } else if (locationSortOrder === 'desc') {
      sorted = [...filtered].sort((a, b) => {
        const locA = String(a.Location ?? '').toLowerCase();
        const locB = String(b.Location ?? '').toLowerCase();
        return locB.localeCompare(locA);
      });
    }

    // Permanently split into a LinkedIn group (EmailConfidence 0) and an Email group (EmailConfidence > 0),
    // LinkedIn leads first; within each group, the most recently updated leads come first.
    return [...sorted].sort((a, b) => {
      const aGroup = Number(a.EmailConfidence ?? 0) === 0 ? 0 : 1;
      const bGroup = Number(b.EmailConfidence ?? 0) === 0 ? 0 : 1;
      if (aGroup !== bGroup) return aGroup - bGroup;
      const aTime = new Date(a.UpdatedAt ?? a.updatedAt ?? a.updated_at ?? 0).getTime() || 0;
      const bTime = new Date(b.UpdatedAt ?? b.updatedAt ?? b.updated_at ?? 0).getTime() || 0;
      return bTime - aTime;
    });
  }, [
    leads,
    location,
    sector,
    showPursuedOnly,
    showCompaniesHouseHitOnly,
    showFilteredOnly,
    showUnsentEnrichedOnly,  // ← add this
    showScheduledOnly,
    showLinkedInOnly,
    linkedInStatusFilter,
    showEmailCampaignOnly,
    showUnfilteredOnly,
    showLinkedInMessageSentOnly,
    followUpStageFilter,
    funnelStageFilter,
    showContactedOnly,
    contactedFromDate,
    contactedToDate,
    globalLeadSearch,
    extractedRevenueMin,
    extractedRevenueMax,
    locationSortOrder,
    filterByCredits,
    updatedAtFilterDate,
  ]);

  const filteredByGenerateCount = useMemo(() => {
    return leads.filter((lead) => Number(lead.Filter ?? 0) === 1).length;
  }, [leads]);

  // Counts for the permanent LinkedIn / Email split, based on the currently active filters
  const linkedInGroupCount = useMemo(
    () => filteredLeads.filter((lead) => Number(lead.EmailConfidence ?? 0) === 0).length,
    [filteredLeads]
  );
  const emailGroupCount = useMemo(
    () => filteredLeads.filter((lead) => Number(lead.EmailConfidence > 0) > 0).length,
    [filteredLeads]
  );

  // Per-filter totals shown as badges in the quick filters bar, computed against the full lead set
  const quickFilterCounts = useMemo(() => ({
    emailCampaign: leads.filter((lead) => Number(lead.EmailConfidence ?? 0) > 0 && Number(lead.Filter ?? 0) === 1).length,
    unfiltered: leads.filter((lead) => Number(lead.Filter ?? 0) === 0).length,
    scheduled: leads.filter((lead) => Boolean(lead.InitalSchedule)).length,
    contacted: leads.filter((lead) => Boolean(parseLeadContactDate(lead))).length,
    unsent: leads.filter((lead) => (Number(lead.Filter ?? 0) === 1 || Number(lead.CompaniesHouseHit ?? 0) === 1) && !lead.EmailSent).length,
    linkedInOnly: leads.filter((lead) => Number(lead.EmailConfidence ?? 0) === 0 && Number(lead.Filter ?? 0) === 1).length,
    liSent: leads.filter((lead) => Boolean(lead.LinkedInSent)).length,
  }), [leads]);

  const visibleFilteredLeads = useMemo(() => {
    return filteredLeads.slice(0, visibleLeadCount);
  }, [filteredLeads, visibleLeadCount]);

  const hasMoreFilteredLeads = visibleLeadCount < filteredLeads.length;

  useEffect(() => {
    setVisibleLeadCount(INITIAL_LEADS_RENDER_COUNT);
  }, [
    filteredLeads.length,
    location,
    sector,
    showFilteredOnly,
    showCompaniesHouseHitOnly,
    showPursuedOnly,
    showContactedOnly,
    contactedFromDate,
    contactedToDate,
    globalLeadSearch,
    locationSortOrder,
  ]);

  const contactedInDateRangeCount = useMemo(() => {
    return leads.filter((lead) => parseLeadContactDate(lead) && isLeadWithinContactDateRange(lead)).length;
  }, [leads, contactedFromDate, contactedToDate]);

  useEffect(() => {
    if (!hasMoreFilteredLeads || !leadsLoadMoreRef.current) {
      return;
    }

    const target = leadsLoadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) {
          return;
        }

        setVisibleLeadCount((prev) => Math.min(prev + LEADS_RENDER_BATCH_COUNT, filteredLeads.length));
      },
      {
        root: null,
        rootMargin: '240px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreFilteredLeads, filteredLeads.length]);

  useEffect(() => {
    const sentinel = filterSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setFilterIsSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { root: null, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const enrichTargetLeads = useMemo(() => {
    return filteredLeads.filter((lead) => Number(lead?.Filter ?? 0) !== 1);
  }, [filteredLeads]);

  const enrichPreview = useMemo(() => {
    const target = enrichTargetLeads;
    const targetCount = target.length;
    const companiesHouseHitCount = target.filter((lead) => Number(lead.CompaniesHouseHit ?? 0) === 1).length;
    const minimumPerLeadCredits = 2;
    const perLeadCredits = 3;
    const selectedCredits = Math.max(0, Number(enrichCreditsToUse) || 0);
    const maxLeadsByCredits = Math.floor(selectedCredits / perLeadCredits);
    const leadsToEnrich = Math.min(targetCount, maxLeadsByCredits, ENRICH_QUEUE_LEAD_CAP);
    return {
      perLeadCredits,
      targetCount,
      companiesHouseHitCount,
      selectedCredits,
      maxLeadsByCredits,
      leadsToEnrich,
      queueLeadCap: ENRICH_QUEUE_LEAD_CAP,
      maxCreditsAllowedByQueueCap: ENRICH_QUEUE_LEAD_CAP * perLeadCredits,
      minimumPerLeadCredits,
      minimumCreditsForAll: targetCount * minimumPerLeadCredits,
      estimatedCreditsForAll: targetCount * perLeadCredits,
      creditsToConsumeNow: leadsToEnrich * perLeadCredits,
    };
  }, [enrichTargetLeads, enrichCreditsToUse, ENRICH_QUEUE_LEAD_CAP]);

  const enrichTargetLeadIds = useMemo(() => {
    return enrichTargetLeads
      .map((lead) => Number(lead?.id))
      .filter((leadId) => Number.isInteger(leadId) && leadId > 0);
  }, [enrichTargetLeads]);

const dashboardStats = useMemo(() => {
  // ── Basic counts ──
  const totalLeads = leads.length;
  const pursuedLeads = leads.filter((lead) => Number(lead.Pursue ?? 0) === 1).length;
  const filteredCount = leads.filter((lead) => Number(lead.Filter ?? 0) === 1).length;
  const leadsWithInitialEmails = leads.filter((lead) => (lead.InitialEmail ?? '').trim()).length;
  const leadsContacted = leads.filter((lead) => lead.Contacted).length;
  const leadsResponded = leads.filter((lead) => lead.Responded).length;
  const companiesHouseHitCount = leads.filter((lead) => Number(lead.CompaniesHouseHit ?? 0) === 1).length;

  // ── Scheduled stats ──
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday

  const scheduledToday = leads.filter((lead) => {
    if (!lead.InitalSchedule) return false;
    const d = new Date(lead.InitalSchedule);
    return !isNaN(d.getTime()) && isToday(d);
  }).length;

  const scheduledTomorrow = leads.filter((lead) => {
    if (!lead.InitalSchedule) return false;
    const d = new Date(lead.InitalSchedule);
    return !isNaN(d.getTime()) && isTomorrow(d);
  }).length;

  const timeLabels = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
  const scheduledTodayByTime = timeLabels.map((label) => {
    const count = leads.filter((lead) => {
      if (!lead.InitalSchedule) return false;
      const d = new Date(lead.InitalSchedule);
      return !isNaN(d.getTime()) && isToday(d) && format(d, 'HH:mm') === label;
    }).length;
    return { x: label, y: count };
  });

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const scheduledThisWeek = dayLabels.map((label, i) => {
    const day = addDays(weekStart, i);
    const count = leads.filter((lead) => {
      if (!lead.InitalSchedule) return false;
      const d = new Date(lead.InitalSchedule);
      return !isNaN(d.getTime()) && isSameDay(d, day);
    }).length;
    return { x: label, y: count };
  });

  // ── LinkedIn sent stats ──
  const linkedInSentToday = leads.filter((lead) => {
    const val = lead.LinkedInSent ?? lead.LinkedInMessageSent;
    if (!val) return false;
    const d = new Date(val);
    return !isNaN(d.getTime()) && isToday(d);
  }).length;

  const linkedInSentThisWeek = leads.filter((lead) => {
    const val = lead.LinkedInSent ?? lead.LinkedInMessageSent;
    if (!val) return false;
    const d = new Date(val);
    if (isNaN(d.getTime())) return false;
    return d >= weekStart && d < addDays(weekStart, 7);
  }).length;

  const linkedInSentThisMonth = leads.filter((lead) => {
    const val = lead.LinkedInSent ?? lead.LinkedInMessageSent;
    if (!val) return false;
    const d = new Date(val);
    return !isNaN(d.getTime()) && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).length;

  // ── Top locations & sectors ──
  const locationCounts = leads.reduce((acc, lead) => {
    const loc = lead.Location?.trim() || 'Unknown';
    acc[loc] = (acc[loc] || 0) + 1;
    return acc;
  }, {});
  const topLocations = Object.entries(locationCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const sectorCounts = leads.reduce((acc, lead) => {
    const sec = lead.Sector?.trim() || 'Unknown';
    acc[sec] = (acc[sec] || 0) + 1;
    return acc;
  }, {});
  const topSectors = Object.entries(sectorCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  // ── Inbox data ──
  const inboxes = smtpConfigs.map((cfg) => ({
    fromEmail: cfg.fromEmail || cfg.username || 'Unnamed',
    stage: cfg.stage || 1,
    sent: cfg.sent || 0,
  }));

  // ── Warmup stats from inboxes ──
  const warmup = getWarmupStatsFromInboxes(inboxes, emailSentToday);

  // ── Percentages ──
  const pursuePercentage = totalLeads > 0 ? ((pursuedLeads / totalLeads) * 100).toFixed(1) : 0;
  const companiesHousePercentage = totalLeads > 0 ? ((companiesHouseHitCount / totalLeads) * 100).toFixed(1) : 0;
  const enrichedOfTotalPct = totalLeads > 0 ? ((filteredCount / totalLeads) * 100).toFixed(1) : '0.0';
  const contactedOfEnrichedPct = filteredCount > 0 ? ((leadsContacted / filteredCount) * 100).toFixed(1) : '0.0';

  // ── Return everything ──
  return {
    totalLeads,
    pursuedLeads,
    filteredCount,
    leadsWithInitialEmails,
    leadsContacted,
    leadsResponded,
    companiesHouseHitCount,
    // Warmup
    emailStage: warmup.emailStage,
    emailSent: emailSent,               // global total (from fetchCompanyCredits)
    emailSentToday: emailSentToday,     // global today (from fetchCompanyCredits)
    emailDailyCap: warmup.emailDailyCap,
    emailRemainingToday: warmup.emailRemainingToday,
    // Top locations / sectors
    topLocations,
    topSectors,
    pursuePercentage,
    companiesHousePercentage,
    enrichedOfTotalPct,
    contactedOfEnrichedPct,
    // Scheduled
    scheduledToday,
    scheduledTomorrow,
    scheduledThisWeek,
    scheduledTodayByTime,
    // LinkedIn
    linkedInSentToday,
    linkedInSentThisWeek,
    linkedInSentThisMonth,
    // Inboxes (for the swipeable cards)
    inboxes,
  };
}, [
  leads,
  emailSent,
  emailSentToday,
  smtpConfigs,
 
]);

  const availableLocations = useMemo(() => {
    const set = new Set();
    leads.forEach((lead) => {
      if (lead.Location && lead.Location.trim()) {
        set.add(lead.Location);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  const existingLeadSectors = useMemo(() => {
    const set = new Set();

    leads.forEach((lead) => {
      const nextSector = String(lead?.Sector ?? '').trim();
      if (nextSector) {
        set.add(nextSector);
      }
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  const locationOptionsForActiveFilters = useMemo(() => {
    const options = new Set();

    leads
      .filter((lead) => {
        const filterValue = Number(lead.Filter ?? 0);
        const companiesHouseHitValue = Number(lead.CompaniesHouseHit ?? 0);
        const matchesFiltered = !showFilteredOnly || filterValue === 1 || companiesHouseHitValue === 1;
        const matchesCompaniesHouseHit = !showCompaniesHouseHitOnly || companiesHouseHitValue === 1;
        return matchesFiltered && matchesCompaniesHouseHit;
      })
      .forEach((lead) => {
        const nextLocation = String(lead.Location ?? '').trim();
        if (nextLocation) {
          options.add(nextLocation);
        }
      });

    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [leads, showFilteredOnly, showCompaniesHouseHitOnly]);

  const sectorOptionsForActiveFilters = useMemo(() => {
    const options = new Set();

    leads
      .filter((lead) => {
        if (showFilteredOnly && Number(lead.Filter ?? 0) !== 1) return false;
        if (showCompaniesHouseHitOnly && Number(lead.CompaniesHouseHit ?? 0) !== 1) return false;
        return true;
      })
      .forEach((lead) => {
        const value = String(lead?.Sector ?? '').trim();
        if (value) options.add(value);
      });

    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [leads, showFilteredOnly, showCompaniesHouseHitOnly]);

  const sectorLocationBreakdown = useMemo(() => {
    const targetLeads = (bulkApplyFilteredOnly ? filteredLeads : leads).filter(
      (lead) => lead?.id && (lead.InitialEmail ?? '').trim() && Number(lead.EmailConfidence ?? 0) > 50 && Number(lead.OfficerEmailConfidence ?? 0) > 50
    );
    const breakdown = {};
    for (const lead of targetLeads) {
      const sector = lead.Sector ?? 'Unknown';
      const location = lead.Location ?? 'Unknown';
      const key = `${sector}|||${location}`;
      if (!breakdown[key]) {
        breakdown[key] = { sector, location, count: 0, leads: [] };
      }
      breakdown[key].count += 1;
      breakdown[key].leads.push(lead);
    }
    return breakdown;
  }, [leads, filteredLeads, bulkApplyFilteredOnly]);

  const totalBulkSendEligible = useMemo(
    () => Object.values(sectorLocationBreakdown).reduce((sum, group) => sum + group.count, 0),
    [sectorLocationBreakdown]
  );

  const scheduleEligibleLeads = useMemo(() => {
    const scope = bulkApplyFilteredOnly ? filteredLeads : leads;
    return scope.filter(
      (lead) =>
        (Number(lead.Filter ?? 0) === 1 || Number(lead.Credits ?? lead.credits ?? 0) === 3) &&
        Number(lead.EmailSent ?? 0) !== 1 &&
        Number(lead.EmailConfidence ?? 0) > 0 &&
        lead.id
    );
  }, [leads, filteredLeads, bulkApplyFilteredOnly]);

  const scheduleEligibleByGroup = useMemo(() => {
    const groups = {};
    for (const lead of scheduleEligibleLeads) {
      const sectorKey = lead.Sector ?? 'Unknown';
      const locationKey = lead.Location ?? 'Unknown';
      const key = `${sectorKey}|||${locationKey}`;
      if (!groups[key]) {
        groups[key] = { sector: sectorKey, location: locationKey, leads: [] };
      }
      groups[key].leads.push(lead);
    }
    return groups;
  }, [scheduleEligibleLeads]);

  const followUpEligibleLeads = useMemo(() => {
    const scope = bulkApplyFilteredOnly ? filteredLeads : leads;
    return scope.filter((lead) => Boolean(lead.Contacted) && !lead.FollowUpEmail1 && lead.id && Number(lead.FollowUpPaid ?? 0) !== 1);
  }, [leads, filteredLeads, bulkApplyFilteredOnly]);

  const followUpEligibleByGroup = useMemo(() => {
    const groups = {};
    for (const lead of followUpEligibleLeads) {
      const sectorKey = lead.Sector ?? 'Unknown';
      const locationKey = lead.Location ?? 'Unknown';
      const key = `${sectorKey}|||${locationKey}`;
      if (!groups[key]) {
        groups[key] = { sector: sectorKey, location: locationKey, leads: [] };
      }
      groups[key].leads.push(lead);
    }
    return groups;
  }, [followUpEligibleLeads]);

  const locationSelectValue = availableLocations.includes(location) ? location : '';
  const sectorSelectValue = sectorOptionsForActiveFilters.includes(sector) ? sector : '';

  const editingLead = useMemo(
    () => leads.find((lead) => lead.id === initialEmailEditingId),
    [leads, initialEmailEditingId]
  );

  const editingDraft = editingLead ? initialEmailDrafts[editingLead.id] ?? '' : '';
  const editingMessage = editingLead ? initialEmailMessages[editingLead.id] : null;
  const editingSubmitting = editingLead ? initialEmailSubmittingId === editingLead.id : false;

  const openModal = (title, text) => {
    setModalState({ open: true, title, body: text });
  };

  const closeModal = () => {
    setModalState({ open: false, title: '', body: '' });
  };

  const actionsDisabled = tableLoading || leadPullLoading || filterLoading || aiLeadPullRunning;

  const toggleRowExpansion = (rowId) => {
    if (rowId === undefined || rowId === null) {
      return;
    }

    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleBulkInitialEmailSend = async () => {
    const targetLeads = (bulkApplyFilteredOnly ? filteredLeads : leads).filter((lead) => lead?.id && (lead.InitialEmail ?? '').trim());

    if (targetLeads.length === 0) {
      setBulkInitialSendError('No drafted initial emails found to send.');
      return;
    }

    setBulkInitialSendRunning(true);
    setBulkInitialSendFeedback('');
    setBulkInitialSendError('');

    let sent = 0;
    let failed = 0;

    try {
      for (const lead of targetLeads) {
        try {
          await queueInitialEmailSend({ lead, initialEmail: lead.InitialEmail });
          sent += 1;
        } catch (innerErr) {
          console.error(innerErr);
          failed += 1;
        }
      }

      setBulkInitialSendFeedback(`Sent initial emails. Success: ${sent}, Failed: ${failed}.`);
      await fetchLeads();
    } catch (err) {
      console.error(err);
      setBulkInitialSendError('Bulk initial email send failed.');
    } finally {
      setBulkInitialSendRunning(false);
    }
  };

  const openGranularSendModal = () => {
    const cap = emailRemainingToday > 0 ? Math.min(totalBulkSendEligible, emailRemainingToday) : totalBulkSendEligible;
    setGranularSendQuantity(Math.max(0, cap));
    setGranularSendFeedback('');
    setGranularSendError('');
    setGranularSendModal({ open: true });
  };

  const closeGranularSendModal = () => {
    setGranularSendModal({ open: false });
    setGranularSendQuantity(0);
    setGranularSendFeedback('');
    setGranularSendError('');
  };

  const handleGranularSendQuantityChange = (value) => {
    const parsed = parseInt(value, 10);
    const max = emailRemainingToday > 0 ? Math.min(totalBulkSendEligible, emailRemainingToday) : totalBulkSendEligible;
    const clamped = Math.max(0, Math.min(isNaN(parsed) ? 0 : parsed, max));
    setGranularSendQuantity(clamped);
  };

  const handleGranularSendEmails = async () => {
    const orderedGroups = Object.keys(sectorLocationBreakdown).sort((a, b) => {
      const aData = sectorLocationBreakdown[a];
      const bData = sectorLocationBreakdown[b];
      const sectorCompare = (aData.sector ?? '').localeCompare(bData.sector ?? '');
      if (sectorCompare !== 0) return sectorCompare;
      return (aData.location ?? '').localeCompare(bData.location ?? '');
    });

    const toSend = [];
    for (const key of orderedGroups) {
      if (toSend.length >= granularSendQuantity) break;
      const remainingSlots = granularSendQuantity - toSend.length;
      toSend.push(...sectorLocationBreakdown[key].leads.slice(0, remainingSlots));
    }

    if (toSend.length === 0) {
      setGranularSendError('No emails selected to send.');
      return;
    }

    setGranularSendRunning(true);
    setGranularSendFeedback('');
    setGranularSendError('');

    let sent = 0;
    let failed = 0;

    try {
      for (const lead of toSend) {
        try {
          await queueInitialEmailSend({ lead, initialEmail: lead.InitialEmail });
          sent += 1;
        } catch (innerErr) {
          console.error(innerErr);
          failed += 1;
        }
      }

      setGranularSendFeedback(`Sent ${sent} email(s). Failed: ${failed}.`);
      await fetchLeads();
    } catch (err) {
      console.error(err);
      setGranularSendError('Granular email send failed.');
    } finally {
      setGranularSendRunning(false);
    }
  };



  const openScheduleSendModal = () => {
    setScheduledLeadIds(new Set(scheduleEligibleLeads.map((l) => l.id)));
    setScheduledDateTime('');
    setScheduleSendFeedback('');
    setScheduleSendError('');
    setScheduleModalOpen(true);
  };

  const closeScheduleSendModal = () => {
    setScheduleModalOpen(false);
    setScheduledLeadIds(new Set());
    setScheduledDateTime('');
    setScheduleSendFeedback('');
    setScheduleSendError('');
  };

  const toggleScheduleLead = (leadId) => {
    setScheduledLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const toggleScheduleGroup = (groupKey) => {
    const groupLeads = scheduleEligibleByGroup[groupKey]?.leads ?? [];
    setScheduledLeadIds((prev) => {
      const allSelected = groupLeads.every((l) => prev.has(l.id));
      const next = new Set(prev);
      if (allSelected) {
        groupLeads.forEach((l) => next.delete(l.id));
      } else {
        groupLeads.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };

  const handleScheduledSend = async () => {
    if (!scheduledDateTime) {
      setScheduleSendError('Please select a date and time to schedule.');
      return;
    }
    if (scheduledLeadIds.size === 0) {
      setScheduleSendError('Please select at least one lead.');
      return;
    }
    if (scheduledLeadIds.size > emailRemainingToday) {
      setScheduleSendError(
        `Selection (${scheduledLeadIds.size}) exceeds your remaining daily allowance of ${emailRemainingToday} email(s). Reduce your selection or wait until tomorrow.`
      );
      return;
    }

    setScheduleSendRunning(true);
    setScheduleSendFeedback('');
    setScheduleSendError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/leads/schedule-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          leadIds: Array.from(scheduledLeadIds),
          scheduledAt: scheduledDateTime,
        }),
      });

      const payload = await parseJsonOrThrow(response, '/leads/schedule-send');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to schedule email send.');
      }

      setScheduleSendFeedback(
        `Scheduled ${scheduledLeadIds.size} email(s) for ${new Date(scheduledDateTime).toLocaleString()}.`
      );
      window.setTimeout(() => {
        closeScheduleSendModal();
      }, 2000);

      await fetchLeads();
    } catch (err) {
      console.error(err);
      setScheduleSendError(err.message || 'Failed to schedule send.');
    } finally {
      setScheduleSendRunning(false);
    }
  };

  const openFollowUpModal = () => {
    setFollowUpLeadIds(new Set(followUpEligibleLeads.map((l) => l.id)));
    setFollowUpSendFeedback('');
    setFollowUpSendError('');
    setFollowUpModalOpen(true);
  };

  const closeFollowUpModal = () => {
    setFollowUpModalOpen(false);
    setFollowUpLeadIds(new Set());
    setFollowUpSendFeedback('');
    setFollowUpSendError('');
  };

  const toggleFollowUpLead = (leadId) => {
    setFollowUpLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const toggleFollowUpGroup = (groupKey) => {
    const groupLeads = followUpEligibleByGroup[groupKey]?.leads ?? [];
    setFollowUpLeadIds((prev) => {
      const allSelected = groupLeads.every((l) => prev.has(l.id));
      const next = new Set(prev);
      if (allSelected) {
        groupLeads.forEach((l) => next.delete(l.id));
      } else {
        groupLeads.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };

  const handleFollowUpSend = async () => {
    if (followUpLeadIds.size === 0) {
      setFollowUpSendError('Please select at least one lead.');
      return;
    }
    if (followUpLeadIds.size > creditsRemaining) {
      setFollowUpSendError(
        `Selection (${followUpLeadIds.size}) exceeds your remaining credits (${creditsRemaining}). Each follow-up costs 1 credit.`
      );
      return;
    }

    setFollowUpSendRunning(true);
    setFollowUpSendFeedback('');
    setFollowUpSendError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/leads/post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          leadIds: Array.from(followUpLeadIds),
        }),
      });

      const payload = await parseJsonOrThrow(response, '/leads/post');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to send follow-up emails.');
      }

      setFollowUpSendFeedback(`Sent ${followUpLeadIds.size} follow-up email(s).`);
      window.setTimeout(() => {
        closeFollowUpModal();
      }, 2000);

      await fetchLeads();
      await fetchCompanyCredits();
    } catch (err) {
      console.error(err);
      setFollowUpSendError(err.message || 'Failed to send follow-up emails.');
    } finally {
      setFollowUpSendRunning(false);
    }
  };

  const handleBulkDeleteLeads = async () => {
    const targetLeads = (bulkApplyFilteredOnly ? filteredLeads : leads).filter((lead) => lead?.id);

    if (targetLeads.length === 0) {
      setBulkDeleteError('No leads found to delete.');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${targetLeads.length} lead(s)? This cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    setBulkDeleteRunning(true);
    setBulkDeleteFeedback('');
    setBulkDeleteError('');

    let deleted = 0;
    let failed = 0;

    try {
      for (const lead of targetLeads) {
        try {
          const response = await fetch(`https://api.insightos.co.uk/leads/leads/${lead.id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error('Delete failed');
          }
          deleted += 1;
        } catch (innerErr) {
          console.error(innerErr);
          failed += 1;
        }
      }

      setBulkDeleteFeedback(`Deleted leads. Success: ${deleted}, Failed: ${failed}.`);
      await fetchLeads();
    } catch (err) {
      console.error(err);
      setBulkDeleteError('Bulk delete operation failed.');
    } finally {
      setBulkDeleteRunning(false);
    }
  };

  const openInitialEmailEditor = (lead) => {
    if (!lead?.id) {
      setError('Cannot edit the initial email without a lead identifier.');
      return;
    }

    setInitialEmailEditingId(lead.id);
    setInitialEmailDrafts({ [lead.id]: lead.InitialEmail ?? '' });
    setInitialEmailMessages((prev) => ({
      ...prev,
      [lead.id]: { success: '', error: '' },
    }));

    setTimeout(() => {
    emailEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 50);
  };



  const closeInitialEmailEditor = async (leadId, options = {}) => { // Added async here

    const targetId = leadId ?? initialEmailEditingId;

    // call the leads function to update the leadtable
    await fetchLeads(); // Added await here

    if (!targetId) {

      setInitialEmailEditingId(null);

      return;

    }



    setInitialEmailEditingId((current) => (current === targetId ? null : current));

    setInitialEmailDrafts((prev) => {

      if (!(targetId in prev)) {

        return prev;

      }

      const next = { ...prev };

      delete next[targetId];

      return next;

    });



    if (options.resetMessage) {

      setInitialEmailMessages((prev) => {

        if (!(targetId in prev)) {

          return prev;

        }

        const next = { ...prev };

        delete next[targetId];

        return next;

      });

    }

    

  };


  const handleInitialEmailDraftChange = (leadId, value) => {
    if (!leadId) {
      return;
    }

    setInitialEmailDrafts((prev) => ({
      ...prev,
      [leadId]: value,
    }));
  };

  const submitInitialEmail = async (lead) => {
    const leadId = lead?.id;

    if (!leadId) {
      setError('Cannot send the initial email without a lead identifier.');
      return;
    }

    const draft = initialEmailDrafts[leadId] ?? '';
    if (!draft.trim()) {
      setInitialEmailMessages((prev) => ({
        ...prev,
        [leadId]: { success: '', error: 'Please provide email content before sending.' },
      }));
      return;
    }

    setInitialEmailSubmittingId(leadId);
    setInitialEmailMessages((prev) => ({
      ...prev,
      [leadId]: { success: '', error: '' },
    }));

    try {
      await queueInitialEmailSend({ lead, initialEmail: draft });
      closeInitialEmailEditor(leadId);
    } catch (err) {
      console.error(err);
      setInitialEmailMessages((prev) => ({
        ...prev,
        [leadId]: { success: '', error: err?.message || 'Unable to send initial email.' },
      }));
    } finally {
      setInitialEmailSubmittingId(null);
      try {
        await fetchLeads();
      } catch (fetchErr) {
        console.error('Failed to reload leads after send', fetchErr);
      }
    }
  };

  const runAiBulkWithCredits = async () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow AI features.');
      return;
    }

    // Run unlimited AI lead-pull for all location × sector combinations
    await handleAiLeadPullLoop();
  };

  const runEnrichWithCredits = async () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow Enrich or AI features.');
      return;
    }

    const selectedCredits = Math.max(0, Number(enrichCreditsToUse) || 0);
    if (creditsRemaining < 2) {
      setError('Insufficient credits. At least 2 credits are required to run enrich.');
      return;
    }
    if (selectedCredits < 2) {
      setError('Enter at least 2 credits to run enrich.');
      return;
    }
    if (selectedCredits <= 0) {
      setError('Enter at least 1 credit to run enrich.');
      return;
    }
    if (selectedCredits > creditsRemaining) {
      setError(`Insufficient credits. You selected ${selectedCredits}, but only ${creditsRemaining} are available.`);
      return;
    }
    if (selectedCredits > enrichPreview.maxCreditsAllowedByQueueCap) {
      setError(
        `This queue is capped at ${enrichPreview.queueLeadCap} leads per enrich run. Reduce credits to ${enrichPreview.maxCreditsAllowedByQueueCap} or less.`
      );
      return;
    }

    if (!enrichTargetLeadIds.length) {
      setError('There are no unenriched leads in the current scope to process.');
      return;
    }

    const estimatedLeadVolume = enrichPreview.leadsToEnrich;
    if (estimatedLeadVolume > ENRICH_QUEUE_LEAD_CAP) {
      setError(`This queue is capped at ${ENRICH_QUEUE_LEAD_CAP} leads per enrich run.`);
      return;
    }

    setFilterLoading(true);
    setError('');
    try {
      await startAvenorQueueJob({
        endpoint: 'https://api.insightos.co.uk/leads/filter',
        method: 'POST',
        body: {
          limit: selectedCredits,
          targetLeadIds: enrichTargetLeadIds,
        },
        title: 'Enriching leads',
        returnState: {
          locationFilter: location.trim(),
          sectorFilter: sector.trim(),
        },
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error filtering leads');
    } finally {
      setFilterLoading(false);
    }
  };

  const runTargetedEnrich = async () => {
    if (!canUseAiAndEnrich) {
      setError('Your permission level does not allow enrichment.');
      return;
    }

    const quantity = Math.max(1, Math.floor(Number(targetLeadQuantity) || 0));
    const maximumCreditCost = quantity * 3;
    if (!selectedSicCodes.length) {
      setError('Select at least one SIC code.');
      return;
    }
    if (!aiLocations.length) {
      setError('Generate or add at least one location.');
      return;
    }
    if (maximumCreditCost > creditsRemaining) {
      setError(`Up to ${maximumCreditCost} credits are required for ${quantity} leads. You have ${creditsRemaining}.`);
      return;
    }

    setFilterLoading(true);
    setError('');
    try {
      await startAvenorQueueJob({
        endpoint: 'https://api.insightos.co.uk/leads/targeted-enrich',
        method: 'POST',
        body: {
          sicCodes: selectedSicCodes,
          locations: aiLocations,
          quantity,
        },
        title: 'Finding and enriching targeted leads',
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to start targeted enrichment.');
    } finally {
      setFilterLoading(false);
    }
  };

  const creditsRemaining = Math.max(0, Number(salesCredits) || 0);
  const enrichCapacityOnBudget = Math.floor(creditsRemaining / 3);
  const noCreditsRemaining = creditsRemaining <= 0;
  const selectedEnrichCredits = Math.max(0, Number(enrichCreditsToUse) || 0);
  const enrichCreditsInputCap = Math.min(creditsRemaining, enrichPreview.maxCreditsAllowedByQueueCap);
  const canRunEnrich =
    creditsRemaining >= 2 &&
    selectedEnrichCredits >= 2 &&
    selectedEnrichCredits <= creditsRemaining &&
    selectedEnrichCredits <= enrichPreview.maxCreditsAllowedByQueueCap &&
    enrichPreview.leadsToEnrich > 0;

  const openSmtpModal = async () => {
    setSmtpError('');
    setSmtpFeedback('');
    setSmtpModalOpen(true);
    await fetchSmtpSettings();
  };

  const openSettingsModal = async () => {
    setSettingsError('');
    setSettingsFeedback('');
    setPasswordError('');
    setPasswordFeedback('');
    setSettingsModalOpen(true);
    await fetchCompanyCredits();
  };

  const handleCompanyFieldChange = (field, value) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveCompanyProfile = async () => {
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsFeedback('');
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/editcompany', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(companyForm),
      });

      const payload = await parseJsonOrThrow(response, '/leads/editcompany');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Unable to update company profile');
      }

      setSettingsFeedback('Company details updated.');
      await fetchCompanyCredits();
    } catch (err) {
      console.error(err);
      setSettingsError(err.message || 'Unable to update company details.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const savePasswordChange = async () => {
    setPasswordError('');
    setPasswordFeedback('');

    if (!passwordOne || !passwordTwo) {
      setPasswordError('Please enter both password fields.');
      return;
    }

    if (passwordOne !== passwordTwo) {
      setPasswordError('Passwords must match.');
      return;
    }

    setPasswordSaving(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/passwordchange', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: passwordOne, confirmPassword: passwordTwo }),
      });

      const payload = await parseJsonOrThrow(response, '/leads/passwordchange');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Unable to change password');
      }

      setPasswordFeedback('Password changed successfully.');
      setPasswordOne('');
      setPasswordTwo('');
    } catch (err) {
      console.error(err);
      setPasswordError(err.message || 'Unable to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };





const saveSmtpSettings = async () => {
  // Validate each config
  const invalid = smtpConfigs.some(
    cfg => !cfg.host.trim() || !cfg.port.trim() || !cfg.username.trim() || !cfg.password.trim()
  );
  if (invalid) {
    setSmtpError('Please complete host, port, username, and password for every SMTP entry.');
    setSmtpFeedback('');
    return;
  }

  try {
    setSmtpSaving(true);
    // Remove the temporary `id` field before sending
    const payload = smtpConfigs.map(({ id, ...cfg }) => cfg);
    const response = await fetch('https://api.insightos.co.uk/leads/smptp', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload), // send array
    });
    const result = await parseJsonOrThrow(response, '/leads/smptp');
    if (!response.ok || !result?.success) {
      throw new Error(result?.message || 'Unable to save SMTP settings');
    }
    setSmtpFeedback('SMTP settings saved.');
    setSmtpError('');
    // Optionally re-check all or just the first one
  } catch (err) {
    console.error(err);
    setSmtpError(err.message || 'Unable to save SMTP data.');
    setSmtpFeedback('');
  } finally {
    setSmtpSaving(false);
  }
};

  const handleLinkedInCorrect = async (leadId, correctValue) => {
  try {
    setLinkedInUpdating(leadId);
    const response = await fetch(`https://api.insightos.co.uk/api/${leadId}/linkedin-verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ leadId, correct: correctValue })
    });
    const data = await response.json();
    if (data.success) {
      setLeads((prev) =>
        prev.map((l) => l.id === leadId ? { ...l, LinkedInCorrect: correctValue } : l)
      );
    }
  } catch (error) {
    console.error('Error verifying LinkedIn:', error);
  } finally {
    setLinkedInUpdating(null);
  }
};

const handleLinkedInUrl = async (leadId, newUrl) => {
  try {
    setLinkedInUpdating(leadId);
    const response = await fetch(`https://api.insightos.co.uk/api/leads/${leadId}/linkedin-url`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ url: newUrl })
    });
    const data = await response.json();
    if (data.success) {
      setLeads((prev) =>
        prev.map((l) => l.id === leadId ? { ...l, URL: newUrl } : l)
      );
    }
  } catch (error) {
    console.error('Error updating LinkedIn URL:', error);
  } finally {
    setLinkedInUpdating(null);
  }
};

const handleLinkedInFollow = async (leadId, status) => {
  try {
    setLinkedInUpdating(leadId);
    const response = await fetch(`https://api.insightos.co.uk/api/leads/${leadId}/linkedin-follow`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status })
    });
    const data = await response.json();
    if (data.success) {
      setLeads((prev) =>
        prev.map((l) => l.id === leadId ? { ...l, LinkedInStatus: status } : l)
      );
    }
  } catch (error) {
    console.error('Error updating follow status:', error);
  } finally {
    setLinkedInUpdating(null);
  }
};

// Parse OfficerURL JSON if it exists



// Handler for adding officer URL
const handleAddOfficerUrl = (leadId, leadName, url) => {
  if (!leadName || !url) return;
  const targetLead = leads.find((l) => l.id === leadId);
  const currentOfficers = targetLead?.OfficerURL ? JSON.parse(targetLead.OfficerURL) : [];
  const updatedOfficers = [...currentOfficers, { LeadName: leadName, URL: url }];
  handleUpdateOfficerUrl(leadId, JSON.stringify(updatedOfficers));
};

// Handler for removing officer URL
const handleRemoveOfficerUrl = (leadId, index) => {
  const targetLead = leads.find((l) => l.id === leadId);
  const currentOfficers = targetLead?.OfficerURL ? JSON.parse(targetLead.OfficerURL) : [];
  const updatedOfficers = currentOfficers.filter((_, i) => i !== index);
  handleUpdateOfficerUrl(leadId, JSON.stringify(updatedOfficers));
};

// Handler for updating OfficerURL
const handleUpdateOfficerUrl = async (leadId, officerUrlJson) => {
  try {
    const response = await fetch(`https://api.insightos.co.uk/api/leads/${leadId}/officer-url`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ officerUrl: officerUrlJson })
    });
    const data = await response.json();
    if (data.success) {
      setLeads((prev) =>
        prev.map((l) => l.id === leadId ? { ...l, OfficerURL: officerUrlJson } : l)
      );
    }
  } catch (error) {
    console.error('Error updating officer URLs:', error);
  }
};

const handleFormSent = async (leadId, sent) => {
  try {
    const response = await fetch(`https://api.insightos.co.uk/leads/${leadId}/form-sent`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ sent })
    });
    const data = await response.json();
    if (data.success) {
      setLeads((prev) =>
        prev.map((l) => l.id === leadId ? { ...l, FormSent: sent } : l)
      );
    }
  } catch (error) {
    console.error('Error updating form sent:', error);
  }
};

function FunnelIndicator({ position }) {
  const levels = FUNNEL_LEVELS;
  const colors = ['#ef4444', '#f97316', '#eab308', '#3b82f6', '#10b981'];
  const activeIndex = levels.indexOf(position);

  return (
    <div className="flex flex-col items-center gap-[1px]" title={position || 'Unaware'}>
      {levels.map((_, idx) => {
        const isActive = idx <= activeIndex;
        return (
          <div
            key={idx}
            className="rounded-[1px] transition-all duration-300"
            style={{
              width: `${(idx + 1) * 6 + 8}px`, // grows downward like a funnel
              height: '3px',
              backgroundColor: isActive ? colors[idx] : '#e5e7eb',
              boxShadow: isActive ? `0 0 4px ${colors[idx]}` : 'none',
            }}
          />
        );
      })}
    </div>
  );
}


  const navLinkClasses = ({ isActive }) => (
    `shrink-0 whitespace-nowrap rounded-lg px-2 py-1 text-xs font-semibold transition sm:px-3 sm:py-2 sm:text-sm ${isActive ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30' : 'text-slate-700 hover:bg-sky-100/80 hover:text-slate-900'}`
  );

  const glassRootClass = 'relative min-h-screen bg-cover bg-center bg-no-repeat px-2 pb-8 pt-3 sm:px-4';
  const glassRootStyle = {
    backgroundImage:
      "linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(233,213,255,0.82) 45%, rgba(255,255,255,0.92) 100%), url('/nologo.png')",
  };
  const glassShellClass = 'rounded-2xl border border-white/50 bg-white/55 shadow-[0_20px_70px_rgba(59,130,246,0.18)] backdrop-blur-xl';
  const glassSectionClass = 'border-b border-white/45 bg-white/45 backdrop-blur-xl';
  const glassCardClass = 'rounded-xl border border-white/50 bg-white/70 shadow-[0_10px_35px_rgba(76,29,149,0.14)] backdrop-blur';

  const scrollToSection = (sectionRef) => {
    if (!sectionRef?.current) return;
    sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };


  const shouldShowNotification = 
  leads.length === 0 && 
  !hasDismissedNotification && 
  !tableLoading && 
  !leadPullLoading && 
  !filterLoading && 
  !aiLeadPullRunning ; 
  // also show notification if .Responded is today 


  



const LeadsLoader = () => {
  // ----- Define the loading steps -----
  const steps = [
    { label: 'Loading Configuration', duration: 10, progressStart: 0, progressEnd: 20 },
    { label: 'Connecting to Data Source', duration: 5, progressStart: 20, progressEnd: 45 },
    { label: 'Populating Lead Table', duration: 5, progressStart: 45, progressEnd: 70 },
    { label: 'Updating Stats', duration: 4, progressStart: 70, progressEnd: 90 },
    { label: 'Finalizing', duration: 4, progressStart: 90, progressEnd: 100 },
  ];

  const totalDuration = steps.reduce((acc, s) => acc + s.duration, 0); // 20s

  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const interval = setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
    return () => clearInterval(interval);
  }, []);

  const elapsedSeconds = Math.min(elapsedMs / 1000, totalDuration);
  const progressPercent = Math.min(100, (elapsedSeconds / totalDuration) * 100);

  // Determine the current step based on elapsed time
  let cumulative = 0;
  let currentStep = steps[0];
  for (const step of steps) {
    cumulative += step.duration;
    if (elapsedSeconds <= cumulative) {
      currentStep = step;
      break;
    }
  }

  // Calculate progress within the current step (for the bar animation)
  const stepStart = currentStep.progressStart;
  const stepEnd = currentStep.progressEnd;
  const stepProgress = Math.min(
    100,
    stepStart + ((elapsedSeconds - (cumulative - currentStep.duration)) / currentStep.duration) * (stepEnd - stepStart)
  );

  // Colour changes per step – just a nice touch
  const barGradients = [
    'from-indigo-500 via-purple-500 to-pink-500',
    'from-blue-500 via-cyan-500 to-teal-500',
    'from-emerald-500 via-green-500 to-lime-500',
    'from-amber-500 via-orange-500 to-red-500',
    'from-rose-500 via-fuchsia-500 to-indigo-500',
  ];
  const stepIndex = steps.indexOf(currentStep);
  const gradientClass = barGradients[stepIndex % barGradients.length];

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center rounded-2xl bg-cover bg-center px-4"
      style={{
        backgroundImage: 'url("/nologo.png")', // replace with your image
      }}
    >
      {/* Dark overlay for readability (optional) */}
      <div className="absolute inset-0 bg-black/40 rounded-2xl" />

      {/* Dark navy container */}
      <div className="relative z-10 flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-navy-900/90 px-6 py-8 shadow-2xl backdrop-blur-sm sm:px-8 sm:py-10">
        {/* Logo with glow */}
        <div className="relative flex items-center justify-center">
          <motion.div
            className="absolute h-28 w-28 rounded-full bg-indigo-400/25 blur-2xl sm:h-32 sm:w-32"
            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.9, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <img
            src="/avenor.png"
            alt="Avenor"
            className="relative h-20 w-20 drop-shadow-[0_0_16px_rgba(99,102,241,0.7)] sm:h-24 sm:w-24"
          />
        </div>

        {/* Step indicators (dots) */}
        <div className="flex gap-2">
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`h-2 w-2 rounded-full transition-all duration-300 ${
                idx === stepIndex
                  ? 'w-6 bg-indigo-400'
                  : idx < stepIndex
                  ? 'bg-indigo-200/50'
                  : 'bg-white/20'
              }`}
            />
          ))}
        </div>

        {/* Label and progress */}
        <div className="w-full">
          <div className="mb-2 flex items-center justify-between text-base font-semibold text-white sm:text-lg">
            <span>
              <span className="text-indigo-300">Step {stepIndex + 1}</span>{' '}
              <span className="text-slate-200">·</span>{' '}
              <span className="text-slate-100">{currentStep.label}</span>
            </span>
            <span className="text-slate-300">{Math.round(progressPercent)}%</span>
          </div>

          <div className="h-3 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div
              className={`h-full rounded-full bg-gradient-to-r ${gradientClass}`}
              animate={{ width: `${stepProgress}%` }}
              transition={{ duration: 0.3, ease: 'linear' }}
            />
          </div>

          {/* Small descriptive subtitle (optional) */}
          <p className="mt-2 text-center text-xs text-slate-400">
            {currentStep.label === 'Loading Configuration' && 'Preparing environment…'}
            {currentStep.label === 'Connecting to Data Source' && 'Establishing secure connection…'}
            {currentStep.label === 'Populating Lead Table' && 'Fetching and caching records…'}
            {currentStep.label === 'Updating Stats' && 'Aggregating metrics…'}
            {currentStep.label === 'Finalizing' && 'Almost done…'}
          </p>
        </div>
      </div>
    </div>
  );
};


  const handleAiRewrite = async (leadId) => {
    if (!leadId) {
      setError('Cannot rewrite the initial email without a lead identifier.');
      return;
    }

    try {
      const response = await fetch(`https://api.insightos.co.uk/leads/ai-rewrite/${leadId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ initialEmail: initialEmailDrafts[leadId] ?? '' }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to rewrite the email.');
      }
      setInitialEmailDrafts((prev) => ({
        ...prev,
        [leadId]: data.rewrittenEmail || prev[leadId],
      }));

      // Handle the successful response here, e.g., update the UI or state
    } catch (error) {
      setError(error.message);
    }
  };


  const filterErrorLeads = useMemo(() => {
  const scope = bulkApplyFilteredOnly ? filteredLeads : leads;
  return scope.filter((lead) => lead?.FilterError != null && String(lead.FilterError).trim() !== '');
}, [leads, filteredLeads, bulkApplyFilteredOnly]);

const handleBulkDeleteBySectorLocation = async () => {
  const locationQuery = location.trim().toLowerCase();
  const sectorQuery = sector.trim().toLowerCase();

  if (!locationQuery && !sectorQuery) {
    setBulkDeleteError('Select a sector or location filter before using this delete action.');
    return;
  }

  const scope = bulkApplyFilteredOnly ? filteredLeads : leads;
  const targetLeads = scope.filter((lead) => {
    if (!lead?.id) return false;

    const leadLocation = String(lead.Location ?? '').toLowerCase();
    const leadSector = String(lead.Sector ?? '').toLowerCase();
    const matchesLocation = !locationQuery || leadLocation.includes(locationQuery);
    const matchesSector = !sectorQuery || leadSector.includes(sectorQuery);

    return matchesLocation && matchesSector;
  });

  if (targetLeads.length === 0) {
    setBulkDeleteError('No leads found for the selected sector/location filter.');
    return;
  }

  const confirmed = window.confirm(
    `Delete ${targetLeads.length} lead(s) matching sector "${sector || 'Any'}" and location "${location || 'Any'}"? This cannot be undone.`
  );
  if (!confirmed) return;

  setBulkDeleteRunning(true);
  setBulkDeleteFeedback('');
  setBulkDeleteError('');
  let deleted = 0;
  let failed = 0;

  try {
    for (const lead of targetLeads) {
      try {
        const response = await fetch(`https://api.insightos.co.uk/leads/leads/${lead.id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Delete failed');
        deleted += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkDeleteFeedback(`Deleted ${deleted} lead(s) by sector/location. Failed: ${failed}.`);
    await fetchLeads();
  } catch (err) {
    setBulkDeleteError('Bulk delete by sector/location failed.');
  } finally {
    setBulkDeleteRunning(false);
  }
};

const handleCancelSchedule = async (leadId) => {
  if (!leadId) return;
  setCancellingScheduleId(leadId);
  try {
    const response = await fetch(`https://api.insightos.co.uk/leads/cancel/${leadId}/schedule`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      const payload = await parseJsonLenient(response, `/leads/cancel/${leadId}/schedule`);
      throw new Error(payload?.message || 'Failed to cancel schedule');
    }
    setLeads((prev) =>
      prev.map((l) => l.id === leadId ? { ...l, InitalSchedule: null } : l)
    );
  } catch (err) {
    console.error(err);
    setError(err.message || 'Unable to cancel schedule.');
  } finally {
    setCancellingScheduleId(null);
  }
};

const clearNotification = async () => {
  try {
    await fetch('https://api.insightos.co.uk/leads/clearnotification', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    setCompanyNotification(null);
  } catch (err) {
    console.error('Failed to clear notification', err);
  }
};

const handleDeleteFilterErrors = async () => {
  if (filterErrorLeads.length === 0) {
    setBulkDeleteError('No leads with filter errors found.');
    return;
  }

  const confirmed = window.confirm(
    `Delete ${filterErrorLeads.length} lead(s) with filter errors? This cannot be undone.`
  );
  if (!confirmed) return;

  setBulkDeleteRunning(true);
  setBulkDeleteFeedback('');
  setBulkDeleteError('');
  let deleted = 0;
  let failed = 0;

  try {
    for (const lead of filterErrorLeads) {
      try {
        const response = await fetch(`https://api.insightos.co.uk/leads/leads/${lead.id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Delete failed');
        deleted += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkDeleteFeedback(`Deleted ${deleted} lead(s) with filter errors. Failed: ${failed}.`);
    await fetchLeads();
  } catch (err) {
    setBulkDeleteError('Delete filter errors failed.');
  } finally {
    setBulkDeleteRunning(false);
  }
};

const isLeadsTableActive = !showLeadActions;

const sidebarSectionNav = [
  {
    key: 'leads-table',
    label: 'Leads Table',
    color: 'bg-indigo-600',
    icon: TableCellsIcon,
    onClick: () => { setShowLeadActions(false); setSidebarOpen(false); },
    active: isLeadsTableActive,
  },
  {
    key: 'lead-actions',
    label: 'Enrichment & Lead Pull',
    color: 'bg-emerald-600',
    icon: AdjustmentsHorizontalIcon,
    onClick: () => { setShowLeadActions(true); setSidebarOpen(false); },
    active: showLeadActions,
  },
  {
    key: 'icp-profile',
    label: 'ICP Profile',
    color: 'bg-cyan-600',
    icon: SparklesIcon,
    onClick: () => { setIcpDrawerOpen(true); setSidebarOpen(false); },
    active: icpDrawerOpen,
  },
];

const sidebarBulkActionNav = [
 
  {
    key: 'bulk-send-sector-location',
    label: 'Send by Sector/Location',
    icon: PaperAirplaneIcon,
    color: 'bg-emerald-600',
    onClick: () => { openGranularSendModal(); setSidebarOpen(false); },
    active: false,
  },
  {
    key: 'bulk-follow-ups',
    label: `Follow Ups (${followUpEligibleLeads.length})`,
    icon: EnvelopeOpenIcon,
    color: 'bg-cyan-600',
    onClick: () => { openFollowUpModal(); setSidebarOpen(false); },
    active: false,
  },
  {
    key: 'bulk-delete-leads',
    label: bulkDeleteRunning ? 'Deleting...' : 'Delete Leads',
    icon: TrashIcon,
    color: 'bg-red-600',
    onClick: () => { handleBulkDeleteLeads(); setSidebarOpen(false); },
    active: false,
  },
  {
    key: 'bulk-delete-sector-location',
    label: bulkDeleteRunning ? 'Deleting...' : 'Delete by Sector/Location',
    icon: MapPinIcon,
    color: 'bg-red-600',
    onClick: () => { handleBulkDeleteBySectorLocation(); setSidebarOpen(false); },
    active: false,
  },
  {
    key: 'bulk-delete-filter-errors',
    label: bulkDeleteRunning ? 'Deleting...' : `Delete Filter Errors (${filterErrorLeads.length})`,
    icon: ExclamationTriangleIcon,
    color: 'bg-red-600',
    onClick: () => { handleDeleteFilterErrors(); setSidebarOpen(false); },
    active: false,
  },
];

const sidebarRouteNav = [
  // Adverts and Gallery intentionally disabled here per design request
  { key: 'billing', label: 'Billing', icon: CreditCardIcon, color: 'bg-yellow-600', to: '/billing' },
  { key: 'docs', label: 'Docs', icon: DocumentTextIcon, color: 'bg-blue-600', to: '/leads-docs' },
].filter(Boolean);

const sidebarAccountNav = [
 
  {
    key: 'buy',
    label: 'Buy Credits',
    icon: BanknotesIcon,
    onClick: () => setBuyModalOpen(true),
  },
  {
    key: 'smtp',
    label: `SMTP (${smtpConfigs.length})`,
    icon: ServerStackIcon,
    onClick: () => openSmtpModal(),
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: Cog6ToothIcon,
    onClick: () => navigate('/settings'),
  },
  {
    key: 'logout',
    label: 'Logout',
    icon: PowerIcon,
    color: 'bg-red-500',
    to: '/logout',
  },
];

// Icon-only text-color companions for each nav item's bg-* color, kept as literal strings so Tailwind emits them
const SIDEBAR_ICON_TEXT_CLASS = {
  'bg-indigo-600': 'text-indigo-600',
  'bg-emerald-600': 'text-emerald-600',
  'bg-red-600': 'text-red-600',
  'bg-purple-600': 'text-purple-600',
  'bg-pink-600': 'text-pink-600',
  'bg-yellow-600': 'text-yellow-600',
  'bg-blue-600': 'text-blue-600',
  'bg-cyan-600': 'text-cyan-600',
};

const SidebarNavItem = ({ item, mobile }) => {
  const Icon = item.icon;
  const activeColor = item.color || 'bg-indigo-600';
  const iconTextColor = SIDEBAR_ICON_TEXT_CLASS[activeColor] || 'text-indigo-600';
  const itemRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const baseClass = classNames(
    'relative flex items-center rounded-lg transition',
    mobile ? 'w-full gap-3 px-3 py-2' : 'h-11 w-11 justify-center',
    item.active
      ? classNames(activeColor, 'text-white shadow-md shadow-black/30')
      : classNames(iconTextColor, 'hover:bg-slate-100')
  );
  // Desktop-only tooltip: rendered via portal so it can't be clipped by the scrollable icon rail
  const showTooltip = () => {
    if (mobile || !itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 12 });
  };
  const hideTooltip = () => setTooltipPos(null);
  const tooltipPortal = !mobile && tooltipPos
    ? createPortal(
        <span
          style={{ position: 'fixed', top: tooltipPos.top, left: tooltipPos.left, transform: 'translateY(-50%)' }}
          className="pointer-events-none z-[999] whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white shadow-lg"
        >
          {item.label}
        </span>,
        document.body
      )
    : null;
  const inner = (
    <>
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {mobile && <span className="text-sm font-semibold">{item.label}</span>}
    </>
  );
  if (item.to) {
    return (
      <>
        <NavLink
          ref={itemRef}
          to={item.to}
          end={item.end}
          onClick={() => setSidebarOpen(false)}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
          className={({ isActive }) => classNames(baseClass, isActive && classNames(activeColor, 'text-white shadow-md shadow-black/30'))}
        >
          {inner}
        </NavLink>
        {tooltipPortal}
      </>
    );
  }
  return (
    <>
      <button
        ref={itemRef}
        type="button"
        onClick={item.onClick}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        className={baseClass}
      >
        {inner}
      </button>
      {tooltipPortal}
    </>
  );
};

  return (
    <>
      {/* Mobile sidebar (slide-out, icons + text) */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-slate-900/70 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative flex w-full max-w-xs flex-1 transform flex-col bg-white transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute right-0 top-0 -mr-12 flex pt-4 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex h-16 shrink-0 items-center gap-2 border-b border-slate-100 px-4">
              <img src="/avenor.png" alt="InsightOS Avenor Logo" className="h-8 w-8" />
              <span className="text-sm font-bold text-slate-900">Avenor</span>
              {showCompanyNameInMenu && (
                <span className="rounded-md border border-indigo-200/80 bg-indigo-100/80 px-2 py-0.5 text-[11px] font-semibold text-indigo-900">
                  {companyNameForMenu}
                </span>
              )}
            </div>
            <nav className="flex flex-1 flex-col gap-y-6 overflow-y-auto px-3 py-4">
              <ul className="flex flex-col gap-y-1">
                {sidebarAccountNav.map(item => (
                  <li key={item.key}><SidebarNavItem item={item} mobile /></li>
                ))}
              </ul>
              <ul className="flex flex-col gap-y-1 border-t border-slate-100 pt-4">
                {sidebarSectionNav.map(item => (
                  <li key={item.key}><SidebarNavItem item={item} mobile /></li>
                ))}
              </ul>
              <ul className="flex flex-col gap-y-1 border-t border-slate-100 pt-4">
                <li className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Bulk Actions</li>
                {sidebarBulkActionNav.map(item => (
                  <li key={item.key}><SidebarNavItem item={item} mobile /></li>
                ))}
              </ul>
              <ul className="flex flex-col gap-y-1 border-t border-slate-100 pt-4">
                {sidebarRouteNav.map(item => (
                  <li key={item.key}><SidebarNavItem item={item} mobile /></li>
                ))}
              </ul>
            </nav>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop icon-rail sidebar with hover labels */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-20 lg:flex-col">
        <div className="flex grow flex-col items-center gap-y-4 border-r border-slate-200 bg-white py-4 overflow-hidden">
          <img src="/avenor.png" alt="InsightOS Avenor Logo" className="h-8 w-8 shrink-0" />
          <nav className="flex flex-1 flex-col items-center gap-y-6 overflow-y-auto w-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <ul className="flex flex-col items-center gap-y-1">
              {sidebarAccountNav.map(item => (
                <li key={item.key}><SidebarNavItem item={item} /></li>
              ))}
            </ul>
            <ul className="flex flex-col items-center gap-y-1 border-t border-slate-100 pt-4">
              {sidebarSectionNav.map(item => (
                <li key={item.key}><SidebarNavItem item={item} /></li>
              ))}
            </ul>
            <ul className="flex flex-col items-center gap-y-1 border-t border-slate-100 pt-4">
              {sidebarBulkActionNav.map(item => (
                <li key={item.key}><SidebarNavItem item={item} /></li>
              ))}
            </ul>
            <ul className="flex flex-col items-center gap-y-1 border-t border-slate-100 pb-2 pt-4">
              {sidebarRouteNav.map(item => (
                <li key={item.key}><SidebarNavItem item={item} /></li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

  <div className={classNames(glassRootClass, 'lg:pl-20')} style={glassRootStyle}>

    {/* Mobile top bar */}
    <div className="mb-3 flex items-center gap-3 rounded-2xl border border-white/50 bg-white/65 px-3 py-2.5 shadow-sm backdrop-blur-xl lg:hidden">
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="rounded-lg border border-slate-200 bg-white p-2 text-slate-700 shadow-sm"
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-5 w-5" aria-hidden="true" />
      </button>
      <img src="/avenor.png" alt="InsightOS Avenor Logo" className="h-7 w-7" />
      <h1 className="text-sm font-bold text-slate-900">Avenor</h1>
      {showCompanyNameInMenu && (
        <span className="ml-auto rounded-md border border-indigo-200/80 bg-indigo-100/80 px-2 py-0.5 text-[11px] font-semibold text-indigo-900">
          {companyNameForMenu}
        </span>
      )}
    </div>

    {/* Filters — fixed flush to the bottom on both mobile and desktop */}
   <div className="fixed inset-x-0 bottom-0 z-30 rounded-none border-x-0 border-b-0 border-t border-white/50 bg-white/65 shadow-[0_-8px_25px_rgba(2,132,199,0.2)] backdrop-blur-xl lg:left-20">
  <div className="flex items-center gap-3 sm:gap-1.5 px-4 py-3 sm:px-3 sm:py-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:overflow-visible">
    {/* Count badge */}
    <span className="shrink-0 rounded-full bg-indigo-50 text-indigo-600 text-xs sm:text-[11px] font-semibold px-2.5 py-1 sm:px-2 sm:py-0.5" title="Filtered leads count">
      {filteredLeads.length}
    </span>

    {/* Credits */}
    <button
      type="button"
      onClick={() => setBuyModalOpen(true)}
      className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 sm:px-2 sm:py-0.5 text-xs sm:text-[11px] font-semibold text-amber-700 hover:bg-amber-100"
      title="Credits remaining · click to buy more"
    >
      Credits: {creditsLoading ? '...' : creditsRemaining}
    </button>

    {/* Used */}
    <button
      type="button"
      onClick={() => setBuyModalOpen(true)}
      className="shrink-0 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 sm:px-2 sm:py-0.5 text-xs sm:text-[11px] font-semibold text-sky-700 hover:bg-sky-100"
      title="Credits used · click to buy more"
    >
      Used: {creditsLoading ? '...' : creditsUsed}
    </button>

    {/* Email Campaign */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">Email Campaign</span>
      <button
        type="button"
        onClick={() => setShowEmailCampaignOnly(p => !p)}
        className={`flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border transition ${showEmailCampaignOnly ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
        <EnvelopeIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
      </button>
      <span className="pointer-events-none absolute -top-1 -right-1 rounded-full bg-emerald-700 px-1.5 sm:px-1 text-[9px] sm:text-[8px] font-bold leading-tight text-white">{quickFilterCounts.emailCampaign}</span>
    </div>

    {/* Unfiltered */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">Unfiltered</span>
      <button
        type="button"
        onClick={() => setShowUnfilteredOnly(p => !p)}
        className={`flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border transition ${showUnfilteredOnly ? 'bg-slate-600 text-white border-slate-600' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>
        <FunnelIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
      </button>
      <span className="pointer-events-none absolute -top-1 -right-1 rounded-full bg-slate-700 px-1.5 sm:px-1 text-[9px] sm:text-[8px] font-bold leading-tight text-white">{quickFilterCounts.unfiltered}</span>
    </div>

    {/* Scheduled */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">Scheduled</span>
      <button
        type="button"
        onClick={() => setShowScheduledOnly(p => !p)}
        className={`flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border transition ${showScheduledOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
        <ClockIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
      </button>
      <span className="pointer-events-none absolute -top-1 -right-1 rounded-full bg-amber-700 px-1.5 sm:px-1 text-[9px] sm:text-[8px] font-bold leading-tight text-white">{quickFilterCounts.scheduled}</span>
    </div>

    {/* Contacted */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">Contacted</span>
      <button
        type="button"
        onClick={() => setShowContactedOnly(p => !p)}
        className={`flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border transition ${showContactedOnly ? 'bg-yellow-600 text-white border-purple-600' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
        <StarIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
      </button>
      <span className="pointer-events-none absolute -top-1 -right-1 rounded-full bg-purple-700 px-1.5 sm:px-1 text-[9px] sm:text-[8px] font-bold leading-tight text-white">{quickFilterCounts.contacted}</span>
    </div>

    {/* Unsent */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">Unsent</span>
      <button
        type="button"
        onClick={() => setShowUnsentEnrichedOnly(p => !p)}
        className={`flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border transition ${showUnsentEnrichedOnly ? 'bg-rose-600 text-white border-rose-600' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
        <PaperAirplaneIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
      </button>
      <span className="pointer-events-none absolute -top-1 -right-1 rounded-full bg-rose-700 px-1.5 sm:px-1 text-[9px] sm:text-[8px] font-bold leading-tight text-white">{quickFilterCounts.unsent}</span>
    </div>

    {/* LinkedIn Only */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">LinkedIn Only</span>
      <button
        type="button"
        onClick={() => setShowLinkedInOnly(p => !p)}
        className={`flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border transition ${showLinkedInOnly ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </button>
      <span className="pointer-events-none absolute -top-1 -right-1 rounded-full bg-blue-700 px-1.5 sm:px-1 text-[9px] sm:text-[8px] font-bold leading-tight text-white">{quickFilterCounts.linkedInOnly}</span>
    </div>

    {/* LinkedIn Status Dropdown */}
    {showLinkedInOnly && (
      <select
        value={linkedInStatusFilter}
        onChange={(e) => setLinkedInStatusFilter(e.target.value)}
        className="shrink-0 rounded-full border border-blue-300 bg-white px-3 py-2 sm:px-2 sm:py-1 text-xs sm:text-[11px] font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        title="Filter by LinkedIn status"
      >
        <option value="">All Statuses</option>
        <option value="Not Followed">Not Followed</option>
        <option value="Following">Following</option>
        <option value="Requested">Requested</option>
        <option value="Connected">Connected</option>
        <option value="Message Sent">Message Sent</option>
      </select>
    )}

    {/* LI Sent */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">LI Sent</span>
      <button
        type="button"
        onClick={() => setShowLinkedInMessageSentOnly(p => !p)}
        className={`flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border transition ${showLinkedInMessageSentOnly ? 'bg-blue-700 text-white border-blue-700' : 'bg-blue-50 text-blue-800 border-blue-300'}`}>
        <svg className="h-4 w-4 sm:h-3.5 sm:w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </button>
      <span className="pointer-events-none absolute -top-1 -right-1 rounded-full bg-blue-900 px-1.5 sm:px-1 text-[9px] sm:text-[8px] font-bold leading-tight text-white">{quickFilterCounts.liSent}</span>
    </div>

    {/* Updated on date */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">Updated on date</span>
      <div className="relative">
        <CalendarDaysIcon className="absolute left-2.5 sm:left-2 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-3.5 sm:w-3.5 text-slate-400 pointer-events-none" aria-hidden="true" />
        <input
          type="date"
          title="Show leads updated on this date"
          value={updatedAtFilterDate}
          onChange={(e) => setUpdatedAtFilterDate(e.target.value)}
          className={`shrink-0 rounded-full border pl-7 sm:pl-6 pr-3 sm:pr-2 py-2 sm:py-1 text-xs sm:text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-400 ${updatedAtFilterDate ? 'border-indigo-400 bg-indigo-50 text-indigo-800' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
        />
      </div>
    </div>

    {/* Location */}
    <div className="relative shrink-0 w-36 sm:w-32">
      <MapPinIcon className="absolute left-2.5 sm:left-2 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-3.5 sm:w-3.5 text-slate-400 pointer-events-none" aria-hidden="true" />
      <select
        title="Filter by location"
        value={locationSelectValue}
        onChange={e => setLocation(e.target.value)}
        className="w-full pl-7 sm:pl-6 pr-2 py-2 sm:py-1 text-xs sm:text-[11px] rounded-full border border-slate-200 bg-slate-50 text-slate-700 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <option value="">All locations</option>
        {locationOptionsForActiveFilters.map(loc => (
          <option key={`sb-loc-${loc}`} value={loc}>{loc}</option>
        ))}
      </select>
    </div>

    {/* Sector */}
    <div className="relative shrink-0 w-36 sm:w-32">
      <TagIcon className="absolute left-2.5 sm:left-2 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-3.5 sm:w-3.5 text-slate-400 pointer-events-none" aria-hidden="true" />
      <select
        title="Filter by sector"
        value={sectorSelectValue}
        onChange={e => setSector(e.target.value)}
        className="w-full pl-7 sm:pl-6 pr-2 py-2 sm:py-1 text-xs sm:text-[11px] rounded-full border border-slate-200 bg-slate-50 text-slate-700 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <option value="">All sectors</option>
        {sectorOptionsForActiveFilters.map(sec => (
          <option key={`sb-sec-${sec}`} value={sec}>{sec}</option>
        ))}
      </select>
    </div>

    {/* Follow-up stage */}
    <div className="relative shrink-0 w-36 sm:w-32">
      <StarIcon className="absolute left-2.5 sm:left-2 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-3.5 sm:w-3.5 text-purple-800 pointer-events-none" aria-hidden="true" />
      <select
        title="Filter by follow-up stage"
        value={followUpStageFilter}
        onChange={e => setFollowUpStageFilter(e.target.value)}
        className="w-full pl-7 sm:pl-6 pr-2 py-2 sm:py-1 text-xs sm:text-[11px] rounded-full border border-purple-600 bg-purple-50 text-purple-700 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <option value="">All stages</option>
        <option value="1">Stage 1 · Initial sent</option>
        <option value="2">Stage 2 · Follow-up 1</option>
        <option value="3">Stage 3 · Follow-up 2</option>
        <option value="4">Stage 4 · Follow-up 3</option>
      </select>
    </div>

    {/* Funnel stage */}
    <div className="relative shrink-0 w-36 sm:w-32">
      <FunnelIcon className="absolute left-2.5 sm:left-2 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-3.5 sm:w-3.5 text-emerald-700 pointer-events-none" aria-hidden="true" />
      <select
        title="Filter by funnel stage (shows only leads exactly at this stage)"
        value={funnelStageFilter}
        onChange={e => setFunnelStageFilter(e.target.value)}
        className="w-full pl-7 sm:pl-6 pr-2 py-2 sm:py-1 text-xs sm:text-[11px] rounded-full border border-emerald-600 bg-emerald-50 text-emerald-700 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <option value="">All funnel stages</option>
        {FUNNEL_LEVELS.map((level) => (
          <option key={`funnel-${level}`} value={level}>{level}</option>
        ))}
      </select>
    </div>

    {/* Clear search button */}
    <div className="group relative shrink-0">
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">Clear search</span>
      <button
        type="button"
        onClick={() => setGlobalLeadSearch('')}
        aria-label="Clear search"
        className="flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 transition"
      >
        <XMarkIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
      </button>
    </div>

    {/* Search input */}
    <div className="relative shrink-0 w-36 sm:w-32">
      <MagnifyingGlassIcon className="absolute left-2.5 sm:left-2 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-3.5 sm:w-3.5 text-slate-400 pointer-events-none" aria-hidden="true" />
      <input
        type="text"
        title="Search leads"
        value={globalLeadSearch}
        onChange={(e) => setGlobalLeadSearch(e.target.value)}
        placeholder="Search leads..."
        className="w-full pl-7 sm:pl-6 pr-2 py-2 sm:py-1 text-xs sm:text-[11px] rounded-full border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      />
    </div>

    {/* Clear All */}
    <button
      type="button"
      title="Clear all filters"
      onClick={() => { setLocation(''); setSector(''); setShowContactedOnly(false); setContactedFromDate(''); setContactedToDate(''); setGlobalLeadSearch(''); setShowUnsentEnrichedOnly(false); setShowScheduledOnly(false); setShowLinkedInOnly(false); setLinkedInStatusFilter(''); setShowEmailCampaignOnly(false); setShowUnfilteredOnly(false); setShowLinkedInMessageSentOnly(false); setUpdatedAtFilterDate(''); setFollowUpStageFilter(''); setFunnelStageFilter(''); }}
      className="ml-auto shrink-0 rounded-full border border-slate-300 bg-white px-4 py-2 sm:px-3 sm:py-1 text-xs sm:text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
    >
      Clear All
    </button>
  </div>
</div>

    <div ref={filterSentinelRef} className="h-0 overflow-hidden" />

     
      {/* Lead Actions panel — inline in normal page flow (no full-page overlay) */}
      {showLeadActions && (
        <section className="mb-4 overflow-hidden rounded-2xl border border-white/10 bg-[#0b0e14] text-white shadow-2xl">
          <header className="flex flex-col gap-3 border-b border-white/10 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div>
              <p className="text-xs font-semibold uppercase text-cyan-300">Targeted lead enrichment</p>
              <h2 className="mt-1 text-xl font-bold">Choose companies by SIC code and location</h2>
            </div>
            <button
              type="button"
              onClick={() => setShowLeadActions(false)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-white/80 sm:w-auto"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              Close
            </button>
          </header>

          <div className="space-y-6 p-4 sm:p-6">
            {isPermissionRestricted && (
              <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-sm text-amber-200">
                AI and enrichment features are disabled for your permission level.
              </div>
            )}

            <div className="grid gap-5 lg:grid-cols-2">
              <div className="min-w-0 rounded-xl border border-indigo-400/20 bg-indigo-400/5 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase text-indigo-300">1. Select SIC codes</p>
                    <p className="mt-1 text-xs text-white/50">Select an industry section, then choose specific activities.</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-indigo-400/15 px-2.5 py-1 text-xs font-bold text-indigo-200">
                    {selectedSicCodes.length} selected
                  </span>
                </div>

                <label className="block text-xs font-semibold text-white/70" htmlFor="sic-category">Industry section</label>
                <select
                  id="sic-category"
                  value={selectedSicCategory}
                  onChange={(event) => setSelectedSicCategory(event.target.value)}
                  className="mt-2 w-full rounded-lg border border-white/15 bg-[#141924] px-3 py-3 text-sm text-white focus:border-indigo-400 focus:outline-none"
                >
                  {Object.keys(sicCodeGroups).map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>

                <div className="mt-3 max-h-80 space-y-2 overflow-y-auto overscroll-contain pr-1">
                  {(sicCodeGroups[selectedSicCategory] || []).map(({ code, description }) => {
                    const checked = selectedSicCodes.includes(code);
                    return (
                      <label
                        key={code}
                        className={classNames(
                          'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition',
                          checked ? 'border-indigo-400 bg-indigo-400/15' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05]'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSicCode(code)}
                          className="mt-0.5 h-5 w-5 shrink-0 rounded border-white/20 bg-transparent text-indigo-500 focus:ring-indigo-400"
                        />
                        <span className="min-w-0">
                          <span className="block font-mono text-xs font-bold text-indigo-200">{code}</span>
                          <span className="mt-0.5 block break-words text-sm leading-5 text-white/75">{description}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {selectedSicCodes.length > 0 && (
                  <button type="button" onClick={() => setSelectedSicCodes([])} className="mt-3 text-xs font-semibold text-rose-300 hover:text-rose-200">
                    Clear all SIC codes
                  </button>
                )}
              </div>

              <div className="min-w-0 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-4">
                <p className="text-xs font-bold uppercase text-cyan-300">2. Choose locations</p>
                <p className="mt-1 text-xs text-white/50">Ask AI for UK towns or areas, then remove any you do not need.</p>

                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={aiLocationPrompt}
                    onChange={(event) => setAiLocationPrompt(event.target.value)}
                    placeholder="e.g. towns across Kent and Sussex"
                    className="min-w-0 flex-1 rounded-lg border border-white/15 bg-[#141924] px-3 py-3 text-sm text-white placeholder:text-white/30 focus:border-cyan-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleAiLocationsRequest}
                    disabled={aiLocationsLoading || actionsDisabled || !canUseAiAndEnrich}
                    className="w-full rounded-lg bg-gradient-to-r from-cyan-400 to-blue-500 px-4 py-3 text-sm font-bold text-[#071019] disabled:opacity-50 sm:w-auto"
                  >
                    {aiLocationsLoading ? 'Generating...' : 'Generate locations'}
                  </button>
                </div>

                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={manualTargetLocation}
                    onChange={(event) => setManualTargetLocation(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addManualTargetLocation();
                      }
                    }}
                    placeholder="Add a location manually"
                    className="min-w-0 flex-1 rounded-lg border border-white/15 bg-[#141924] px-3 py-3 text-sm text-white placeholder:text-white/30 focus:border-cyan-400 focus:outline-none"
                  />
                  <button type="button" onClick={addManualTargetLocation} className="w-full rounded-lg border border-cyan-300/30 px-4 py-3 text-sm font-bold text-cyan-200 sm:w-auto">
                    Add location
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {aiLocations.map((targetLocation) => (
                    <span key={targetLocation} className="inline-flex max-w-full items-center gap-2 rounded-full bg-cyan-400/15 px-3 py-1.5 text-sm text-cyan-100">
                      <span className="truncate">{targetLocation}</span>
                      <button type="button" onClick={() => removeAiLocation(targetLocation)} aria-label={`Remove ${targetLocation}`} className="shrink-0 text-cyan-200/60 hover:text-rose-300">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                  {!aiLocations.length && <p className="text-sm text-white/35">No locations selected yet.</p>}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-emerald-400/25 bg-emerald-400/5 p-4 sm:p-5">
              <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                <div>
                  <label htmlFor="target-lead-quantity" className="text-xs font-bold uppercase text-emerald-300">3. Leads to enrich</label>
                  <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <input
                      id="target-lead-quantity"
                      type="number"
                      min="1"
                      max={Math.floor(creditsRemaining / 3)}
                      value={targetLeadQuantity}
                      onChange={(event) => setTargetLeadQuantity(event.target.value)}
                      className="w-full rounded-lg border border-white/15 bg-[#141924] px-3 py-3 text-base font-bold text-white focus:border-emerald-400 focus:outline-none sm:w-28"
                    />
                    <p className="text-sm text-white/55">
                      Maximum cost: <strong className="text-white">{Math.max(0, Number(targetLeadQuantity) || 0) * 3} credits</strong>. Available: {creditsRemaining}.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={runTargetedEnrich}
                  disabled={actionsDisabled || !canUseAiAndEnrich || !selectedSicCodes.length || !aiLocations.length || Number(targetLeadQuantity) < 1 || Number(targetLeadQuantity) * 3 > creditsRemaining}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 px-5 py-3 text-sm font-extrabold text-[#071019] shadow-lg disabled:cursor-not-allowed disabled:opacity-40 md:w-auto"
                >
                  <SparklesIcon className="h-5 w-5" aria-hidden="true" />
                  {filterLoading ? 'Finding and enriching...' : `Enrich ${Math.max(0, Number(targetLeadQuantity) || 0)} leads`}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">{error}</div>
            )}
          </div>
        </section>
      )}
      {false && (
  <div
    className="lp-panel mb-4 overflow-hidden rounded-3xl border"
    style={{
      borderColor: 'rgba(255,255,255,0.08)',
      background: 'radial-gradient(120% 140% at 50% -10%, #171D2C 0%, #0B0E14 55%)',
      boxShadow: '0 24px 60px -24px rgba(76,111,255,0.2)',
    }}
  >
    <style>
      {`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        .lp-panel, .lp-panel * { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
        .lp-display { font-family: 'Space Grotesk', ui-sans-serif, system-ui, sans-serif; }
        .lp-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .lp-field {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          color: #F1F4F9;
        }
        .lp-field::placeholder { color: rgba(241,244,249,0.28); }
        .lp-field:focus { outline: none; border-color: rgba(76,111,255,0.5); box-shadow: 0 0 0 3px rgba(76,111,255,0.15); }
        .lp-field option { background: #141924; color: #F1F4F9; }
      `}
    </style>

    {/* Header */}
    <div className="flex items-center gap-3 border-b px-5 py-4 sm:px-7" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
      <button
        type="button"
        onClick={() => setShowLeadActions(false)}
        className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition"
        style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.75)' }}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        Close
      </button>
      <div>
        <p className="lp-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.35)' }}>
          Lead management
        </p>
        <h3 className="lp-display text-sm font-semibold sm:text-base" style={{ color: '#F1F4F9' }}>
          Actions &amp; filters
        </h3>
      </div>
    </div>

    <div className="mx-auto max-w-5xl space-y-6 px-5 py-6 sm:px-7">
      {/* ── Manual Workflow ─────────────────────────────────── */}
      <div className="rounded-2xl border p-5" style={{ borderColor: 'rgba(76,111,255,0.25)', background: 'rgba(76,111,255,0.05)' }}>
        <div className="mb-4 flex items-start gap-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-base" style={{ background: 'rgba(76,111,255,0.15)' }}>
            🎯
          </span>
          <div>
            <p className="lp-mono text-[11px] font-medium uppercase tracking-wide" style={{ color: '#93A9FF' }}>
              Manual workflow
            </p>
            <p className="mt-1 text-xs" style={{ color: 'rgba(241,244,249,0.5)' }}>
              Use manual filters and run a standard lead pull. This does not consume credits.
            </p>
          </div>
        </div>

        <button
          onClick={handleLeadPull}
          disabled={actionsDisabled || deletingLeadId !== null}
          className="mb-4 w-full rounded-full px-4 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          style={{ color: '#0B0E14', background: 'linear-gradient(90deg, #4C6FFF, #06B6D4)', boxShadow: '0 8px 20px -8px rgba(76,111,255,0.5)' }}
        >
          {leadPullLoading ? 'Processing…' : 'New Lead Pull (Free)'}
        </button>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            placeholder="Location"
            className="lp-field w-full rounded-xl px-3.5 py-2.5 text-sm transition sm:w-auto"
          />
          <select
            value={locationSelectValue}
            onChange={(event) => setLocation(event.target.value)}
            className="lp-field w-full rounded-xl px-3.5 py-2.5 text-sm transition sm:w-auto"
          >
            <option value="">All Locations</option>
            {availableLocations.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
          <input
            type="text"
            value={sector}
            onChange={(event) => setSector(event.target.value)}
            placeholder="Sector"
            className="lp-field w-full rounded-xl px-3.5 py-2.5 text-sm transition sm:w-auto"
          />
        </div>
      </div>

      {/* ── AI Workflow (Credits) ───────────────────────────── */}
      <div className="rounded-2xl border p-5" style={{ borderColor: 'rgba(139,92,246,0.25)', background: 'rgba(139,92,246,0.05)' }}>
        <div className="mb-4 flex items-start gap-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-base" style={{ background: 'rgba(139,92,246,0.15)' }}>
            🤖
          </span>
          <div>
            <p className="lp-mono text-[11px] font-medium uppercase tracking-wide" style={{ color: '#C4B5FD' }}>
              AI workflow · credits
            </p>
            <p className="mt-1 text-xs" style={{ color: 'rgba(241,244,249,0.5)' }}>
              AI town pull = 1 credit, AI sector pull = 1 credit, AI pull leads = 1 credit per run.
            </p>
          </div>
        </div>

        {isPermissionRestricted && (
          <div className="mb-4 rounded-xl border px-3.5 py-2.5 text-xs" style={{ borderColor: 'rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.08)', color: '#FCD34D' }}>
            AI and Enrich features are disabled for your permission level.
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Towns */}
          <div className="flex flex-col gap-3 rounded-2xl border p-4" style={{ borderColor: 'rgba(139,92,246,0.18)', background: 'rgba(255,255,255,0.015)' }}>
            <div>
              <label className="lp-mono text-[11px] font-medium uppercase tracking-wide" style={{ color: '#C4B5FD' }}>
                AI towns / areas prompt
              </label>
              <p className="mt-1 text-[11px]" style={{ color: 'rgba(241,244,249,0.45)' }}>
                Generate town/area suggestions from AI. Cost: 1 credit.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={aiLocationPrompt}
                onChange={(event) => setAiLocationPrompt(event.target.value)}
                placeholder="e.g. towns in Kent UK"
                className="lp-field flex-1 rounded-xl px-3.5 py-2.5 text-sm transition"
              />
              <button
                type="button"
                onClick={handleAiLocationsRequest}
                disabled={aiLocationsLoading || actionsDisabled || !canUseAiAndEnrich}
                className="w-full flex-shrink-0 rounded-full px-3.5 py-2.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                style={{ color: '#0B0E14', background: 'linear-gradient(90deg, #8B5CF6, #4C6FFF)', boxShadow: '0 8px 20px -8px rgba(139,92,246,0.5)' }}
              >
                {aiLocationsLoading ? 'Pulling…' : 'AI Town Pull (1 credit)'}
              </button>
            </div>
            {aiLocations.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {aiLocations.map((loc) => (
                  <span
                    key={`ai-loc-${loc}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                    style={{ background: 'rgba(139,92,246,0.14)', color: '#C4B5FD' }}
                  >
                    {loc}
                    <button
                      type="button"
                      onClick={() => removeAiLocation(loc)}
                      style={{ color: 'rgba(196,181,253,0.6)' }}
                      className="hover:text-red-400"
                      aria-label={`Remove ${loc}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => setAiLocations([])}
                  className="text-xs font-medium"
                  style={{ color: '#FCA5A5' }}
                >
                  Clear towns
                </button>
              </div>
            )}
          </div>

          {/* Sectors */}
          <div className="flex flex-col gap-3 rounded-2xl border p-4" style={{ borderColor: 'rgba(139,92,246,0.18)', background: 'rgba(255,255,255,0.015)' }}>
            <div>
              <label className="lp-mono text-[11px] font-medium uppercase tracking-wide" style={{ color: '#C4B5FD' }}>
                AI sectors prompt
              </label>
              <p className="mt-1 text-[11px]" style={{ color: 'rgba(241,244,249,0.45)' }}>
                Generate sector suggestions from AI. Cost: 1 credit.
              </p>
            </div>

            {existingLeadSectors.length > 0 && (
              <div className="rounded-xl border p-3" style={{ borderColor: 'rgba(139,92,246,0.15)', background: 'rgba(255,255,255,0.02)' }}>
                <div className="flex items-center justify-between gap-2">
                  <p className="lp-mono text-[10px] font-medium uppercase tracking-wide" style={{ color: 'rgba(241,244,249,0.5)' }}>
                    Use existing sectors with new towns
                  </p>
                  {selectedExistingAiSectors.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedExistingAiSectors([])}
                      className="text-[11px] font-medium"
                      style={{ color: '#FCA5A5' }}
                    >
                      Clear selected
                    </button>
                  )}
                </div>
                <div className="mt-2 flex max-h-28 flex-wrap gap-1.5 overflow-y-auto pr-1">
                  {existingLeadSectors.map((existingSector) => {
                    const isSelected = selectedExistingAiSectors.includes(existingSector);
                    return (
                      <button
                        key={`existing-ai-sector-${existingSector}`}
                        type="button"
                        onClick={() => toggleExistingAiSector(existingSector)}
                        className="rounded-full border px-3 py-1 text-xs font-medium transition"
                        style={
                          isSelected
                            ? { borderColor: '#8B5CF6', background: '#8B5CF6', color: '#F1F4F9' }
                            : { borderColor: 'rgba(139,92,246,0.2)', background: 'rgba(255,255,255,0.02)', color: 'rgba(241,244,249,0.6)' }
                        }
                      >
                        {isSelected ? '✓ ' : ''}
                        {existingSector}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={aiSectorPrompt}
                onChange={(event) => setAiSectorPrompt(event.target.value)}
                placeholder="e.g. service sectors in construction"
                className="lp-field flex-1 rounded-xl px-3.5 py-2.5 text-sm transition"
              />
              <button
                type="button"
                onClick={handleAiSectorsRequest}
                disabled={aiSectorsLoading || actionsDisabled || !canUseAiAndEnrich}
                className="w-full flex-shrink-0 rounded-full px-3.5 py-2.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                style={{ color: '#0B0E14', background: 'linear-gradient(90deg, #8B5CF6, #4C6FFF)', boxShadow: '0 8px 20px -8px rgba(139,92,246,0.5)' }}
              >
                {aiSectorsLoading ? 'Pulling…' : 'AI Sector Pull (1 credit)'}
              </button>
            </div>

            {aiSectors.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {aiSectors.map((sec) => (
                  <span
                    key={`ai-sec-${sec}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                    style={{ background: 'rgba(139,92,246,0.14)', color: '#C4B5FD' }}
                  >
                    {sec}
                    <button
                      type="button"
                      onClick={() => removeAiSector(sec)}
                      style={{ color: 'rgba(196,181,253,0.6)' }}
                      className="hover:text-red-400"
                      aria-label={`Remove ${sec}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => setAiSectors([])}
                  className="text-xs font-medium"
                  style={{ color: '#FCA5A5' }}
                >
                  Clear sectors
                </button>
              </div>
            )}

            {selectedExistingAiSectors.length > 0 && (
              <p className="text-[11px]" style={{ color: 'rgba(241,244,249,0.4)' }}>
                Selected existing sectors will be combined with any AI sector results when you run AI Pull Leads.
              </p>
            )}
          </div>
        </div>

        {/* AI lead pull */}
        <div className="mt-4 rounded-2xl border p-4" style={{ borderColor: 'rgba(139,92,246,0.2)', background: 'rgba(139,92,246,0.08)' }}>
          <label className="lp-mono text-[11px] font-medium uppercase tracking-wide" style={{ color: '#C4B5FD' }}>
            AI lead pull
          </label>
          <p className="mb-3 mt-1 text-[11px]" style={{ color: 'rgba(241,244,249,0.45)' }}>
            This runs lead pulls using your current AI towns and AI sectors list.
          </p>
          <button
            onClick={runAiBulkWithCredits}
            disabled={actionsDisabled || aiLeadPullRunning || deletingLeadId !== null || !canUseAiAndEnrich}
            className="w-full rounded-full px-4 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            style={{ color: '#0B0E14', background: 'linear-gradient(90deg, #8B5CF6, #0EA5E9)', boxShadow: '0 8px 20px -8px rgba(139,92,246,0.5)' }}
          >
            {aiLeadPullRunning ? 'Running AI Pull Leads…' : 'AI Pull Leads'}
          </button>
        </div>

        {aiLeadPullProgress && (
          <div className="mt-3 rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm" style={{ borderColor: 'rgba(139,92,246,0.25)', background: 'rgba(139,92,246,0.1)', color: '#DDD6FE' }}>
            {aiLeadPullProgress}
          </div>
        )}
      </div>

      {/* ── Filters & Enrich ─────────────────────────────────── */}
      <div className="space-y-4">
        {/* Row 1: Search + Location + Sector */}
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
          <input
            type="text"
            value={globalLeadSearch}
            onChange={(e) => setGlobalLeadSearch(e.target.value)}
            placeholder="Search all lead fields…"
            className="lp-field w-full rounded-xl px-3.5 py-2.5 text-sm transition"
          />
          <select
            value={locationSelectValue}
            onChange={(e) => setLocation(e.target.value)}
            className="lp-field w-full rounded-xl px-3.5 py-2.5 text-sm transition"
          >
            <option value="">All Locations</option>
            {locationOptionsForActiveFilters.map((loc) => (
              <option key={`active-filter-loc-${loc}`} value={loc}>{loc}</option>
            ))}
          </select>
          <select
            value={sectorSelectValue}
            onChange={(e) => setSector(e.target.value)}
            className="lp-field w-full rounded-xl px-3.5 py-2.5 text-sm transition"
          >
            <option value="">All Sectors</option>
            {sectorOptionsForActiveFilters.map((sec) => (
              <option key={`active-filter-sec-${sec}`} value={sec}>{sec}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="lp-mono text-xs" style={{ color: 'rgba(241,244,249,0.4)' }}>
            {filteredLeads.length} matching lead{filteredLeads.length === 1 ? '' : 's'}
          </p>
          <button
            type="button"
            onClick={() => setShowAdvancedFilters((previous) => !previous)}
            aria-expanded={showAdvancedFilters}
            className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition"
            style={
              showAdvancedFilters
                ? { borderColor: '#06B6D4', background: '#06B6D4', color: '#0B0E14' }
                : { borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.7)' }
            }
          >
            <FunnelIcon className="h-3.5 w-3.5" />
            {showAdvancedFilters ? 'Hide advanced filters' : 'Advanced filters'}
          </button>
        </div>

        {showAdvancedFilters && (
          <div className="space-y-4 rounded-2xl border p-4" style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.015)' }}>
            {/* Secondary filters */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowEmailCampaignOnly((prev) => !prev)}
                disabled={actionsDisabled || deletingLeadId !== null}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition"
                style={
                  showEmailCampaignOnly
                    ? { borderColor: '#10B981', background: '#10B981', color: '#0B0E14' }
                    : { borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.06)', color: '#6EE7B7' }
                }
              >
                <EnvelopeIcon className="h-3 w-3" />
                {showEmailCampaignOnly ? '✓ Email Campaign' : 'Email Campaign'}
              </button>

              <button
                onClick={() => setShowUnfilteredOnly((prev) => !prev)}
                disabled={actionsDisabled || deletingLeadId !== null}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition"
                style={
                  showUnfilteredOnly
                    ? { borderColor: '#94A3B8', background: '#94A3B8', color: '#0B0E14' }
                    : { borderColor: 'rgba(148,163,184,0.3)', background: 'rgba(148,163,184,0.06)', color: '#CBD5E1' }
                }
              >
                <FunnelIcon className="h-3 w-3" />
                {showUnfilteredOnly ? '✓ Unfiltered' : 'Unfiltered'}
              </button>

              <button
                onClick={() => setShowContactedOnly((prev) => {
                  if (prev) { setContactedFromDate(''); setContactedToDate(''); }
                  return !prev;
                })}
                disabled={actionsDisabled || deletingLeadId !== null}
                className="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                style={
                  showContactedOnly
                    ? { borderColor: '#FBBF24', background: '#FBBF24', color: '#0B0E14' }
                    : { borderColor: 'rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.06)', color: '#FCD34D' }
                }
              >
                {showContactedOnly ? '✓ Contacted' : 'Contacted Only'}
              </button>

              <button
                onClick={() => setShowUnsentEnrichedOnly((prev) => !prev)}
                disabled={actionsDisabled || deletingLeadId !== null}
                className="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                style={
                  showUnsentEnrichedOnly
                    ? { borderColor: '#10B981', background: '#10B981', color: '#0B0E14' }
                    : { borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.06)', color: '#6EE7B7' }
                }
              >
                {showUnsentEnrichedOnly ? '✓ Enriched & Unsent' : 'Enriched & Unsent'}
              </button>

              {hasInsightAccount && (
                <button
                  onClick={() => setShowScheduledOnly((prev) => !prev)}
                  disabled={actionsDisabled || deletingLeadId !== null}
                  className="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                  style={
                    showScheduledOnly
                      ? { borderColor: '#FBBF24', background: '#FBBF24', color: '#0B0E14' }
                      : { borderColor: 'rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.06)', color: '#FCD34D' }
                  }
                >
                  {showScheduledOnly ? '✓ Scheduled Only' : 'Scheduled Only'}
                </button>
              )}

              <button
                onClick={() => setShowLinkedInOnly((prev) => !prev)}
                disabled={actionsDisabled || deletingLeadId !== null}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition"
                style={
                  showLinkedInOnly
                    ? { borderColor: '#4C6FFF', background: '#4C6FFF', color: '#0B0E14' }
                    : { borderColor: 'rgba(76,111,255,0.3)', background: 'rgba(76,111,255,0.06)', color: '#93A9FF' }
                }
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                {showLinkedInOnly ? '✓ LinkedIn Only' : 'LinkedIn Only'}
              </button>

              {showLinkedInOnly && (
                <select
                  value={linkedInStatusFilter}
                  onChange={(e) => setLinkedInStatusFilter(e.target.value)}
                  disabled={actionsDisabled || deletingLeadId !== null}
                  title="Filter by LinkedIn status"
                  className="lp-field rounded-full px-3 py-1.5 text-xs font-medium"
                  style={{ borderColor: 'rgba(76,111,255,0.3)', color: '#93A9FF' }}
                >
                  <option value="">All Statuses</option>
                  <option value="Not Followed">Not Followed</option>
                  <option value="Following">Following</option>
                  <option value="Requested">Requested</option>
                  <option value="Connected">Connected</option>
                  <option value="Message Sent">Message Sent</option>
                </select>
              )}

              <button
                onClick={() => setShowLinkedInMessageSentOnly((prev) => !prev)}
                disabled={actionsDisabled || deletingLeadId !== null}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition"
                style={
                  showLinkedInMessageSentOnly
                    ? { borderColor: '#3B5BDB', background: '#3B5BDB', color: '#F1F4F9' }
                    : { borderColor: 'rgba(76,111,255,0.3)', background: 'rgba(76,111,255,0.06)', color: '#93A9FF' }
                }
              >
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {showLinkedInMessageSentOnly ? '✓ LI Msg Sent' : 'LI Msg Sent'}
              </button>

              <div className="ml-auto flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setLocation('')}
                  className="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                  style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.55)' }}
                >
                  Clear Location
                </button>
                <button
                  type="button"
                  onClick={() => setSector('')}
                  className="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                  style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.55)' }}
                >
                  Clear Sector
                </button>
                <button
                  type="button"
                  onClick={() => { setShowContactedOnly(false); setContactedFromDate(''); setContactedToDate(''); setGlobalLeadSearch(''); setShowUnsentEnrichedOnly(false); setShowScheduledOnly(false); setShowLinkedInOnly(false); setLinkedInStatusFilter(''); setShowEmailCampaignOnly(false); setShowUnfilteredOnly(false); setShowLinkedInMessageSentOnly(false); }}
                  className="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                  style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.55)' }}
                >
                  Clear All
                </button>
              </div>
            </div>

            {/* Revenue range */}
            <div className="grid gap-3 rounded-xl border p-3.5 sm:grid-cols-2" style={{ borderColor: 'rgba(6,182,212,0.2)', background: 'rgba(6,182,212,0.05)' }}>
              <label className="flex flex-col gap-1.5">
                <span className="lp-mono text-[10px] font-medium uppercase tracking-wide" style={{ color: '#67E8F9' }}>
                  Search revenue min
                </span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={extractedRevenueMin}
                  onChange={(event) => setExtractedRevenueMin(event.target.value)}
                  placeholder="e.g. 50000"
                  className="lp-field rounded-xl px-3.5 py-2.5 text-sm transition"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="lp-mono text-[10px] font-medium uppercase tracking-wide" style={{ color: '#67E8F9' }}>
                  Search revenue max
                </span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={extractedRevenueMax}
                  onChange={(event) => setExtractedRevenueMax(event.target.value)}
                  placeholder="e.g. 250000"
                  className="lp-field rounded-xl px-3.5 py-2.5 text-sm transition"
                />
              </label>
            </div>

            {/* Contacted date range */}
            {showContactedOnly && (
              <div className="flex flex-wrap items-center gap-2.5 rounded-xl border p-3" style={{ borderColor: 'rgba(251,191,36,0.25)', background: 'rgba(251,191,36,0.06)' }}>
                <span className="lp-mono text-[11px] font-medium uppercase tracking-wide" style={{ color: '#FCD34D' }}>
                  Contact date range
                </span>
                <input
                  type="date"
                  value={contactedFromDate}
                  onChange={(e) => setContactedFromDate(e.target.value)}
                  aria-label="Contacted from"
                  className="lp-field rounded-lg px-2.5 py-1.5 text-xs"
                  style={{ borderColor: 'rgba(251,191,36,0.25)' }}
                />
                <span className="text-xs" style={{ color: 'rgba(252,211,77,0.6)' }}>to</span>
                <input
                  type="date"
                  value={contactedToDate}
                  onChange={(e) => setContactedToDate(e.target.value)}
                  aria-label="Contacted to"
                  className="lp-field rounded-lg px-2.5 py-1.5 text-xs"
                  style={{ borderColor: 'rgba(251,191,36,0.25)' }}
                />
                <span className="lp-mono rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: 'rgba(251,191,36,0.18)', color: '#FCD34D' }}>
                  {contactedInDateRangeCount} in range
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── Enrich — linked to active location/sector filter ── */}
        <div
          className="rounded-2xl border p-5"
          style={
            noCreditsRemaining
              ? { borderColor: 'rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.06)' }
              : { borderColor: 'rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.05)' }
          }
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <span className="lp-display text-sm font-semibold" style={{ color: noCreditsRemaining ? '#FCA5A5' : '#6EE7B7' }}>
              Enrich Leads
            </span>
            <span className="lp-mono text-[11px]" style={{ color: noCreditsRemaining ? 'rgba(252,165,165,0.7)' : 'rgba(110,231,183,0.7)' }}>
              2 cr/lead (+ 1 cr if Companies House found)
            </span>
          </div>

          {/* Active filter context */}
          <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
            {location.trim() && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium" style={{ background: 'rgba(6,182,212,0.14)', color: '#67E8F9' }}>
                📍 {location.trim()}
                <button type="button" onClick={() => setLocation('')} className="ml-0.5" style={{ color: 'rgba(103,232,249,0.6)' }}>×</button>
              </span>
            )}
            {sector.trim() && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium" style={{ background: 'rgba(139,92,246,0.14)', color: '#C4B5FD' }}>
                🏢 {sector.trim()}
                <button type="button" onClick={() => setSector('')} className="ml-0.5" style={{ color: 'rgba(196,181,253,0.6)' }}>×</button>
              </span>
            )}
            {!location.trim() && !sector.trim() && (
              <span style={{ color: 'rgba(241,244,249,0.4)' }}>No location/sector filter — enriching all leads in scope</span>
            )}
          </div>

          {/* Stats grid */}
          <div className="mb-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { label: 'Leads in scope', value: enrichPreview.targetCount },
              { label: 'Can enrich now', value: enrichPreview.leadsToEnrich },
              { label: 'Queue cap', value: `${enrichPreview.queueLeadCap} leads` },
              { label: 'Cost now (max)', value: `${enrichPreview.creditsToConsumeNow} cr` },
              { label: 'All leads min', value: `${enrichPreview.minimumCreditsForAll} cr` },
              { label: 'All leads max', value: `${enrichPreview.estimatedCreditsForAll} cr` },
            ].map((stat) => (
              <div key={stat.label} className="rounded-xl border px-3 py-2" style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
                <p className="lp-mono text-[9px] uppercase tracking-wide" style={{ color: 'rgba(241,244,249,0.4)' }}>
                  {stat.label}
                </p>
                <p className="lp-display text-sm font-semibold" style={{ color: '#F1F4F9' }}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          {noCreditsRemaining && (
            <div className="mb-4 flex items-center gap-3">
              <span className="text-xs font-medium" style={{ color: '#FCA5A5' }}>No credits remaining.</span>
              <button
                type="button"
                onClick={() => setBuyModalOpen(true)}
                className="rounded-full px-3.5 py-1.5 text-xs font-medium"
                style={{ background: 'linear-gradient(90deg, #F87171, #EC4899)', color: '#0B0E14' }}
              >
                Buy Credits
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="lp-mono text-[11px] font-medium uppercase tracking-wide" style={{ color: noCreditsRemaining ? '#FCA5A5' : '#6EE7B7' }}>
                Credits to use
              </label>
              <input
                type="number"
                min="2"
                max={enrichCreditsInputCap}
                step="1"
                value={enrichCreditsToUse}
                onChange={(e) => {
                  const nextValue = Number(e.target.value);
                  if (!Number.isFinite(nextValue)) {
                    setEnrichCreditsToUse(e.target.value);
                    return;
                  }
                  setEnrichCreditsToUse(String(Math.max(2, Math.min(nextValue, enrichCreditsInputCap))));
                }}
                className="lp-field w-20 rounded-lg px-2.5 py-1.5 text-sm"
              />
            </div>
            <button
              onClick={runEnrichWithCredits}
              disabled={actionsDisabled || deletingLeadId !== null || !canRunEnrich || !canUseAiAndEnrich}
              className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
              style={{ color: '#0B0E14', background: 'linear-gradient(90deg, #10B981, #0EA5E9)', boxShadow: '0 8px 20px -8px rgba(16,185,129,0.5)' }}
            >
              {filterLoading ? (
                <>
                  <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Enriching…
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Enrich {enrichPreview.leadsToEnrich > 0 ? `${enrichPreview.leadsToEnrich} leads` : '(no leads)'}{(location.trim() || sector.trim()) ? ' · filtered' : ''}
                </>
              )}
            </button>
          </div>

          <p className="mt-3 text-[11px] leading-relaxed" style={{ color: 'rgba(241,244,249,0.4)' }}>
            If Companies House details are not found for a lead, the extra credit is not charged. Minimum charge is 2 credits per lead. Revenue filters apply to lead search results before enrichment. Enrich runs are capped at {enrichPreview.queueLeadCap} leads so more users can use the queue at the same time.
          </p>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Bulk Operations feedback — actions now trigger directly from the sidebar */}
      {(bulkInitialSendFeedback || bulkInitialSendError || bulkDeleteFeedback || bulkDeleteError) && (
        <div className="mb-4 space-y-2">
          {bulkInitialSendFeedback && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {bulkInitialSendFeedback}
            </div>
          )}
          {bulkInitialSendError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {bulkInitialSendError}
            </div>
          )}
          {bulkDeleteFeedback && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {bulkDeleteFeedback}
            </div>
          )}
          {bulkDeleteError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {bulkDeleteError}
            </div>
          )}
        </div>
      )}

      {error && !showLeadActions && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      )}
{editingLead && !showLeadActions && (
  <div ref={emailEditorRef} className="border-y border-slate-200 bg-white">
    {/* Header */}
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-6">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-slate-900">
            {editingLead.Company ?? 'Unknown Company'}
          </h3>
          <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600">
            <EnvelopeIcon className="h-3 w-3" />
            Initial outreach
          </span>
        </div>
        <p className="text-xs text-slate-500">
          {editingLead.LeadName ?? ''}
          {editingLead.Email ? ` — ${editingLead.Email}` : ''}
        </p>
        <p className="text-xs text-slate-500">
          {editingLead.Location ? `Location: ${editingLead.Location}` : ''}
          {editingLead.Sector ? ` — Sector: ${editingLead.Sector}` : ''}
          {editingLead.CompanyReport ? ` — Company Report: ${editingLead.CompanyReport}` : ''}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleAiRewrite(editingLead.id)}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 box-shadow-md disabled:cursor-not-allowed disabled:opacity-50"
          disabled={editingSubmitting}
        >
          <SparklesIcon className="h-3.5 w-3.5" />
          AI rewrite
        </button>
        <div className="h-5 w-px bg-slate-200" />
        <button
          type="button"
          onClick={() => closeInitialEmailEditor(editingLead.id, { resetMessage: true })}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
          disabled={editingSubmitting}
        >
          <XMarkIcon className="h-3.5 w-3.5" />
          Close
        </button>
      </div>
    </div>

    {/* Body */}
    <div className="flex flex-col gap-3 px-4 py-4 sm:px-6">
      <textarea
        value={editingDraft}
        onChange={(event) => handleInitialEmailDraftChange(editingLead.id, event.target.value)}
        rows={10}
        className="w-full resize-y rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm leading-relaxed text-slate-900 shadow-none focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
        placeholder="Compose the initial email..."
        disabled={editingSubmitting}
      />

      {editingMessage?.error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
          {editingMessage.error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!editingLead.InitalSchedule && Number(editingLead?.EmailConfidence ?? 0) > 0 && (
        <button
          type="button"
          onClick={() => submitInitialEmail(editingLead)}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={editingSubmitting}
        >
          <PaperAirplaneIcon className="h-4 w-4" />
          {editingSubmitting ? 'Sending...' : 'Send email'}
        </button>
        )}
        <button
          type="button"
          onClick={() => closeInitialEmailEditor(editingLead.id, { resetMessage: true })}
          className="inline-flex items-center rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={editingSubmitting}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
     
      {sectorSummary && !showLeadActions && (
        <div className="px-4 sm:px-6 py-3 bg-indigo-50 border-b border-indigo-100 text-sm text-indigo-800">
          <span className="font-semibold mr-2">Pursued Leads by Sector:</span>
          {Object.keys(sectorSummary).length === 0 ? (
            <span>No pursued leads available.</span>
          ) : (
            <span>
              {Object.entries(sectorSummary).map(([sectorName, count], idx) => (
                <span key={sectorName}>
                  {idx > 0 && ', '}
                  <span className="font-medium">{sectorName}</span>: {count}
                </span>
              ))}
            </span>
          )}
        </div>
      )}

     
      {/* Dashboard Stats */}
      <div className={classNames('relative mb-3 rounded-2xl border border-white/45 bg-gradient-to-br from-purple-100/60 via-sky-100/50 to-emerald-100/60 px-3 py-3 shadow-[0_16px_50px_rgba(59,130,246,0.15)] backdrop-blur sm:px-4 sm:py-4', showLeadActions && 'hidden')}>
        {tableLoading && !aiLeadPullRunning && !leadPullLoading && <LeadsLoader />}
        <h2 className="mb-2 text-base font-bold text-gray-900 sm:text-lg">Lead Dashboard</h2>
        
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-6">
          <div className={classNames(glassCardClass, 'border-purple-200/80 p-2.5')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">Total Leads</p>
                <p className="mt-0.5 text-xl font-bold text-indigo-600">{dashboardStats.totalLeads}</p>
              </div>
              <div className="rounded-full bg-indigo-100 p-2">
                <svg className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className={classNames(glassCardClass, 'border-blue-200/80 p-2.5')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">Enriched</p>
                <p className="mt-0.5 text-xl font-bold text-blue-600">{dashboardStats.filteredCount}</p>
                <p className="mt-0.5 text-[10px] text-gray-500">{dashboardStats.enrichedOfTotalPct}% of Total</p>
              </div>
              <div className="rounded-full bg-blue-100 p-2">
                <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className={classNames(glassCardClass, 'border-fuchsia-200/80 p-2.5')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">Contacted</p>
                <p className="mt-0.5 text-xl font-bold text-fuchsia-600">{dashboardStats.leadsContacted}</p>
                <p className="mt-0.5 text-[10px] text-gray-500">{dashboardStats.contactedOfEnrichedPct}% of Enriched</p>
              </div>
              <div className="rounded-full bg-fuchsia-100 p-2">
                <StarIcon className="h-4 w-4 text-fuchsia-600" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className={classNames(glassCardClass, 'border-emerald-300/80 bg-gradient-to-br from-emerald-50/90 via-lime-50/80 to-white/80 p-2.5')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">Emails Sent</p>
                <p className="mt-0.5 text-xl font-bold text-emerald-700">{dashboardStats.emailSent}</p>
                <p className="mt-0.5 text-[10px] text-emerald-500">{dashboardStats.emailSentToday} sent today</p>
              </div>
              <div className="rounded-full bg-emerald-100 p-2">
                <svg className="h-4 w-4 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 11H5a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className={classNames(glassCardClass, 'border-amber-200/80 p-2.5')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">Credits</p>
                <p className="mt-0.5 text-xl font-bold text-amber-600">{creditsLoading ? '...' : creditsRemaining}</p>
                <p className="mt-0.5 text-[10px] text-gray-500">
                  {creditsLoading ? 'Remaining' : `Enrich ~${enrichCapacityOnBudget} leads`}
                </p>
                {!creditsLoading && enrichCapacityOnBudget < dashboardStats.totalLeads && (
                  <button
                    type="button"
                    onClick={() => setBuyModalOpen(true)}
                    className="mt-1 rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-amber-500"
                  >
                    Buy Credits
                  </button>
                )}
              </div>
              <div className="rounded-full bg-amber-100 p-2">
                <BanknotesIcon className="h-4 w-4 text-amber-600" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className={classNames(glassCardClass, 'border-sky-200/80 p-2.5')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">Credits Used</p>
                <p className="mt-0.5 text-xl font-bold text-sky-600 animate-pulse">{creditsLoading ? '...' : creditsUsed}</p>
              </div>
              <div className="rounded-full bg-sky-100 p-2">
                <ChartBarIcon className="h-4 w-4 text-sky-600" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
     {/* ── Connected Inboxes — Signal Panel ── */}
{/* ── Connected Inboxes — Signal Panel ── */}
<div className="mt-6">
  {/* Header */}
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2.5">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
      </span>
      <h3
        className="text-[11px] font-semibold uppercase tracking-[0.16em] text-black">
        
        Connected Inboxes
      </h3>
    </div>
    <span
      className="rounded-md border border-indigo-500/25 bg-indigo-600 px-2 py-0.5 text-[11px] text-white"
    >
      {String(dashboardStats.inboxes.length).padStart(2, '0')} ACTIVE
    </span>
  </div>

  {/* Scroll row */}
  <div className="overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
    <div className="flex gap-3 w-max">
      {dashboardStats.inboxes.length === 0 ? (
        <div className="w-44 flex-shrink-0 rounded-xl border border-dashed border-indigo-500/25 bg-gradient-to-br from-[#171142] to-[#0d0a2b] p-4 text-center">
          <EnvelopeIcon className="h-6 w-6 mx-auto text-indigo-400/50 mb-2" />
          <p className="text-xs font-medium text-indigo-200">No inboxes</p>
          <p
            className="text-[10px] text-indigo-400/60 mt-1"
            style={{ fontFamily: "'Space Grotesk', monospace" }}
          >
            Configure SMTP to start
          </p>
        </div>
      ) : (
        dashboardStats.inboxes.map((inbox, idx) => {
          // Distinct dark gradient per inbox so cards are visually distinguishable at a glance
          const inboxGradients = [
            'bg-gradient-to-br from-blue-950 via-indigo-800 to-purple-700',
            'bg-gradient-to-br from-emerald-950 via-teal-800 to-cyan-700',
            'bg-gradient-to-br from-rose-950 via-fuchsia-800 to-purple-700',
            'bg-gradient-to-br from-amber-950 via-orange-800 to-red-700',
            'bg-gradient-to-br from-slate-950 via-sky-800 to-blue-700',
            'bg-gradient-to-br from-violet-950 via-pink-800 to-rose-700',
          ];
          const inboxGradientClass = inboxGradients[idx % inboxGradients.length];
          // Accent spectrum: cool → warm as warmup stage advances
          const stageAccents = {
            0: { ring: '#8183c9', glow: 'rgba(129,131,201,0.4)',  text: 'text-indigo-300', label: 'IDLE' },
            1: { ring: '#38bdf8', glow: 'rgba(56,189,248,0.4)',   text: 'text-sky-300',    label: 'WARMING' },
            2: { ring: '#22d3ee', glow: 'rgba(34,211,238,0.4)',   text: 'text-cyan-300',   label: 'BUILDING' },
            3: { ring: '#34d399', glow: 'rgba(52,211,153,0.4)',   text: 'text-emerald-300',label: 'ACTIVE' },
            4: { ring: '#c084fc', glow: 'rgba(192,132,252,0.4)',  text: 'text-fuchsia-300',label: 'ADVANCED' },
            5: { ring: '#fbbf24', glow: 'rgba(251,191,36,0.4)',   text: 'text-amber-300',  label: 'HIGH VOL' },
          };
          const accent = stageAccents[inbox.stage] || stageAccents[0];

          // Progress is against this inbox's own stage daily cap, not a fixed target
          const stageForCap = Number(inbox.stage) || 1;
          let stageDailyCap = 20;
          if (stageForCap === 2) stageDailyCap = 30;
          else if (stageForCap === 3) stageDailyCap = 40;
          else if (stageForCap === 4) stageDailyCap = 50;
          const progress = Math.min(100, (inbox.sent / stageDailyCap) * 100);
          const r = 30;
          const circumference = 2 * Math.PI * r;
          const dashOffset = circumference * (1 - progress / 100);

          return (
            <div
              key={idx}
              className={classNames(
                'group relative w-44 flex-shrink-0 overflow-hidden rounded-xl border border-indigo-500/15 p-3 shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset] transition-all duration-300 hover:-translate-y-0.5 hover:border-indigo-400/30',
                inboxGradientClass
              )}>
              {/* ambient accent glow, tinted to this card's stage */}
              <div
                className="pointer-events-none absolute -top-8 -right-8 h-20 w-20 rounded-full blur-2xl opacity-30"
                style={{ backgroundColor: accent.ring }}
              />

              {/* corner accent */}
              <div
                className="pointer-events-none absolute -top-px -left-px h-5 w-5 rounded-tl-xl border-t border-l opacity-80"
                style={{ borderColor: accent.ring }}
              />

              {/* Header row */}
              <div className="relative flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <EnvelopeIcon className="h-3 w-3 text-white flex-shrink-0" />
                  <span
                    className="truncate text-[10px] text-white"
                    title={inbox.fromEmail}
                  >
                    {inbox.fromEmail}
                  </span>
                </div>
              </div>

              {/* Signal ring */}
              <div className="relative mx-auto flex h-16 w-16 items-center justify-center">
                <svg viewBox="0 0 96 96" className="h-16 w-16 -rotate-90">
                  <circle
                    cx="48"
                    cy="48"
                    r={r}
                    fill="none"
                    stroke="rgba(129,131,201,0.15)"
                    strokeWidth="4"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r={r}
                    fill="none"
                    stroke={accent.ring}
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    style={{
                      transition: 'stroke-dashoffset 600ms ease-out',
                      filter: `drop-shadow(0 0 7px ${accent.glow})`,
                    }}
                  />
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span
                    className={`text-sm font-semibold ${accent.text}`}
                  >
                    {Math.round(progress)}
                    <span className="text-[9px] text-white">%</span>
                  </span>
                  
                </div>
              </div>

              {/* Sent readout */}
              <div className="relative mt-2 flex items-center justify-between border-t border-indigo-500/15 pt-2">
                <span className="text-[9px] uppercase tracking-wide text-white">Sent</span>
                <span
                  className="text-xs font-semibold text-white"
                >
                  {inbox.sent.toLocaleString()}
                </span>
              </div>

              {/* Stage badge */}
              <div
                className="relative mt-1.5 flex items-center justify-center gap-1.5 rounded-md border py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                style={{
                  borderColor: `${accent.ring}55`,
                  color: accent.ring,
                  backgroundColor: `${accent.ring}18`,
                  fontFamily: "'Space Grotesk', monospace",
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: accent.ring, boxShadow: `0 0 6px ${accent.glow}` }}
                />
                {accent.label}
                <span className="text-white">· S{inbox.stage}</span>
              </div>
            </div>
          );
        })
      )}
    </div>
  </div>
</div>



        {/* Secondary Metrics */}
       <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3">
  <div className={classNames(glassCardClass, 'p-1.5')}>
    <p className="text-[10px] font-medium text-gray-600 uppercase tracking-wide">Responded</p>
    <p className="mt-0 text-sm font-bold text-gray-900">{dashboardStats.leadsResponded}</p>
  </div>
  
  <div className={classNames(glassCardClass, 'border-amber-200/80 bg-amber-50/60 p-1.5')}>
    <p className="text-[10px] font-medium text-gray-600 uppercase tracking-wide">Sent Today</p>
    <p className="mt-0 text-sm font-bold text-emerald-600">{dashboardStats.emailSentToday}</p>
  </div>
  
  <div className={classNames(glassCardClass, 'border-amber-200/80 bg-amber-50/60 p-1.5')}>
    <p className="text-[10px] font-medium text-gray-600 uppercase tracking-wide">Remaining Today</p>
    <p className="mt-0 text-sm font-bold text-indigo-700">
      {Number(dashboardStats.emailRemainingToday)} / {Number(dashboardStats.emailSentToday) + Number(dashboardStats.emailRemainingToday)}
    </p>
  </div>
</div>

        <div className="mt-4 rounded-2xl border border-cyan-200/70 bg-gradient-to-r from-cyan-50/90 via-sky-50/80 to-emerald-50/80 p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Why staged email sending exists</p>
          <p className="mt-2 text-sm text-slate-700">
            Avenor increases daily email allowance in stages so your domain warms up gradually instead of jumping straight to high-volume sending. Stage progression is based on cumulative successful sends, and each stage unlocks more daily capacity.
          </p>
        </div>

        {/* Scheduled Posts — only shown for InsightOS accounts */}
        {hasInsightAccount && (
          <div className="mt-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Scheduled Emails</h3>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <div className={classNames(glassCardClass, 'border-yellow-300/80 bg-gradient-to-br from-yellow-50/90 via-amber-50/80 to-white/80 p-4')}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Scheduled Today</p>
                    <p className="mt-1 text-3xl font-bold text-yellow-600">{dashboardStats.scheduledToday}</p>
                    <p className="mt-1 text-xs text-gray-500">emails queued for today</p>
                  </div>
                  <div className="rounded-full bg-yellow-100 p-3">
                    <ClockIcon className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
              </div>
              <div className={classNames(glassCardClass, 'border-orange-300/80 bg-gradient-to-br from-orange-50/90 via-amber-50/80 to-white/80 p-4')}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Scheduled Tomorrow</p>
                    <p className="mt-1 text-3xl font-bold text-orange-600">{dashboardStats.scheduledTomorrow}</p>
                    <p className="mt-1 text-xs text-gray-500">emails queued for tomorrow</p>
                  </div>
                  <div className="rounded-full bg-orange-100 p-3">
                    <ClockIcon className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">

                        <div className="mt-3 rounded-xl border border-yellow-300/70 bg-gradient-to-br from-yellow-50/90 via-amber-50/70 to-white/80 p-3 shadow-sm sm:p-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800">Scheduled Times Today </p>
{dashboardStats.scheduledTodayByTime.every((d) => d.y === 0) ? (
                <p className="py-6 text-center text-xs text-amber-500">No scheduled emails for today.</p>
              ) : (
                <div className="w-full">
                  <VictoryChart
                    height={180}
                    width={undefined}
                    padding={{ top: 40, bottom: 32, left: 28, right: 12 }}
                    containerComponent={
                      <VictoryVoronoiContainer
                        responsive={true}
                        voronoiDimension="x"
                        labels={({ datum }) => `${datum.x}: ${datum.y}`}
                        labelComponent={
                          <VictoryTooltip
                            style={{ fontSize: 9 }}
                            flyoutStyle={{ fill: '#fffbeb', stroke: '#fbbf24' }}
                          />
                        }
                      />
                    }
                  >
                    <VictoryAxis
                      tickFormat={dashboardStats.scheduledTodayByTime.map((d) => d.x)}
                      style={{ tickLabels: { fontSize: 8, fill: '#92400e' }, axis: { stroke: '#fde68a' }, grid: { stroke: 'none' } }}
                    />
                    <VictoryAxis
                      dependentAxis
                      tickFormat={(t) => (Number.isInteger(t) ? t : '')}
                      style={{ tickLabels: { fontSize: 8, fill: '#92400e' }, axis: { stroke: '#fde68a' }, grid: { stroke: '#fef3c7' } }}
                    />
                    <VictoryBar
 data={dashboardStats.scheduledTodayByTime}
style={{ data: { fill: '#f59e0b', width: 18 } }}
/>
                  </VictoryChart>
                </div>
              )}
            </div>

            <div className="mt-3 rounded-xl border border-yellow-300/70 bg-gradient-to-br from-yellow-50/90 via-amber-50/70 to-white/80 p-3 shadow-sm sm:p-4">
            
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800">Scheduled this week (Mon–Sun)</p>
              {dashboardStats.scheduledThisWeek.every((d) => d.y === 0) ? (
                <p className="py-6 text-center text-xs text-amber-500">No scheduled emails this week.</p>
              ) : (
                <div className="w-full">
  <VictoryChart
    height={180}
    width={undefined}
    padding={{ top: 40, bottom: 32, left: 28, right: 12 }}
    containerComponent={
      <VictoryVoronoiContainer
        responsive={true}
        voronoiDimension="x"
        labels={({ datum }) => `${datum.x}: ${datum.y}`}
        labelComponent={
          <VictoryTooltip
            style={{ fontSize: 9 }}
            flyoutStyle={{ fill: '#fffbeb', stroke: '#fbbf24' }}
          />
        }
      />
    }
  >
                      <VictoryAxis
                        tickFormat={dashboardStats.scheduledThisWeek.map((d) => d.x)}
                        style={{ tickLabels: { fontSize: 8, fill: '#92400e' }, axis: { stroke: '#fde68a' }, grid: { stroke: 'none' } }}
                      />
                      <VictoryAxis
                        dependentAxis
                        tickFormat={(t) => (Number.isInteger(t) ? t : '')}
                        style={{ tickLabels: { fontSize: 8, fill: '#92400e' }, axis: { stroke: '#fde68a' }, grid: { stroke: '#fef3c7' } }}
                      />
                      <VictoryLine
                        data={dashboardStats.scheduledThisWeek}
                        style={{ data: { stroke: '#f59e0b', strokeWidth: 2.5 } }}
                        interpolation="monotoneX"
                      />
                    </VictoryChart>
                  </div>
              )}
            </div>
          </div>
          </div>
        )}

        {/* LinkedIn Stats */}
        <div className="mt-4">
          <div className={classNames(glassCardClass, 'border-blue-300/80 bg-gradient-to-br from-blue-50/90 via-sky-50/80 to-white/80 p-4')}>
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-full bg-blue-600 p-2.5 shadow-md shadow-blue-500/40">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="h-5 w-5">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-bold text-blue-900">LinkedIn Messages Sent</h3>
                <p className="text-[11px] text-blue-500">Outreach tracked via LinkedInSent timestamp</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-blue-600/10 border border-blue-200/60 px-3 py-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-500 mb-1">Today</p>
                <p className="text-3xl font-bold text-blue-700">{dashboardStats.linkedInSentToday}</p>
                <p className="text-[10px] text-blue-400 mt-0.5">messages</p>
              </div>
              <div className="rounded-xl bg-blue-600/10 border border-blue-200/60 px-3 py-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-500 mb-1">This Week</p>
                <p className="text-3xl font-bold text-blue-700">{dashboardStats.linkedInSentThisWeek}</p>
                <p className="text-[10px] text-blue-400 mt-0.5">Mon – Sun</p>
              </div>
              <div className="rounded-xl bg-blue-600/10 border border-blue-200/60 px-3 py-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-500 mb-1">This Month</p>
                <p className="text-3xl font-bold text-blue-700">{dashboardStats.linkedInSentThisMonth}</p>
                <p className="text-[10px] text-blue-400 mt-0.5">{format(new Date(), 'MMMM')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Companies House */}
        <div className="mt-4 grid gap-4 sm:grid-cols-1">
          <div className={classNames(glassCardClass, 'border-sky-200/80 p-4')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Companies House Hit</p>
                <p className="mt-1 text-3xl font-bold text-sky-600">{dashboardStats.companiesHouseHitCount}</p>
                <p className="mt-1 text-xs text-gray-500">{dashboardStats.companiesHousePercentage}% of total</p>
              </div>
              <div className="rounded-full bg-sky-100 p-3">
                <svg className="h-6 w-6 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Top Locations and Sectors */}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Top 5 Locations</h3>
            <div className="space-y-2">
              {dashboardStats.topLocations.length > 0 ? (
                dashboardStats.topLocations.map(([location, count]) => (
                  <div key={location} className="flex items-center justify-between gap-2">
                    <span className="max-w-[8.5rem] truncate text-xs text-gray-700 sm:max-w-none sm:text-sm">{location}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 sm:w-24">
                        <div
                          className="h-full bg-indigo-500"
                          style={{ width: `${(count / dashboardStats.totalLeads) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-gray-900 sm:text-sm">{count}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No location data</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Top 5 Sectors</h3>
            <div className="space-y-2">
              {dashboardStats.topSectors.length > 0 ? (
                dashboardStats.topSectors.map(([sector, count]) => (
                  <div key={sector} className="flex items-center justify-between gap-2">
                    <span className="max-w-[8.5rem] truncate text-xs text-gray-700 sm:max-w-none sm:text-sm">{sector}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 sm:w-24">
                        <div
                          className="h-full bg-purple-500"
                          style={{ width: `${(count / dashboardStats.totalLeads) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-gray-900 sm:text-sm">{count}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No sector data</p>
              )}
            </div>
          </div>
        </div>

      </div>

      <div ref={leadsTableRef} className={classNames('bg-white/30 backdrop-blur', showLeadActions && 'hidden')}>
        {/* ── Mobile card view (hidden on md+) ──────────────────── */}
        <div className="p-3 space-y-2 md:hidden">
          {tableLoading && !aiLeadPullRunning && !leadPullLoading ? null : filteredLeads.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No leads found.</p>
          ) : (
            visibleFilteredLeads.map((lead, index) => {
              const rowId = lead.id ?? `${lead.LeadName ?? 'lead'}-${lead.Email ?? index}`;
              const companiesHouseHit = Number(lead.CompaniesHouseHit ?? lead.companieshousehit ?? 0) === 1;
              const filterValue = Number(lead.Filter ?? 0);
              const callScriptText = String(lead.CallScript ?? lead.callScript ?? '').trim();
              const hasCallScript = callScriptText.length > 0;
              const isContactedLead = Boolean(parseLeadContactDate(lead));
              const followUpStage = getLeadFollowUpStage(lead);
              const followUpScheduleForLead = parseFollowUpSchedule(lead);
              const isSingleEnrichRunning = singleLeadEnrichingId === lead.id;
              const isExpanded = expandedRows.has(rowId);

              // Permanent LinkedIn / Email group split — render a header whenever the group changes
              const isLinkedInGroupLead = Number(lead.EmailConfidence ?? 0) === 0;
              const previousMobileLead = visibleFilteredLeads[index - 1];
              const isMobileGroupBoundary = index === 0 || isLinkedInGroupLead !== (Number(previousMobileLead?.EmailConfidence ?? 0) === 0);
              const mobileGroupHeader = isMobileGroupBoundary ? (
                <div key={`group-${rowId}`} className={`rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wide ${isLinkedInGroupLead ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                  {isLinkedInGroupLead ? `LinkedIn Leads (${linkedInGroupCount})` : `Email Leads (${emailGroupCount})`}
                </div>
              ) : null;

              const handleAddOfficerUrl = (leadId, leadName, url) => {
                if (!leadName || !url) return;
                const currentOfficers = lead.OfficerURL ? JSON.parse(lead.OfficerURL) : [];
                const updatedOfficers = [...currentOfficers, { LeadName: leadName, URL: url }];
                handleUpdateOfficerUrl(leadId, JSON.stringify(updatedOfficers));
              };

              const handleRemoveOfficerUrl = (leadId, idx) => {
                const currentOfficers = lead.OfficerURL ? JSON.parse(lead.OfficerURL) : [];
                const updatedOfficers = currentOfficers.filter((_, i) => i !== idx);
                handleUpdateOfficerUrl(leadId, JSON.stringify(updatedOfficers));
              };

              const companyReportText = lead.CompanyReport ?? '';
              const jobListingsText = lead.JobLisitings ?? lead.JobListings ?? '';
              const painPointText = lead.PainPointReport ?? '';
              const showLinkedInQuestionnaire = Number(lead?.EmailConfidence || 0) === 0 && (lead?.Credits || 0) >= 2;
              const isLinkedInLead = showLinkedInQuestionnaire;
              const emailConfidenceValue = Number(lead?.EmailConfidence ?? 0);
              const badgeColorClass = isLinkedInLead
                ? 'bg-[#0A66C2]'
                : emailConfidenceValue >= 100
                ? 'bg-emerald-500'
                : emailConfidenceValue > 0
                ? 'bg-amber-500'
                : 'bg-red-500';
              const socialsList = parseSocials(lead);
              const officerUrls = lead.OfficerURL ? JSON.parse(lead.OfficerURL) : [];
              const leadNamesArray = (lead?.Credits || 0) >= 3 && lead?.LeadName
                ? lead.LeadName.split(',').map(name => name.trim()).filter(name => name)
                : [];
              const cardBorderClass = lead.InitalSchedule ? 'border-yellow-400' : isContactedLead ? 'border-amber-400' : companiesHouseHit ? 'border-emerald-400' : 'border-red-400';

              const linkedInBadgeIcon = (
                <span
                  title={isLinkedInLead ? 'LinkedIn Outreach' : `Email Confidence ${emailConfidenceValue}%`}
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${badgeColorClass} text-white shadow-sm`}
                >
                  {isLinkedInLead ? (
                    <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  ) : (
                    <EnvelopeOpenIcon className="h-3.5 w-3.5" />
                  )}
                </span>
              );

              return [
                mobileGroupHeader,
                <div key={rowId} className={`rounded-xl border-l-4 ${cardBorderClass} border border-slate-200 bg-white p-3 shadow-sm`}>
                  {/* Compact header row — max 3 lines */}
                  <div className="flex items-center gap-2">
                    {linkedInBadgeIcon}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">{lead.LeadName ?? '-'}</div>
                      <div className="truncate text-xs text-slate-500">{lead.Company ?? '-'} · {lead.Sector ?? '-'} · {lead.Location ?? '-'}</div>
                    </div>
                    {isContactedLead ? (
                      <span title={`${followUpStage.label}${followUpStage.sentAt ? ` · ${new Date(followUpStage.sentAt).toLocaleString()}` : ''}`} className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <span className="text-[10px] font-bold leading-none">★{followUpStage.stage}</span>
                      </span>
                    ) : lead.InitialEmail ? (
                      <span title="Initial email drafted" className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                        <EnvelopeOpenIcon className="h-3.5 w-3.5" />
                      </span>
                    ) : (
                      <span title="No initial email" className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleRowExpansion(rowId)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </button>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">Credits: {lead.Credits != null && String(lead.Credits).trim() !== '' ? lead.Credits : '-'}</span>
                    <select
                      value={['No Reply', 'Interested', 'Sale'].includes(String(lead?.Progress || '').trim()) ? String(lead.Progress).trim() : 'No Reply'}
                      onChange={(e) => handleProgressChange(lead.id, e.target.value)}
                      disabled={!lead.id || progressSavingLeadId === lead.id}
                      className="rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-xs"
                    >
                      <option value="No Reply">No Reply</option>
                      <option value="Interested">Interested</option>
                      <option value="Sale">Sale</option>
                    </select>
                    <div className="ml-auto flex flex-wrap gap-1.5">
                      {filterValue !== 1 && lead.id && (
                        <button
                          type="button"
                          onClick={() => handleSingleLeadEnrich(lead)}
                          disabled={deletingLeadId !== null || singleLeadEnrichingId !== null}
                          className="rounded-md bg-gradient-to-r from-purple-600 to-blue-600 px-2 py-1 text-[11px] font-semibold text-white shadow-sm"
                        >
                          {isSingleEnrichRunning ? 'Enriching...' : 'Enrich'}
                        </button>
                      )}
                      {!hasCallScript && (
                        <button
                          type="button"
                          onClick={() => handleGenerateCallScript(lead)}
                          disabled={deletingLeadId !== null || singleLeadEnrichingId !== null || callScriptGeneratingId !== null || !canUseAiAndEnrich}
                          className="rounded-md bg-gradient-to-r from-emerald-600 to-sky-600 px-2 py-1 text-[11px] font-semibold text-white shadow-sm"
                        >
                          {callScriptGeneratingId === lead.id ? 'Script...' : 'Script'}
                        </button>
                      )}
                      {lead.id && (
                        <button
                          type="button"
                          onClick={() => handleDeleteLead(lead.id)}
                          disabled={deletingLeadId !== null || singleLeadEnrichingId !== null}
                          className="rounded-md bg-red-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-red-700"
                        >
                          {deletingLeadId === lead.id ? '...' : 'Delete'}
                        </button>
                      )}
                      {hasInsightAccount && isContactedLead && lead.id && (
                        <button
                          type="button"
                          onClick={() => handleAddToCrm(lead)}
                          disabled={crmAddingLeadId === lead.id}
                          className="rounded-md bg-amber-500 px-2 py-1 text-[11px] font-semibold text-white hover:bg-amber-600"
                        >
                          {crmAddingLeadId === lead.id ? '...' : 'Continue'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded detail — unified for LinkedIn and Email leads */}
                  {isExpanded && (
                    <div className="mt-3 space-y-3 border-t border-slate-200 pt-3 text-xs text-slate-700">
                      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                        {linkedInBadgeIcon}
                        <span className="font-semibold text-slate-700">
                          {isLinkedInLead ? 'LinkedIn Outreach' : `Email Confidence: ${emailConfidenceValue}%`}
                        </span>
                        {Boolean(lead.InitalSchedule) && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                            <ClockIcon className="h-3 w-3 text-amber-500 shrink-0" />
                            {safeFormatDate(lead.InitalSchedule, 'dd/MM/yyyy HH:mm')}
                            <button
                              type="button"
                              onClick={() => handleCancelSchedule(lead.id)}
                              disabled={cancellingScheduleId === lead.id}
                              className="ml-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                              aria-label="Cancel schedule"
                            >
                              <XMarkIcon className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        )}
                      </div>

                      {isLinkedInLead && (
                        <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                          <p className="mb-2 text-xs font-semibold text-slate-700">Company URL correct?</p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleLinkedInCorrect(lead.id, 1)}
                              className={`flex-1 rounded-md py-1.5 text-xs font-semibold ${lead.LinkedInCorrect === 1 ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                            >✓ Yes</button>
                            <button
                              type="button"
                              onClick={() => handleLinkedInCorrect(lead.id, 0)}
                              className={`flex-1 rounded-md py-1.5 text-xs font-semibold ${lead.LinkedInCorrect === 0 ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                            >✗ No</button>
                          </div>
                        </div>
                      )}

                      {Number(lead.FormDetected ?? 0) === 1 && (
                        <div className="rounded-lg border border-purple-200 bg-purple-50 px-2.5 py-2">
                          <p className="mb-1 font-semibold text-purple-800">📋 Form Detected</p>
                          <button
                            type="button"
                            onClick={() => handleFormSent(lead.id, lead.FormSent ? 0 : 1)}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${Boolean(lead.FormSent) ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-600'}`}
                          >
                            {Boolean(lead.FormSent) ? 'Sent' : 'Unsent'}
                          </button>
                        </div>
                      )}

                      {isLinkedInLead && (lead?.Credits || 0) >= 3 && (
                        <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-2">
                          <p className="text-xs font-semibold text-slate-700">Officer URLs</p>
                          <select
                            id={`mobile-officer-select-${lead.id}`}
                            className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700"
                            defaultValue=""
                          >
                            <option value="">Select officer...</option>
                            {leadNamesArray.map((name, idx) => (
                              <option key={idx} value={name}>{name}</option>
                            ))}
                          </select>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="LinkedIn URL..."
                              id={`mobile-officer-url-${lead.id}`}
                              className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder-slate-400"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const sel = document.getElementById(`mobile-officer-select-${lead.id}`);
                                const inp = document.getElementById(`mobile-officer-url-${lead.id}`);
                                if (sel?.value && inp?.value) {
                                  handleAddOfficerUrl(lead.id, sel.value, inp.value);
                                  sel.value = ''; inp.value = '';
                                }
                              }}
                              className="rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600 whitespace-nowrap"
                            >Add</button>
                          </div>
                          {officerUrls.length > 0 && (
                            <div className="space-y-1">
                              {officerUrls.map((officer, idx) => (
                                <div key={idx} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1.5">
                                  <div className="min-w-0 flex-1 text-xs">
                                    <span className="font-medium text-slate-700">{officer.LeadName}: </span>
                                    <a href={officer.URL} target="_blank" rel="noopener noreferrer" className="truncate text-indigo-600 hover:underline">{officer.URL}</a>
                                  </div>
                                  <button type="button" onClick={() => handleRemoveOfficerUrl(lead.id, idx)} className="shrink-0 text-red-500 hover:text-red-700">
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">LinkedIn Outreach</p>
                        <div className="flex items-center gap-1.5">
                          <select
                            value={lead.LinkedInStatus || ''}
                            onChange={(e) => handleLinkedInFollow(lead.id, e.target.value)}
                            className="flex-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700"
                          >
                            <option value="">Status...</option>
                            <option value="Not Followed">Not Followed</option>
                            <option value="Following">Following</option>
                            <option value="Requested">Requested</option>
                            <option value="Connected">Connected</option>
                            <option value="Message Sent">Message Sent</option>
                          </select>
                        </div>
                        {lead.LinkedInSent && (
                          <p className="mt-1.5 text-[10px] text-slate-500">Sent {safeFormatDate(lead.LinkedInSent, 'dd/MM/yyyy HH:mm')}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                        <div><span className="font-semibold">Email:</span> <span className="break-all">{lead.Email || '-'}</span></div>
                        <div><span className="font-semibold">Tel:</span> {lead.Telephone || '-'}</div>
                        <div className="col-span-2"><span className="font-semibold">Address:</span> {lead.Address || '-'}</div>
                        <div><span className="font-semibold">Co. No:</span> {lead.CompanyNumber || '-'}</div>
                        <div><span className="font-semibold">Co. Size:</span> {lead.CompanySize || '-'}</div>
                        <div><span className="font-semibold">CH Hit:</span> {companiesHouseHit ? 'Yes' : 'No'}</div>
                        <div><span className="font-semibold">Occupation:</span> {lead.Occupation || '-'}</div>
                        <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 font-semibold text-amber-900">
                          Revenue: £{formatExtractedRevenueValue(lead.ExtractedRevenue)}
                          {lead.ExtractedRevenueTimestamp ? ` - ${safeFormatDate(lead.ExtractedRevenueTimestamp)}` : ' - No Information'}
                        </div>
                      </div>
                      {lead.Website && (
                        <div><span className="font-semibold">Website:</span> <a href={lead.Website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline break-all">{lead.Website}</a></div>
                      )}

                      {emailConfidenceValue > 50 && (
                        <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 p-2.5">
                          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-cyan-800">Follow-Up Emails</span>
                            <span className="rounded-full bg-cyan-600 px-2 py-0.5 text-[10px] font-bold text-white">{followUpStage.label}</span>
                          </div>
                          <div className="space-y-2">
                            {[1, 2, 3].map((n) => {
                              const bodyText = lead[`FollowUpEmail${n}`];
                              const sentAt = lead[`FollowUpEmail${n}Sent`];
                              const scheduledFor = followUpScheduleForLead?.[`followup${n}`];
                              return (
                                <div key={n} className="rounded-md border border-white bg-white p-2">
                                  <div className="mb-1 flex items-center justify-between gap-2">
                                    <span className="font-semibold text-slate-700">Follow-up {n}</span>
                                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${sentAt ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                      {sentAt ? `Sent ${new Date(sentAt).toLocaleDateString()}` : scheduledFor ? `Scheduled ${new Date(scheduledFor).toLocaleDateString()}` : 'Not sent'}
                                    </span>
                                  </div>
                                  {bodyText ? (
                                    <p className="line-clamp-3 whitespace-pre-wrap break-words text-slate-600">{bodyText}</p>
                                  ) : (
                                    <p className="text-slate-400">No draft yet.</p>
                                  )}
                                  {bodyText && bodyText.length > 120 && (
                                    <button type="button" onClick={() => openModal(`Follow-up ${n} Email`, bodyText)} className="mt-1 font-semibold text-indigo-600 hover:underline">View</button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {socialsList.length > 0 && (
                        <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                          <div className="mb-2 flex items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Social profiles</span>
                            <SocialBadges lead={lead} />
                          </div>
                          <div className="space-y-2">
                            {socialsList.map(({ platform, url, posts }) => (
                              <div key={platform} className="rounded-md border border-slate-100 bg-slate-50 p-2 text-xs text-slate-700">
                                <span className="font-semibold text-slate-800">{platform}</span>
                                {url ? (
                                  <a href={url} target="_blank" rel="noopener noreferrer" className="mt-1 block break-all text-indigo-600 hover:underline">{url}</a>
                                ) : (
                                  <span className="mt-1 block text-slate-400">Profile not found</span>
                                )}
                                {posts.length > 0 && (
                                  <ul className="mt-1.5 space-y-1 border-t border-slate-200 pt-1.5 text-slate-600">
                                    {posts.map((post, postIndex) => <li key={postIndex} className="leading-relaxed">{post}</li>)}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email Details</span>
                          <div className="flex items-center gap-2">
                            {lead.InitialEmail && (
                              <button
                                type="button"
                                onClick={() => navigator.clipboard?.writeText(lead.InitialEmail ?? '')}
                                className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
                              >Copy</button>
                            )}
                            {initialEmailEditingId !== lead.id && lead.id && (
                              <button
                                type="button"
                                onClick={() => openInitialEmailEditor(lead)}
                                disabled={deletingLeadId !== null || !lead.id}
                                className="rounded-md border border-indigo-200 px-2 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50"
                              >Edit</button>
                            )}
                          </div>
                        </div>
                        <div className="rounded-md border border-slate-100 bg-slate-50 p-2 max-h-28 overflow-y-auto">
                          {lead.InitialEmail
                            ? <p className="whitespace-pre-wrap break-words text-xs text-slate-700">{lead.InitialEmail}</p>
                            : <p className="text-xs text-slate-400">No email drafted.</p>
                          }
                        </div>
                        {initialEmailMessages[lead.id]?.success && (
                          <div className="mt-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700">{initialEmailMessages[lead.id].success}</div>
                        )}
                        {initialEmailMessages[lead.id]?.error && (
                          <div className="mt-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-600">{initialEmailMessages[lead.id].error}</div>
                        )}
                      </div>

                      {companyReportText ? (
                        <div>
                          <span className="font-semibold">Company Report:</span>
                          <span className="ml-1 line-clamp-3">{companyReportText.slice(0, 200)}{companyReportText.length > 200 ? '...' : ''}</span>
                          {companyReportText.length > 100 && (
                            <button type="button" onClick={() => openModal('Company Report', companyReportText)} className="ml-1 font-semibold text-indigo-600 hover:underline">View</button>
                          )}
                        </div>
                      ) : null}
                      {jobListingsText ? (
                        <div>
                          <span className="font-semibold">Job Listings:</span>
                          <span className="ml-1 line-clamp-3">{jobListingsText.slice(0, 200)}{jobListingsText.length > 200 ? '...' : ''}</span>
                          {jobListingsText.length > 100 && (
                            <button type="button" onClick={() => openModal('Job Listings', jobListingsText)} className="ml-1 font-semibold text-indigo-600 hover:underline">View</button>
                          )}
                        </div>
                      ) : null}
                      {painPointText ? (
                        <div>
                          <span className="font-semibold">Pain Points:</span>
                          <span className="ml-1 line-clamp-3">{painPointText.slice(0, 200)}{painPointText.length > 200 ? '...' : ''}</span>
                          {painPointText.length > 100 && (
                            <button type="button" onClick={() => openModal('Pain Points', painPointText)} className="ml-1 font-semibold text-indigo-600 hover:underline">View</button>
                          )}
                        </div>
                      ) : null}
                      {hasCallScript && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold text-slate-700">📞 Call Script:</span>
                          <button
                            type="button"
                            onClick={() => openModal('Call Script', callScriptText)}
                            className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                          >
                            View Script <span className="ml-1 text-indigo-400">→</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ];
            })
          )}
        </div>

        {/* ── Desktop table view (hidden on mobile) ────────────────── */}
        <div className="hidden md:block overflow-x-auto">
        <table className="min-w-[1080px] divide-y divide-slate-200/80 text-xs sm:min-w-full sm:text-sm">
          <thead className="bg-white/60">
            <tr>
              <th className="w-16 px-2 py-2"></th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Lead Name</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Company</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Sector</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">
                <button
                  onClick={() => {
                    setLocationSortOrder((prev) => {
                      if (prev === 'none') return 'asc';
                      if (prev === 'asc') return 'desc';
                      return 'none';
                    });
                  }}
                  className="flex items-center gap-1 hover:text-indigo-600 uppercase focus:outline-none"
                >
                  Location
                  {locationSortOrder === 'asc' && (
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                  {locationSortOrder === 'desc' && (
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </th>
              {/* Pursue column removed per request */}
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Credits</th>
              <th className="px-3 py-2 text-center text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Email</th>
              <th className="px-3 py-2 text-center text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">FP</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Status</th>
              <th className="px-3 py-2 text-left text-[0.65rem] font-semibold text-gray-600 uppercase tracking-wider sm:text-xs">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white/40 divide-y divide-slate-200/60">
            {tableLoading && !aiLeadPullRunning && !leadPullLoading ? null : filteredLeads.length > 0 ? (
 visibleFilteredLeads.map((lead, index) => {
  const rowId = lead.id ?? `${lead.LeadName ?? 'lead'}-${lead.Email ?? index}`;
  const companyReportText = lead.CompanyReport ?? '';
  const jobListingsText = lead.JobLisitings ?? lead.JobListings ?? '';
  const painPointText = lead.PainPointReport ?? '';
  const companyUrl = lead.URL ?? lead.Url ?? lead.url ?? '';
  const isScheduled = !!lead.InitalSchedule;
  
  // Check if we should show the full-width LinkedIn questionnaire
  const showLinkedInQuestionnaire = Number(lead?.EmailConfidence || 0) === 0 && 
                                     (lead?.Credits || 0) >= 2;
  const isLinkedInLead = showLinkedInQuestionnaire;
  const emailConfidenceValue = Number(lead?.EmailConfidence ?? 0);
  const badgeColorClass = isLinkedInLead
    ? 'bg-[#0A66C2]'
    : emailConfidenceValue >= 100
    ? 'bg-emerald-500'
    : emailConfidenceValue > 0
    ? 'bg-amber-500'
    : 'bg-red-500';

  const companiesHouseHit = Number(lead.CompaniesHouseHit ?? lead.companieshousehit ?? 0) === 1;
  const filterValue = Number(lead.Filter ?? 0);
  const callScriptText = String(lead.CallScript ?? lead.callScript ?? '').trim();
  const hasCallScript = callScriptText.length > 0;
  const filterError = lead.FilterError ?? null;
  const isContactedLead = Boolean(parseLeadContactDate(lead));
  const followUpStage = getLeadFollowUpStage(lead);
  const followUpScheduleForLead = parseFollowUpSchedule(lead);
  const isSingleEnrichRunning = singleLeadEnrichingId === lead.id;
  const isExpanded = expandedRows.has(rowId);
  

  const officerUrls = lead.OfficerURL ? JSON.parse(lead.OfficerURL) : [];

// Get lead names array from credits (if credits >= 3, split by comma)
const leadNamesArray = (lead?.Credits || 0) >= 3 && lead?.LeadName 
  ? lead.LeadName.split(',').map(name => name.trim()).filter(name => name)
  : [];


  const summaryAccent = isScheduled
    ? 'bg-yellow-100/60'
    : companiesHouseHit
    ? 'bg-emerald-100/45'
    : 'bg-sky-100/40';

  const summaryErrorAccent = filterError
    ? 'bg-red-100/40'
    : summaryAccent;
  const detailAccent = companiesHouseHit ? 'bg-emerald-100/35' : 'bg-sky-100/30';
  const summaryBorderClass = isScheduled ? 'border-yellow-400' : isContactedLead ? 'border-amber-400' : companiesHouseHit ? 'border-emerald-400' : 'border-red-400';
  const detailBorderClass = companiesHouseHit ? 'border-emerald-200' : 'border-red-200';

  const renderLongFormCell = (text, viewLabel) => {
    if (!text) {
      return <span className="text-gray-400">No data</span>;
    }

    const normalizedText = typeof text === 'string' ? text : JSON.stringify(text, null, 2);

    return (
      <div className="flex flex-col gap-1">
        <div className="max-h-16 overflow-hidden text-xs text-slate-600 sm:text-sm">
          {normalizedText.length > 160 ? `${normalizedText.slice(0, 160)}...` : normalizedText}
        </div>
        <button
          type="button"
          onClick={() => openModal(viewLabel, normalizedText)}
          className="self-start text-indigo-600 hover:text-indigo-800 font-medium"
        >
          View {viewLabel}
        </button>
      </div>
    );
  };

  const renderDetail = (label, value) => (
    <div className="flex flex-col">
      <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">{label}</span>
      <span className="text-xs text-slate-700 sm:text-sm">{value ?? '-'}</span>
    </div>
  );

  const socialsList = parseSocials(lead);

  // Permanent LinkedIn / Email group split — render a header row whenever the group changes
  const isLinkedInGroupLead = Number(lead.EmailConfidence ?? 0) === 0;
  const previousLead = visibleFilteredLeads[index - 1];
  const isGroupBoundary = index === 0 || isLinkedInGroupLead !== (Number(previousLead?.EmailConfidence ?? 0) === 0);

  return (
    <React.Fragment key={rowId}>
      {isGroupBoundary && (
        <tr className={isLinkedInGroupLead ? 'bg-blue-100/80' : 'bg-emerald-100/80'}>
          <td colSpan={9} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide ${isLinkedInGroupLead ? 'text-blue-800' : 'text-emerald-800'}`}>
            {isLinkedInGroupLead ? `Unfiltered Leads (${linkedInGroupCount})` : `Email Leads (${emailGroupCount})`}
          </td>
        </tr>
      )}
      {/* Single slim row design used for every lead — LinkedIn vs Email leads are only distinguished by the status badge */}
      <tr className="border-b border-slate-200/60 bg-white text-slate-700 transition-colors duration-150 hover:bg-slate-50">
        <td className={`px-2 py-1.5 align-middle border-l-4 ${summaryBorderClass}`}>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => toggleRowExpansion(rowId)}
              className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            >
              {isExpanded ? '▾' : '▸'}
            </button>
            <span
              title={isLinkedInLead ? 'LinkedIn Outreach' : `Email Confidence ${emailConfidenceValue}%`}
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${badgeColorClass} text-white shadow-sm`}
            >
              {isLinkedInLead ? (
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              ) : (
                <EnvelopeOpenIcon className="h-3.5 w-3.5" />
              )}
            </span>
          </div>
        </td>
        <td className="px-3 py-1.5 text-xs font-semibold text-slate-900 sm:text-sm">{lead.LeadName ?? '-'}</td>
        <td className="px-3 py-1.5 text-xs sm:text-sm">{lead.Company ?? '-'}</td>
        <td className="px-3 py-1.5 text-xs sm:text-sm">{lead.Sector ?? '-'}</td>
        <td className="px-3 py-1.5 text-xs sm:text-sm">{lead.Location ?? '-'}</td>
        <td className="px-3 py-1.5 text-xs text-center sm:text-sm">{lead?.Credits != null && String(lead?.Credits).trim() !== '' ? String(lead.Credits) : '-'}</td>
        <td className="px-3 py-1.5 text-center">
  <div className="flex items-center justify-center gap-1.5">
    {isContactedLead ? (
      <span title={`${followUpStage.label}${followUpStage.sentAt ? ` · ${new Date(followUpStage.sentAt).toLocaleString()}` : ''}`} className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-amber-600">
        <span className="text-[10px] font-bold leading-none">★{followUpStage.stage}</span>
      </span>
    ) : lead.InitialEmail ? (
      <span title="Initial email drafted" className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
        <EnvelopeOpenIcon className="h-3.5 w-3.5" />
      </span>
    ) : (
      <span title="No initial email" className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-600">
        <XMarkIcon className="h-3.5 w-3.5" />
      </span>
    )}
    {Number(lead.Agent) === 1 && (
      <img
        src="/ava.png"
        alt="Agent"
        className="h-5 w-5 rounded-full ring-1 ring-slate-300"
      />
    )}
  </div>
</td>
<td className="px-3 py-1.5 text-center">
  <FunnelIndicator position={lead?.FunnelPosition || 'Unaware'} />
</td>        <td className="px-3 py-1.5 text-xs sm:text-sm">
          <select
            value={['No Reply', 'Interested', 'Sale'].includes(String(lead?.Progress || '').trim()) ? String(lead.Progress).trim() : 'No Reply'}
            onChange={(event) => handleProgressChange(lead.id, event.target.value)}
            disabled={!lead.id || progressSavingLeadId === lead.id}
            className="rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="No Reply">No Reply</option>
            
            <option value="Interested">Interested</option>
            <option value="Sale">Sale</option>
          </select>
        </td>
        <td className="px-3 py-1.5 text-xs sm:text-sm">
          <div className="flex flex-wrap items-center gap-1.5">
            {filterValue !== 1 && lead.id ? (
              <button
                type="button"
                onClick={() => handleSingleLeadEnrich(lead)}
                className="rounded-md bg-gradient-to-r from-purple-600 to-blue-600 px-2 py-1 text-[11px] font-semibold text-white shadow-sm hover:from-purple-500 hover:to-blue-500"
                disabled={deletingLeadId !== null || singleLeadEnrichingId !== null}
              >
                {isSingleEnrichRunning ? 'Enriching...' : 'Enrich'}
              </button>
            ) : null}
            {!hasCallScript && (
              <button
                type="button"
                onClick={() => handleGenerateCallScript(lead)}
                className="rounded-md bg-gradient-to-r from-emerald-600 to-sky-600 px-2 py-1 text-[11px] font-semibold text-white shadow-sm hover:from-emerald-500 hover:to-sky-500"
                disabled={deletingLeadId !== null || singleLeadEnrichingId !== null || callScriptGeneratingId !== null || !canUseAiAndEnrich}
              >
                {callScriptGeneratingId === lead.id ? 'Script...' : 'Script'}
              </button>
            )}
            {lead.id && (
              <button
                type="button"
                onClick={() => handleDeleteLead(lead.id)}
                className="rounded-md bg-red-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-red-700"
                disabled={deletingLeadId !== null || singleLeadEnrichingId !== null}
              >
                {deletingLeadId === lead.id ? '...' : 'Delete'}
              </button>
            )}
            {hasInsightAccount && isContactedLead && lead.id ? (
              <button
                type="button"
                onClick={() => handleAddToCrm(lead)}
                className="rounded-md bg-amber-500 px-2 py-1 text-[11px] font-semibold text-white hover:bg-amber-600"
                disabled={crmAddingLeadId === lead.id}
              >
                {crmAddingLeadId === lead.id ? '...' : 'Continue'}
              </button>
            ) : null}
          </div>
        </td>
      </tr>

      {/* Unified expanded panel — same content for LinkedIn and Email leads */}
      {isExpanded && (
        <tr className={`text-slate-700 ${detailAccent}`}>
          <td colSpan={9} className={`border-l-4 ${detailBorderClass} bg-white px-4 sm:px-6 py-4 shadow-sm`}>
            <div className="space-y-6 text-slate-700">
              {/* Status summary bar */}
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${badgeColorClass} text-white shadow-sm`}>
                  {isLinkedInLead ? (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  ) : (
                    <EnvelopeOpenIcon className="h-4 w-4" />
                  )}
                </span>
                <span className="text-xs font-semibold text-slate-700">
                  {isLinkedInLead ? 'LinkedIn Outreach' : `Email Confidence: ${emailConfidenceValue}%`}
                </span>
                {isLinkedInLead && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Company URL correct?</span>
                    <button
                      type="button"
                      onClick={() => handleLinkedInCorrect(lead.id, 1)}
                      className={`rounded-md px-2 py-1 text-[10px] font-semibold ${lead.LinkedInCorrect === 1 ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                    >✓ Yes</button>
                    <button
                      type="button"
                      onClick={() => handleLinkedInCorrect(lead.id, 0)}
                      className={`rounded-md px-2 py-1 text-[10px] font-semibold ${lead.LinkedInCorrect === 0 ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                    >✗ No</button>
                  </div>
                )}
                {Number(lead.FormDetected ?? 0) === 1 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700">
                    📋 Form Detected
                    <button
                      type="button"
                      onClick={() => handleFormSent(lead.id, lead.FormSent ? 0 : 1)}
                      className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${Boolean(lead.FormSent) ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-600'}`}
                    >
                      {Boolean(lead.FormSent) ? 'Sent' : 'Unsent'}
                    </button>
                  </span>
                )}
                {isScheduled && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                    <ClockIcon className="h-3 w-3 text-amber-500 shrink-0" />
                    {safeFormatDate(lead.InitalSchedule, 'dd/MM/yyyy - HH:mm')}
                    <button
                      type="button"
                      onClick={() => handleCancelSchedule(lead.id)}
                      disabled={cancellingScheduleId === lead.id}
                      className="ml-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                      aria-label="Cancel schedule"
                    >
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  </span>
                )}
              </div>

              {/* Officer URLs — only for LinkedIn leads with Credits >= 3 */}
              {isLinkedInLead && (lead?.Credits || 0) >= 3 && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Officer URLs</span>
                    <span className="text-[10px] text-slate-400">(Add LinkedIn URLs for officers)</span>
                  </div>
                  <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-2">
                    <select
                      className="flex-1 min-w-[120px] rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-blue-400"
                      defaultValue=""
                      id={`officer-select-${lead.id}`}
                    >
                      <option value="">Select officer...</option>
                      {leadNamesArray.map((name, idx) => (
                        <option key={idx} value={name}>{name}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="LinkedIn URL..."
                      className="flex-1 min-w-[150px] rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder-slate-400 focus:ring-2 focus:ring-blue-400"
                      id={`officer-url-${lead.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const select = document.getElementById(`officer-select-${lead.id}`);
                        const input = document.getElementById(`officer-url-${lead.id}`);
                        const selectedName = select.value;
                        const url = input.value;
                        if (selectedName && url) {
                          handleAddOfficerUrl(lead.id, selectedName, url);
                          select.value = '';
                          input.value = '';
                        }
                      }}
                      className="rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-600 whitespace-nowrap"
                    >
                      Add Officer
                    </button>
                  </div>
                  {officerUrls.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {officerUrls.map((officer, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-1.5">
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-slate-700">{officer.LeadName}</span>
                            <a href={officer.URL} target="_blank" rel="noopener noreferrer" className="truncate max-w-[200px] text-xs text-indigo-600 hover:underline">{officer.URL}</a>
                          </div>
                          <button type="button" onClick={() => handleRemoveOfficerUrl(lead.id, idx)} className="text-red-500 hover:text-red-700">
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {renderDetail('Occupation', lead.Occupation || '-')}
                {renderDetail('Company Size', lead.CompanySize || '-')}
                {renderDetail('Company Number', lead.CompanyNumber || '-')}
                {renderDetail('Companies House Hit', companiesHouseHit ? 'Yes' : 'No')}
                {renderDetail('Website', lead.Website ? (
                  <a href={lead.Website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{lead.Website}</a>
                ) : '-')}
                {renderDetail('Company URL', companyUrl ? (
                  <a href={companyUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{companyUrl}</a>
                ) : '-')}
                {renderDetail('Email', lead.Email || '-')}
                {renderDetail('Telephone', lead.Telephone || '-')}
                {renderDetail('Address', lead.Address || '-')}
                {renderDetail('Town', lead.Town || '-')}
                {renderDetail('Contacted', lead.Contacted ? new Date(lead.Contacted).toLocaleString() : 'Not contacted')}
                {renderDetail('Sent To', lead.SentTo || '-')}
                {renderDetail('Last Contacted', lead.LastContacted ? new Date(lead.LastContacted).toLocaleDateString() : '-')}
                {renderDetail('Responded', lead.Responded ? new Date(lead.Responded).toLocaleString() : '-')}
                {renderDetail('Extracted Revenue', lead.ExtractedRevenue ? `£${formatExtractedRevenueValue(lead.ExtractedRevenue)} - ${safeFormatDate(lead.ExtractedRevenueTimestamp)}` : 'No Information')}
                {renderDetail('Created At', lead.CreatedAt ? new Date(lead.CreatedAt).toLocaleString() : '-')}
              </div>

              {emailConfidenceValue > 50 && (
                <div className="rounded-xl border border-cyan-200 bg-cyan-50/60 p-4 shadow-sm">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-cyan-800">Follow-Up Emails</span>
                    <span className="rounded-full bg-cyan-600 px-2 py-0.5 text-[10px] font-bold text-white">{followUpStage.label}</span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {[1, 2, 3].map((n) => {
                      const bodyText = lead[`FollowUpEmail${n}`];
                      const sentAt = lead[`FollowUpEmail${n}Sent`];
                      const scheduledFor = followUpScheduleForLead?.[`followup${n}`];
                      return (
                        <div key={n} className="rounded-lg border border-white bg-white p-3 text-xs shadow-sm">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <span className="font-semibold text-slate-700">Follow-up {n}</span>
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${sentAt ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                              {sentAt ? `Sent ${new Date(sentAt).toLocaleDateString()}` : scheduledFor ? `Scheduled ${new Date(scheduledFor).toLocaleDateString()}` : 'Not sent'}
                            </span>
                          </div>
                          {bodyText ? (
                            <p className="line-clamp-3 whitespace-pre-wrap break-words text-slate-600">{bodyText}</p>
                          ) : (
                            <p className="text-slate-400">No draft yet.</p>
                          )}
                          {bodyText && bodyText.length > 120 && (
                            <button type="button" onClick={() => openModal(`Follow-up ${n} Email`, bodyText)} className="mt-1 font-semibold text-indigo-600 hover:underline">View</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {socialsList.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Social profiles</span>
                    <SocialBadges lead={lead} />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {socialsList.map(({ platform, url, posts }) => (
                      <div key={platform} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-700">
                        <span className="font-semibold text-slate-800">{platform}</span>
                        {url ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" className="mt-1 block break-all text-indigo-600 hover:underline">{url}</a>
                        ) : (
                          <span className="mt-1 block text-slate-400">Profile not found</span>
                        )}
                        {posts.length > 0 && (
                          <ul className="mt-2 space-y-1 border-t border-slate-200 pt-2 text-slate-600">
                            {posts.map((post, postIndex) => <li key={postIndex} className="leading-relaxed">{post}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* LinkedIn Outreach management */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">LinkedIn Outreach</span>
                  {socialsList.length > 0 && <SocialBadges lead={lead} />}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={lead.URL || lead.LinkedInFollow || ''}
                      onChange={(e) => handleLinkedInUrl(lead.id, e.target.value)}
                      placeholder="LinkedIn URL..."
                      className="flex-1 min-w-[80px] rounded-lg border border-slate-300 bg-white/80 px-2 py-1.5 text-xs text-slate-700 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => handleLinkedInCorrect(lead.id, 1)}
                      className={`rounded-lg px-2 py-1.5 text-[10px] font-semibold ${lead.LinkedInCorrect === 1 ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      title="Mark URL correct"
                    >✓</button>
                    <button
                      type="button"
                      onClick={() => handleLinkedInCorrect(lead.id, 0)}
                      className={`rounded-lg px-2 py-1.5 text-[10px] font-semibold ${lead.LinkedInCorrect === 0 ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      title="Mark URL incorrect"
                    >✗</button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <select
                      value={lead.LinkedInStatus || ''}
                      onChange={(e) => handleLinkedInFollow(lead.id, e.target.value)}
                      className="flex-1 rounded-lg border border-slate-300 bg-white/80 px-2 py-1.5 text-xs text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">Status...</option>
                      <option value="Not Followed">Not Followed</option>
                      <option value="Following">Following</option>
                      <option value="Requested">Requested</option>
                      <option value="Connected">Connected</option>
                      <option value="Message Sent">Message Sent</option>
                    </select>
                    {lead.LinkedInSent && (
                      <span className="whitespace-nowrap text-[10px] font-medium text-slate-500">
                        Sent {safeFormatDate(lead.LinkedInSent, 'dd/MM/yyyy HH:mm')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Email details */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email Details</span>
                    <div className="flex items-center gap-2">
                      {lead.InitialEmail && (
                        <button
                          type="button"
                          onClick={() => navigator.clipboard?.writeText(lead.InitialEmail ?? '')}
                          className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                          title="Copy email to clipboard"
                        >
                          Copy
                        </button>
                      )}
                      {initialEmailEditingId !== lead.id && lead.id && (
                        <button
                          type="button"
                          onClick={() => openInitialEmailEditor(lead)}
                          className="rounded-md border border-indigo-200 px-2.5 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
                          disabled={deletingLeadId !== null || !lead.id}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-xs font-semibold text-slate-700 sm:text-sm">{lead?.Subject ?? 'No subject'}</div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 shadow-inner">
                    {lead.InitialEmail ? (
                      <p className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-left text-xs text-slate-700 sm:text-sm">{lead.InitialEmail}</p>
                    ) : (
                      <p className="text-xs text-slate-400 sm:text-sm">No email drafted.</p>
                    )}
                  </div>
                  {initialEmailMessages[lead.id]?.success && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-600">{initialEmailMessages[lead.id].success}</div>
                  )}
                  {initialEmailMessages[lead.id]?.error && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">{initialEmailMessages[lead.id].error}</div>
                  )}
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Company Report</span>
                  {renderLongFormCell(companyReportText, 'Company Report')}
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Job Listings</span>
                  {renderLongFormCell(jobListingsText, 'Job Listings')}
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pain Points</span>
                  {renderLongFormCell(painPointText, 'Pain Points')}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Call Script</span>
                  {hasCallScript ? (
                    <button
                      type="button"
                      onClick={() => openModal('Call Script', callScriptText)}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                    >
                      View Full Script
                    </button>
                  ) : null}
                </div>
                {hasCallScript ? (
                  <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 sm:text-sm">
                    {callScriptText}
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-400 sm:text-sm">
                    No call script generated.
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
})
) : (
  <tr>
    <td colSpan={9} className="px-4 py-6 text-center text-xs text-gray-500 sm:text-sm">No leads found.</td>
  </tr>
)}
          </tbody>
        </table>
        </div>
        {!tableLoading && filteredLeads.length > 0 && hasMoreFilteredLeads && (
          <div ref={leadsLoadMoreRef} className="py-4 text-center text-xs text-slate-500">
            Loading more leads... ({visibleFilteredLeads.length}/{filteredLeads.length})
          </div>
        )}
      </div>

      <CreditBuyModal
        open={buyModalOpen}
        onClose={() => setBuyModalOpen(false)}
        creditsRemaining={creditsRemaining}
        creditsUsed={creditsUsed}
        totalLeads={dashboardStats.totalLeads}
      />

      {/* ── ICP Profile slide-over drawer ── */}
    <Dialog open={icpDrawerOpen} onClose={setIcpDrawerOpen} className="relative z-[70]">
  <DialogBackdrop
    transition
    className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
  />
  <div className="fixed inset-0 overflow-hidden">
    <div className="absolute inset-0 overflow-hidden">
      <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full max-w-full pl-10 sm:pl-16">
        <DialogPanel
          transition
          className="pointer-events-auto w-screen max-w-5xl transform transition duration-300 ease-in-out data-[closed]:translate-x-full"
        >
          <div className="relative flex h-full flex-col overflow-y-auto bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
              <DialogTitle className="text-base font-semibold text-slate-900">
                ICP Profile
              </DialogTitle>
              <button
                type="button"
                onClick={() => setIcpDrawerOpen(false)}
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1">
              <ICP embedded onClose={() => setIcpDrawerOpen(false)} />
            </div>
          </div>
        </DialogPanel>
      </div>
    </div>
  </div>
</Dialog>

      {smtpModalOpen && (
  <div className="fixed inset-0 z-[60] overflow-y-auto bg-slate-900/60 backdrop-blur-sm">
    <div className="mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center px-3 py-6 sm:px-4 sm:py-10">
      <div className="w-full rounded-2xl border border-white/55 bg-white shadow-[0_25px_70px_rgba(14,116,144,0.35)] backdrop-blur-xl">
        
        {/* ── Header ── */}
        <div className="flex flex-col gap-3 border-b border-white/50 bg-white/40 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">SMTP Accounts</h2>
            <p className="text-xs text-gray-600 sm:text-sm">Add multiple sender configurations.</p>
          </div>
          <button
            type="button"
            onClick={() => setSmtpModalOpen(false)}
            className="w-full rounded-lg border border-white/60 bg-white/75 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-white sm:w-auto"
          >
            Close
          </button>
        </div>

        {/* ── Information & Warning Section (full width) ── */}
        <div className="border-b border-white/50 bg-gradient-to-br from-amber-50/80 to-yellow-50/80 px-4 py-4 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 text-2xl">⚠️</div>
            <div className="flex-1 space-y-3">
              <p className="text-base font-bold text-amber-900">
                DO NOT USE your personal login email – this protects your domain reputation.
              </p>
              <div className="space-y-2 text-sm text-amber-800">
                <p>
                  <span className="font-semibold">Why?</span> Using your main email for sending campaigns risks:
                </p>
                <ul className="list-disc space-y-1 pl-5 text-xs text-amber-700">
                  <li>Damaging your domain's sender reputation</li>
                  <li>Emails landing in spam folders</li>
                  <li>Getting your primary email blacklisted</li>
                </ul>
              </div>
              <div className="rounded-lg border border-amber-200 bg-white/50 p-3 text-xs text-amber-900">
                <p className="font-semibold">📌 Recommended Setup:</p>
                <ol className="mt-1 list-decimal space-y-1 pl-5">
                  <li>
                    Buy a dedicated domain (e.g. <strong>yourbrand-mail.co.uk</strong>) – we recommend{' '}
                    <a
                      href="https://www.namecheap.com/domains/registration/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-700 underline hover:text-blue-900"
                    >
                      Namecheap
                    </a>
                    .
                  </li>
                  <li>
                    Create <strong>10–15 email addresses</strong> (e.g., sales1@, sales2@, …) – you can do this for free with{' '}
                    <a
                      href="https://www.cloudflare.com/en-gb/email-routing/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-700 underline hover:text-blue-900"
                    >
                      Cloudflare Email Routing
                    </a>
                    .
                  </li>
                  <li>
                    For ready‑to‑use mailboxes, consider services like{' '}
                    <a
                      href="https://hunter.io/email-accounts"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-700 underline hover:text-blue-900"
                    >
                      Hunter
                    </a>
                    ,{' '}
                    <a
                      href="https://snov.io/email-accounts"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-700 underline hover:text-blue-900"
                    >
                      Snov.io
                    </a>
                    , or{' '}
                    <a
                      href="https://woodpecker.co/email-accounts/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-700 underline hover:text-blue-900"
                    >
                      Woodpecker
                    </a>
                    .
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* ── SMTP Accounts List ── */}
        <div className="max-h-[55vh] overflow-y-auto px-4 py-5 sm:px-6">
          {smtpConfigs.map((cfg, index) => (
            <div key={cfg.id} className="mb-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">Account #{index + 1}</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => checkSmtpConnection(cfg)}
                    disabled={smtpChecking}
                    className="rounded-md border border-sky-300 bg-white px-3 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-60"
                  >
                    {smtpChecking ? 'Checking...' : 'Check'}
                  </button>
                  {smtpConfigs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSmtpConfig(cfg.id)}
                      className="rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Provider */}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Provider</label>
                  <select
                    value={cfg.provider}
                    onChange={(e) => handleSmtpProviderChange(cfg.id, e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {Object.entries(smtpProviderPresets).map(([key, provider]) => (
                      <option key={key} value={key}>{provider.label}</option>
                    ))}
                  </select>
                </div>

                {/* Host */}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">SMTP Host</label>
                  <input
                    type="text"
                    value={cfg.host}
                    onChange={(e) => updateSmtpConfig(cfg.id, 'host', e.target.value)}
                    placeholder="smtp.example.com"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Port */}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Port</label>
                  <input
                    type="number"
                    value={cfg.port}
                    onChange={(e) => updateSmtpConfig(cfg.id, 'port', e.target.value)}
                    placeholder="587"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Secure */}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Secure (SSL/TLS)</label>
                  <select
                    value={cfg.secure ? 'true' : 'false'}
                    onChange={(e) => updateSmtpConfig(cfg.id, 'secure', e.target.value === 'true')}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>

                {/* Username */}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">SMTP Username</label>
                  <input
                    type="text"
                    value={cfg.username}
                    onChange={(e) => updateSmtpConfig(cfg.id, 'username', e.target.value)}
                    placeholder="username"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">SMTP Password</label>
                  <input
                    type="password"
                    value={cfg.password}
                    onChange={(e) => updateSmtpConfig(cfg.id, 'password', e.target.value)}
                    placeholder="password"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* From Email */}
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">From Email</label>
                  <input
                    type="email"
                    value={cfg.fromEmail}
                    onChange={(e) => updateSmtpConfig(cfg.id, 'fromEmail', e.target.value)}
                    placeholder="sales@yourdomain.com"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Per‑config connection status */}
              {smtpConnected !== null && smtpLastCheckedAt && (
                <div
                  className={classNames(
                    'mt-3 rounded-md border px-3 py-2 text-sm',
                    smtpConnected
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-red-200 bg-red-50 text-red-700'
                  )}
                >
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <span>{smtpConnectionMessage}</span>
                    {smtpLastCheckedAt && (
                      <span className="text-[11px] opacity-75">Last checked: {smtpLastCheckedAt}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add button */}
          <button
            type="button"
            onClick={addSmtpConfig}
            className="mt-2 inline-flex items-center gap-1 rounded-lg border border-dashed border-indigo-400 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            <span className="text-lg">➕</span> Add SMTP Account
          </button>
        </div>

        {/* Error & feedback */}
        {smtpError && (
          <div className="mx-4 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 sm:mx-6">
            {smtpError}
          </div>
        )}
        {smtpFeedback && (
          <div className="mx-4 mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 sm:mx-6">
            {smtpFeedback}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6">
          <button
            type="button"
            onClick={() => setSmtpModalOpen(false)}
            className="w-full rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveSmtpSettings}
            disabled={smtpSaving}
            className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 sm:w-auto"
          >
            {smtpSaving ? 'Saving...' : 'Save All'}
          </button>
        </div>

      </div>
    </div>
  </div>
)}
      

      {/* Granular Bulk Send Modal */}
   {granularSendModal.open && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 backdrop-blur-sm sm:p-4">
    <div
      className="lm-modal relative flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border"
      style={{
        borderColor: 'rgba(16,185,129,0.25)',
        background: 'radial-gradient(120% 140% at 50% -10%, #171D2C 0%, #0B0E14 55%)',
        boxShadow: '0 25px 70px -20px rgba(16,185,129,0.3)',
      }}
    >
      {/* Header */}
      <div className="border-b px-4 py-4 sm:px-6" style={{ borderColor: 'rgba(16,185,129,0.18)', background: 'rgba(16,185,129,0.06)' }}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-base" style={{ background: 'rgba(16,185,129,0.15)' }}>
              📧
            </span>
            <div className="min-w-0">
              <h2 className="lm-display text-base font-semibold sm:text-lg" style={{ color: '#F1F4F9' }}>
                Send Emails
              </h2>
              <p className="mt-0.5 text-[11px] sm:text-xs" style={{ color: 'rgba(241,244,249,0.5)' }}>
                Send straight up to your daily limit — no need to configure each sector/location group. Only leads with Email Confidence &amp; Officer Confidence over 50% are eligible.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeGranularSendModal}
            className="shrink-0 rounded-full p-1.5 transition-colors"
            style={{ color: 'rgba(241,244,249,0.4)' }}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Daily limit banner */}
      <div className="border-b px-4 py-3 sm:px-6" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="lm-mono text-[10px] uppercase tracking-wide" style={{ color: 'rgba(241,244,249,0.4)' }}>
            Daily limit
          </span>
          <span className="lm-mono rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(99,102,241,0.15)', color: '#A5B4FC' }}>
            Cap {emailDailyCap}
          </span>
          <span className="lm-mono rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(6,182,212,0.15)', color: '#67E8F9' }}>
            Sent {emailSentToday}
          </span>
          <span
            className="lm-mono rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
            style={
              emailRemainingToday === 0
                ? { background: 'rgba(248,113,113,0.18)', color: '#FCA5A5' }
                : { background: 'rgba(16,185,129,0.18)', color: '#6EE7B7' }
            }
          >
            Remaining {emailRemainingToday}
          </span>
          {granularSendQuantity > emailRemainingToday && emailRemainingToday > 0 && (
            <span className="lm-mono w-full rounded-full px-2.5 py-1 text-[10px] font-semibold sm:w-auto" style={{ background: 'rgba(248,113,113,0.15)', color: '#FCA5A5' }}>
              ⚠ Exceeds daily limit by {granularSendQuantity - emailRemainingToday}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="lm-scroll flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        {totalBulkSendEligible === 0 ? (
          <div className="flex items-center gap-2.5 rounded-xl border px-4 py-3.5 text-sm" style={{ borderColor: 'rgba(251,191,36,0.25)', background: 'rgba(251,191,36,0.06)', color: '#FCD34D' }}>
            <span className="text-lg">📭</span>
            <span>No eligible leads found (drafted email, Email Confidence &amp; Officer Confidence over 50%).</span>
          </div>
        ) : (
          <>
            {/* Quick send control */}
            <div className="rounded-2xl border p-4" style={{ borderColor: 'rgba(16,185,129,0.2)', background: 'rgba(16,185,129,0.05)' }}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium" style={{ color: '#6EE7B7' }}>Emails to send</p>
                  <p className="mt-0.5 text-[11px]" style={{ color: 'rgba(110,231,183,0.7)' }}>
                    {totalBulkSendEligible} eligible lead(s) across {Object.keys(sectorLocationBreakdown).length} sector/location group(s).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max={Math.min(totalBulkSendEligible, emailRemainingToday > 0 ? emailRemainingToday : totalBulkSendEligible)}
                    value={granularSendQuantity}
                    onChange={(e) => handleGranularSendQuantityChange(e.target.value)}
                    className="lm-field w-20 rounded-lg px-2 py-1.5 text-center text-sm font-semibold sm:w-24"
                  />
                  <button
                    type="button"
                    onClick={() => handleGranularSendQuantityChange(Math.min(totalBulkSendEligible, emailRemainingToday > 0 ? emailRemainingToday : totalBulkSendEligible))}
                    className="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                    style={{ borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', color: '#6EE7B7' }}
                  >
                    Max
                  </button>
                </div>
              </div>
            </div>

            {/* Sector/location breakdown — informational only */}
            <div className="mt-3 grid gap-2">
              {Object.keys(sectorLocationBreakdown)
                .sort((a, b) => {
                  const aData = sectorLocationBreakdown[a];
                  const bData = sectorLocationBreakdown[b];
                  const sectorCompare = (aData.sector ?? '').localeCompare(bData.sector ?? '');
                  if (sectorCompare !== 0) return sectorCompare;
                  return (aData.location ?? '').localeCompare(bData.location ?? '');
                })
                .map((key) => {
                  const { sector, location, count } = sectorLocationBreakdown[key];
                  return (
                    <div key={key} className="flex flex-wrap items-center gap-2 rounded-xl border px-3.5 py-2.5" style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
                      <span className="rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(99,102,241,0.15)', color: '#A5B4FC' }}>{sector}</span>
                      <span className="rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(6,182,212,0.15)', color: '#67E8F9' }}>{location}</span>
                      <span className="lm-mono rounded-full px-2.5 py-0.5 text-[11px]" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(241,244,249,0.5)' }}>{count} eligible</span>
                    </div>
                  );
                })}
            </div>
          </>
        )}

        {/* Feedback messages */}
        {granularSendFeedback && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm" style={{ borderColor: 'rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.08)', color: '#6EE7B7' }}>
            <span className="text-lg">✅</span>
            <span>{granularSendFeedback}</span>
          </div>
        )}
        {granularSendError && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm" style={{ borderColor: 'rgba(248,113,113,0.25)', background: 'rgba(248,113,113,0.08)', color: '#FCA5A5' }}>
            <span className="text-lg">⚠️</span>
            <span>{granularSendError}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t px-4 py-4 sm:px-6" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: 'rgba(241,244,249,0.45)' }}>
            <span>{Object.keys(sectorLocationBreakdown).length} sector(s)</span>
            <span className="hidden sm:inline">•</span>
            <span className="font-medium" style={{ color: 'rgba(241,244,249,0.75)' }}>{granularSendQuantity} emails</span>
            {granularSendQuantity > 0 && (
              <>
                <span className="hidden sm:inline">•</span>
                <span
                  className="font-medium"
                  style={
                    granularSendQuantity > emailRemainingToday && emailRemainingToday > 0
                      ? { color: '#FCA5A5' }
                      : { color: '#6EE7B7' }
                  }
                >
                  {emailRemainingToday > 0 ? `${emailRemainingToday - granularSendQuantity} remaining` : '❌ No capacity'}
                </span>
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={closeGranularSendModal}
              disabled={granularSendRunning}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium transition disabled:opacity-50 sm:text-sm"
              style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.7)' }}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              Cancel
            </button>

            {user.insightAccount && (
              <button
                type="button"
                onClick={openScheduleSendModal}
                disabled={granularSendRunning || totalBulkSendEligible === 0}
                className="inline-flex items-center justify-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium transition disabled:opacity-50 sm:text-sm"
                style={{ borderColor: 'rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.08)', color: '#FCD34D' }}
              >
                <ClockIcon className="h-3.5 w-3.5" />
                Schedule
              </button>
            )}

            <button
              type="button"
              onClick={handleGranularSendEmails}
              disabled={
                granularSendRunning ||
                totalBulkSendEligible === 0 ||
                granularSendQuantity === 0 ||
                (granularSendQuantity > emailRemainingToday && emailRemainingToday > 0)
              }
              className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
              style={{ color: '#0B0E14', background: 'linear-gradient(90deg, #10B981, #06B6D4)', boxShadow: '0 8px 20px -8px rgba(16,185,129,0.5)' }}
            >
              <PaperAirplaneIcon className="h-3.5 w-3.5" />
              {granularSendRunning ? (
                <>
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: 'rgba(11,14,20,0.4)', borderTopColor: 'transparent' }} />
                  Sending…
                </>
              ) : (
                `Send ${granularSendQuantity}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

{/* ══════════════════════════════════════ Schedule Email Send ══════════════════════════════════════ */}
{scheduleModalOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 backdrop-blur-sm sm:p-4">
    <div
      className="lm-modal relative flex max-h-[95vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border"
      style={{
        borderColor: 'rgba(251,191,36,0.25)',
        background: 'radial-gradient(120% 140% at 50% -10%, #171D2C 0%, #0B0E14 55%)',
        boxShadow: '0 25px 70px -20px rgba(251,191,36,0.28)',
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b px-4 py-4 sm:px-6" style={{ borderColor: 'rgba(251,191,36,0.18)', background: 'rgba(251,191,36,0.06)' }}>
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-base" style={{ background: 'rgba(251,191,36,0.15)' }}>
            ⏰
          </span>
          <div className="min-w-0">
            <h2 className="lm-display text-base font-semibold sm:text-lg" style={{ color: '#F1F4F9' }}>
              Schedule Email Send
            </h2>
            <p className="mt-0.5 text-[11px] sm:text-xs" style={{ color: 'rgba(252,211,77,0.75)' }}>
              Pick a date &amp; time, then select which enriched leads (not yet emailed) to schedule.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={closeScheduleSendModal}
          className="shrink-0 rounded-full p-1.5 text-xl transition-colors"
          style={{ color: 'rgba(241,244,249,0.4)' }}
        >
          ×
        </button>
      </div>

      {/* Date/time + summary bar */}
      <div className="border-b px-4 py-4 sm:px-6" style={{ borderColor: 'rgba(251,191,36,0.15)' }}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-5">
          <label className="flex w-full flex-col gap-1.5 sm:w-auto">
            <span className="lm-mono text-[10px] font-medium uppercase tracking-wide" style={{ color: '#FCD34D' }}>
              Scheduled date &amp; time
            </span>
            <input
              type="datetime-local"
              value={scheduledDateTime}
              min={new Date().toISOString().slice(0, 16)}
              onChange={(e) => setScheduledDateTime(e.target.value)}
              className="lm-field rounded-lg px-3 py-2 text-sm"
              style={{ borderColor: 'rgba(251,191,36,0.3)' }}
            />
          </label>
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className="lm-mono rounded-full px-3 py-1 text-xs font-semibold"
              style={
                scheduledLeadIds.size > emailRemainingToday
                  ? { background: 'rgba(248,113,113,0.18)', color: '#FCA5A5' }
                  : { background: 'rgba(251,191,36,0.18)', color: '#FCD34D' }
              }
            >
              {scheduledLeadIds.size} selected
            </span>
            <span className="lm-mono rounded-full border px-3 py-1 text-xs font-medium" style={{ borderColor: 'rgba(251,191,36,0.25)', background: 'rgba(255,255,255,0.02)', color: 'rgba(252,211,77,0.8)' }}>
              {scheduleEligibleLeads.length} eligible
            </span>
          </div>
        </div>

        {/* Daily limit info */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-xl border px-3 py-2" style={{ borderColor: 'rgba(251,191,36,0.15)', background: 'rgba(255,255,255,0.015)' }}>
          <span className="lm-mono text-[10px] uppercase tracking-wide" style={{ color: 'rgba(241,244,249,0.4)' }}>Daily sending limit</span>
          <span className="lm-mono rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(148,163,184,0.15)', color: '#CBD5E1' }}>Cap {emailDailyCap}</span>
          <span className="lm-mono rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(6,182,212,0.15)', color: '#67E8F9' }}>Sent today {emailSentToday}</span>
          <span
            className="lm-mono rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
            style={
              emailRemainingToday === 0
                ? { background: 'rgba(248,113,113,0.18)', color: '#FCA5A5' }
                : { background: 'rgba(16,185,129,0.18)', color: '#6EE7B7' }
            }
          >
            Remaining {emailRemainingToday}
          </span>
          {scheduledLeadIds.size > emailRemainingToday && (
            <span className="lm-mono w-full rounded-full px-2.5 py-1 text-[10px] font-semibold sm:w-auto" style={{ background: 'rgba(248,113,113,0.15)', color: '#FCA5A5' }}>
              ⚠ Exceeds by {scheduledLeadIds.size - emailRemainingToday}
            </span>
          )}
        </div>
      </div>

      {/* Select all / none controls */}
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5 sm:px-6" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <span className="lm-mono text-[10px] font-medium uppercase tracking-wide" style={{ color: 'rgba(241,244,249,0.35)' }}>Bulk</span>
        <button
          type="button"
          onClick={() => setScheduledLeadIds(new Set(scheduleEligibleLeads.map((l) => l.id)))}
          className="rounded-full border px-3 py-1 text-xs font-medium transition"
          style={{ borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', color: '#6EE7B7' }}
        >
          All ({scheduleEligibleLeads.length})
        </button>
        <button
          type="button"
          onClick={() => setScheduledLeadIds(new Set())}
          className="rounded-full border px-3 py-1 text-xs font-medium transition"
          style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.6)' }}
        >
          None
        </button>
      </div>

      {/* Lead list grouped by sector / location */}
      <div className="lm-scroll flex-1 overflow-y-auto px-4 py-3 sm:px-6">
        {scheduleEligibleLeads.length === 0 ? (
          <div className="rounded-xl border px-4 py-3.5 text-sm" style={{ borderColor: 'rgba(251,191,36,0.25)', background: 'rgba(251,191,36,0.06)', color: '#FCD34D' }}>
            No enriched leads with unsent emails found in the current scope.
          </div>
        ) : (
          <div className="space-y-3">
            {Object.keys(scheduleEligibleByGroup)
              .sort((a, b) => {
                const aD = scheduleEligibleByGroup[a];
                const bD = scheduleEligibleByGroup[b];
                const sc = (aD.sector ?? '').localeCompare(bD.sector ?? '');
                return sc !== 0 ? sc : (aD.location ?? '').localeCompare(bD.location ?? '');
              })
              .map((groupKey) => {
                const { sector: grpSector, location: grpLocation, leads: grpLeads } = scheduleEligibleByGroup[groupKey];
                const allGroupSelected = grpLeads.every((l) => scheduledLeadIds.has(l.id));
                const selectedInGroup = grpLeads.filter((l) => scheduledLeadIds.has(l.id)).length;
                return (
                  <div key={groupKey} className="overflow-hidden rounded-xl border" style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.015)' }}>
                    {/* Group header row */}
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <input
                        type="checkbox"
                        checked={allGroupSelected}
                        onChange={() => toggleScheduleGroup(groupKey)}
                        style={{ accentColor: '#FBBF24' }}
                        className="h-4 w-4 rounded"
                      />
                      <span className="rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(99,102,241,0.15)', color: '#A5B4FC' }}>{grpSector}</span>
                      <span className="rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(6,182,212,0.15)', color: '#67E8F9' }}>{grpLocation}</span>
                      <span className="lm-mono ml-auto text-xs" style={{ color: 'rgba(241,244,249,0.4)' }}>
                        {selectedInGroup}/{grpLeads.length}
                      </span>
                    </div>
                    {/* Individual lead rows */}
                    <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                      {grpLeads.map((lead) => (
                        <label
                          key={lead.id}
                          className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition hover:bg-white/[0.02]"
                          style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
                        >
                          <input
                            type="checkbox"
                            checked={scheduledLeadIds.has(lead.id)}
                            onChange={() => toggleScheduleLead(lead.id)}
                            style={{ accentColor: '#FBBF24' }}
                            className="h-4 w-4 shrink-0 rounded"
                          />
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium" style={{ color: '#F1F4F9' }}>{lead.LeadName ?? '-'}</span>
                            {lead.Company && (
                              <span className="ml-2 text-xs" style={{ color: 'rgba(241,244,249,0.4)' }}>{lead.Company}</span>
                            )}
                          </div>
                          {lead.Email && (
                            <span className="hidden max-w-[12rem] truncate text-xs sm:block" style={{ color: 'rgba(241,244,249,0.3)' }}>
                              {lead.Email}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {scheduleSendFeedback && (
          <div className="mt-3 rounded-xl border px-3.5 py-2.5 text-sm" style={{ borderColor: 'rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.08)', color: '#6EE7B7' }}>
            {scheduleSendFeedback}
          </div>
        )}
        {scheduleSendError && (
          <div className="mt-3 rounded-xl border px-3.5 py-2.5 text-sm" style={{ borderColor: 'rgba(248,113,113,0.25)', background: 'rgba(248,113,113,0.08)', color: '#FCA5A5' }}>
            {scheduleSendError}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t px-4 py-4 sm:px-6" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <button
            type="button"
            onClick={closeScheduleSendModal}
            disabled={scheduleSendRunning}
            className="w-full rounded-full border px-4 py-2.5 text-sm font-medium transition disabled:opacity-50 sm:w-auto"
            style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.7)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleScheduledSend}
            disabled={scheduleSendRunning || scheduledLeadIds.size === 0 || !scheduledDateTime}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            style={{ color: '#0B0E14', background: 'linear-gradient(90deg, #FBBF24, #F97316)', boxShadow: '0 8px 20px -8px rgba(251,191,36,0.5)' }}
          >
            <ClockIcon className="h-4 w-4" />
            {scheduleSendRunning ? 'Scheduling…' : `Schedule ${scheduledLeadIds.size} Email(s)`}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* ══════════════════════════════════════ Follow Ups ══════════════════════════════════════ */}
{followUpModalOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 backdrop-blur-sm sm:p-4">
    <div
      className="lm-modal relative flex max-h-[95vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border"
      style={{
        borderColor: 'rgba(6,182,212,0.25)',
        background: 'radial-gradient(120% 140% at 50% -10%, #171D2C 0%, #0B0E14 55%)',
        boxShadow: '0 25px 70px -20px rgba(6,182,212,0.28)',
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b px-4 py-4 sm:px-6" style={{ borderColor: 'rgba(6,182,212,0.18)', background: 'rgba(6,182,212,0.06)' }}>
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-base" style={{ background: 'rgba(6,182,212,0.15)' }}>
            🔁
          </span>
          <div className="min-w-0">
            <h2 className="lm-display text-base font-semibold sm:text-lg" style={{ color: '#F1F4F9' }}>
              Follow Ups
            </h2>
            <p className="mt-0.5 text-[11px] sm:text-xs" style={{ color: 'rgba(103,232,249,0.75)' }}>
              Select contacted leads without a follow-up email yet. Each follow-up costs 1 credit.
            </p>
            <p className="mt-1 text-[11px]" style={{ color: 'rgba(103,232,249,0.55)' }}>
              Each follow-up is sent a week apart from the previous email.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={closeFollowUpModal}
          className="shrink-0 rounded-full p-1.5 text-xl transition-colors"
          style={{ color: 'rgba(241,244,249,0.4)' }}
        >
          ×
        </button>
      </div>

      {/* Summary bar */}
      <div className="border-b px-4 py-4 sm:px-6" style={{ borderColor: 'rgba(6,182,212,0.15)' }}>
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className="lm-mono rounded-full px-3 py-1 text-xs font-semibold"
            style={
              followUpLeadIds.size > creditsRemaining
                ? { background: 'rgba(248,113,113,0.18)', color: '#FCA5A5' }
                : { background: 'rgba(6,182,212,0.18)', color: '#67E8F9' }
            }
          >
            {followUpLeadIds.size} selected
          </span>
          <span className="lm-mono rounded-full border px-3 py-1 text-xs font-medium" style={{ borderColor: 'rgba(6,182,212,0.25)', background: 'rgba(255,255,255,0.02)', color: 'rgba(103,232,249,0.8)' }}>
            {followUpEligibleLeads.length} eligible
          </span>
          <span className="lm-mono rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: 'rgba(16,185,129,0.15)', color: '#6EE7B7' }}>
            Credits remaining {creditsRemaining}
          </span>
          <span className="lm-mono rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: 'rgba(148,163,184,0.15)', color: '#CBD5E1' }}>
            Cost {followUpLeadIds.size} credit(s)
          </span>
          {followUpLeadIds.size > creditsRemaining && (
            <span className="lm-mono w-full rounded-full px-2.5 py-1 text-[10px] font-semibold sm:w-auto" style={{ background: 'rgba(248,113,113,0.15)', color: '#FCA5A5' }}>
              ⚠ Exceeds remaining credits by {followUpLeadIds.size - creditsRemaining}
            </span>
          )}
        </div>
      </div>

      {/* Select all / none controls */}
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5 sm:px-6" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <span className="lm-mono text-[10px] font-medium uppercase tracking-wide" style={{ color: 'rgba(241,244,249,0.35)' }}>Bulk</span>
        <button
          type="button"
          onClick={() => setFollowUpLeadIds(new Set(followUpEligibleLeads.map((l) => l.id)))}
          className="rounded-full border px-3 py-1 text-xs font-medium transition"
          style={{ borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', color: '#6EE7B7' }}
        >
          All ({followUpEligibleLeads.length})
        </button>
        <button
          type="button"
          onClick={() => setFollowUpLeadIds(new Set())}
          className="rounded-full border px-3 py-1 text-xs font-medium transition"
          style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.6)' }}
        >
          None
        </button>
      </div>

      {/* Lead list grouped by sector / location */}
      <div className="lm-scroll flex-1 overflow-y-auto px-4 py-3 sm:px-6">
        {followUpEligibleLeads.length === 0 ? (
          <div className="rounded-xl border px-4 py-3.5 text-sm" style={{ borderColor: 'rgba(6,182,212,0.25)', background: 'rgba(6,182,212,0.06)', color: '#67E8F9' }}>
            No contacted leads without a follow-up email found in the current scope.
          </div>
        ) : (
          <div className="space-y-3">
            {Object.keys(followUpEligibleByGroup)
              .sort((a, b) => {
                const aD = followUpEligibleByGroup[a];
                const bD = followUpEligibleByGroup[b];
                const sc = (aD.sector ?? '').localeCompare(bD.sector ?? '');
                return sc !== 0 ? sc : (aD.location ?? '').localeCompare(bD.location ?? '');
              })
              .map((groupKey) => {
                const { sector: grpSector, location: grpLocation, leads: grpLeads } = followUpEligibleByGroup[groupKey];
                const allGroupSelected = grpLeads.every((l) => followUpLeadIds.has(l.id));
                const selectedInGroup = grpLeads.filter((l) => followUpLeadIds.has(l.id)).length;
                return (
                  <div key={groupKey} className="overflow-hidden rounded-xl border" style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.015)' }}>
                    {/* Group header row */}
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <input
                        type="checkbox"
                        checked={allGroupSelected}
                        onChange={() => toggleFollowUpGroup(groupKey)}
                        style={{ accentColor: '#06B6D4' }}
                        className="h-4 w-4 rounded"
                      />
                      <span className="rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(99,102,241,0.15)', color: '#A5B4FC' }}>{grpSector}</span>
                      <span className="rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: 'rgba(6,182,212,0.15)', color: '#67E8F9' }}>{grpLocation}</span>
                      <span className="lm-mono ml-auto text-xs" style={{ color: 'rgba(241,244,249,0.4)' }}>
                        {selectedInGroup}/{grpLeads.length}
                      </span>
                    </div>
                    {/* Individual lead rows */}
                    <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                      {grpLeads.map((lead) => (
                        <label
                          key={lead.id}
                          className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition hover:bg-white/[0.02]"
                          style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
                        >
                          <input
                            type="checkbox"
                            checked={followUpLeadIds.has(lead.id)}
                            onChange={() => toggleFollowUpLead(lead.id)}
                            style={{ accentColor: '#06B6D4' }}
                            className="h-4 w-4 shrink-0 rounded"
                          />
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium" style={{ color: '#F1F4F9' }}>{lead.LeadName ?? '-'}</span>
                            {lead.Company && (
                              <span className="ml-2 text-xs" style={{ color: 'rgba(241,244,249,0.4)' }}>{lead.Company}</span>
                            )}
                          </div>
                          {lead.Email && (
                            <span className="hidden max-w-[12rem] truncate text-xs sm:block" style={{ color: 'rgba(241,244,249,0.3)' }}>
                              {lead.Email}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {followUpSendFeedback && (
          <div className="mt-3 rounded-xl border px-3.5 py-2.5 text-sm" style={{ borderColor: 'rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.08)', color: '#6EE7B7' }}>
            {followUpSendFeedback}
          </div>
        )}
        {followUpSendError && (
          <div className="mt-3 rounded-xl border px-3.5 py-2.5 text-sm" style={{ borderColor: 'rgba(248,113,113,0.25)', background: 'rgba(248,113,113,0.08)', color: '#FCA5A5' }}>
            {followUpSendError}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t px-4 py-4 sm:px-6" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <button
            type="button"
            onClick={closeFollowUpModal}
            disabled={followUpSendRunning}
            className="w-full rounded-full border px-4 py-2.5 text-sm font-medium transition disabled:opacity-50 sm:w-auto"
            style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(241,244,249,0.7)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleFollowUpSend}
            disabled={followUpSendRunning || followUpLeadIds.size === 0 || followUpLeadIds.size > creditsRemaining}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            style={{ color: '#0B0E14', background: 'linear-gradient(90deg, #06B6D4, #4C6FFF)', boxShadow: '0 8px 20px -8px rgba(6,182,212,0.5)' }}
          >
            <EnvelopeOpenIcon className="h-4 w-4" />
            {followUpSendRunning ? 'Set' : `Set ${followUpLeadIds.size} Follow-Up(s)`}
          </button>
        </div>
      </div>
    </div>
  </div>
)}


{modalState.open && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-2 backdrop-blur-sm sm:p-4">
    <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/55 bg-white/75 shadow-[0_20px_60px_rgba(37,99,235,0.35)] backdrop-blur-xl flex flex-col">
      <div className="flex items-center justify-between border-b border-white/50 bg-white/40 px-3 py-3 sm:px-4 sm:py-4 md:px-6">
        <h2 className="text-sm font-semibold text-gray-900 sm:text-base md:text-lg">{modalState.title}</h2>
        <button
          type="button"
          onClick={closeModal}
          className="text-2xl text-gray-500 hover:text-gray-700 sm:text-xl"
        >
          <span className="sr-only">Close modal</span>
          ×
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 text-xs text-gray-700 whitespace-pre-wrap sm:px-4 sm:py-4 sm:text-sm md:px-6">
        {modalState.body}
      </div>
      <div className="flex justify-end border-t border-gray-200 px-3 py-3 sm:px-4 sm:py-3 md:px-6">
        <button
          type="button"
          onClick={closeModal}
          className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-auto sm:px-4"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
<div className="fixed bottom-4 right-4 z-50">
  <AIAgent />
  </div>

      
      </div>
      <AvenorNew 
      show={shouldShowNotification} 
      setShow={setHasDismissedNotification} 
    />
    </>
  );
}
